import { captureBookLiquidity, type BookLiquiditySeed } from '@/stores/bookLiquiditySettings';
import { StaleMarketBadge } from '@/components/atoms/StaleMarketBadge';
import { OrderbookFreshness } from '@/components/atoms/OrderbookFreshness';
import type { LadderReference } from '../SpreadsTotals/ReferenceFloater';
import { DualOutcomeBooks } from '../GameDetail/DualOutcomeBooks';
import { bookForDisplayOnly } from '@/lib/market/bookDisplay';
// ── GameTable — V2 two-row moneyline table ──────────────────────────
// Per-team Yes/No price pills, global Yes/No toggle in the heading.
// Selecting either team row expands the game inline into paired full-width
// ladders: away book, both team rows together, then home book. The right-side
// detail panel keeps the deeper streaming view; this is the quick paired-market
// view.

import { observeGameRowsRender } from './renderObserver';
import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode, memo } from 'react';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/infra/utils';
import { SaveGameToggle } from './SaveGameToggle';
import { MinimizeGameToggle } from './MinimizeGameToggle';
// #754 — one provenance rule for the event total: what this row DISPLAYS
// is what a discovery mode RANKS on. Type-only elsewhere in this file is
// deliberate; this is the one value import from the selection module.
import { measuredEventVolume } from './discovery';
import {
  GameCountdown,
  LiveBadge,
  NearEventStartPulse,
  UnknownStatusBadge,
} from './GameTimeIndicators';
import { LiquidityBar } from '@/components/atoms/LiquidityBar';
import { BotPresenceChip } from '@/components/bots/BotPresenceChip';
import type { AgentStats } from '@/lib/bots/edge';
import { Th, Td } from '@/components/atoms/TableCells';
import type { UsBoardQuote } from '@/stores/usBoardQuotes';
import { usBookCurrent, usBookForOutcome, usGameKeyForBoardKey, usOutcomeFor, usVenuePlanForBoardKey } from '@/lib/polymarketUs/gameMapping';
import type { UsMarketBookData } from '@/lib/polymarketUs/marketClient';
import { UsLinePriceCell } from '../SpreadsTotals/UsLinePriceCell';
import { UsLineBooks } from '../SpreadsTotals/UsLineBooks';
import type { UsLineCell } from '../SpreadsTotals/usLineCell';
import { UsBoardCell, usQuoteHasCurrentPrice, usQuoteCurrent } from './us/UsBoardCell';
import { UsMetricHeaders, UsMetricCells, UsLadderMetricCaption, UsLadderMetricCells } from './us/UsMarketMetrics';
import { useUsLadderBook } from './us/useUsLadderBook';
import { baseGameKey, foldGameKeySport } from '@/services/analyticsGameKey';
import { NovigBoardCell, type NovigBoardClick } from './novig/NovigBoardCell';
import type { NovigBoardQuote, NovigBoardQuotes } from '@/lib/novig/presenter';
import { SortableTh, type SortDirection } from '@/components/atoms/SortableTh';
import { formatCompactCurrency } from '@/lib/infra/formatters';
import {
  LIQ_HEADER_TOOLTIP_KALSHI,
  LIQ_HEADER_TOOLTIP_POLY,
  KALSHI_TOTAL_VOLUME_LABEL,
  POLY_TOTAL_VOLUME_LABEL,
  SHARED_LIQ_TOOLTIP,
  SHARED_VOL_TOOLTIP,
  VOL_HEADER_TOOLTIP_KALSHI,
  VOL_HEADER_TOOLTIP_POLY,
} from './sharedBookCue';
import { formatGameDateLabel, getTimeAgo } from '@/lib/infra/dateUtils';
import { roundLabel } from '@/services/hydraAnalytics';
import {
  gameDetailLinkForRow,
  toHydraGameKeyLive,
} from '@/services/analyticsGameKey';
import type { OddsStreamGameState } from '@/services/oddsStream';
import { useGameStateFor, useGameStatesStore } from '@/stores/gameStatesStore';
import { primaryGameStateEnabled, usePrimaryGameState } from '@/services/primaryGameState';
import { isGameStateFeedStale, resolveLiveDisplayState } from '@/lib/sport/liveDisplayState';
import {
  useConsensusFor,
  useAdvanceConsensusFor,
  useConsensusTrendFor,
  useAdvanceConsensusTrendFor,
  useMarketStore,
  useOpMarketFor,
  useOpTrendFor,
  useOpLastAnchorFairFor,
  type OpOddsSlice,
} from '@/stores/marketStore';
import {
  buildOpFairView,
  fairAmericanText,
  fairPctText,
  opAnchorGlowDelayMs,
  opHeaderTooltip,
  opIsStale,
  opNoDeltaText,
  opRowHomeCodeFor,
  opWithdrawalAgeDeadline,
  opWithdrawalTitle,
  OP_HEADER_LABEL,
  OP_STALE_MS,
  type OpFairSideView,
  type OpLastAnchorFair,
} from '@/lib/opOdds';
import { useConsensusGlow, type ConsensusSideGlow } from '@/hooks/ui/useConsensusGlow';
import { useDeadlineTick } from '@/hooks/ui/useDeadlineTick';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { CONSENSUS_TREND_WINDOW_MS } from '@/lib/consensusTrend';
import { useChangeKey } from '@/hooks/ui/useFlash';
import { TrendArrow } from '@/components/atoms/TrendArrow';
import { SharpStar } from '@/components/atoms/SharpStar';
import { formatGameStatus, isConfirmedFinal } from '@/lib/sport/periodLabel';
import { queueTone } from '@/components/cards/orderCardModel';
import { EdgeFadeScroller } from '@/components/cards/cardKit';
import {
  depthDeltaTone,
  depthKeyForBook,
  depthVelocityDisplay,
  type DepthVelocityDisplay,
} from '@/components/cards/bookDepthVelocity';
import { DepthVelocityValue } from '@/components/cards/DepthVelocityValue';
import type {
  UserPositionSlice,
  UserOrderSlice,
} from '@/hooks/useUserKalshiPOS';
import type { GameRow, MarketType, MarketSide, PriceMode, Sport, TeamSide, BookLevel, VenueBook } from './types';
import { SPORT_LABEL, SPORT_TABS } from './types';
import { americanOdds, gameMarketKind } from './tradeModalBook';
import { useDisplaySettings, type DashboardSortKey } from '@/stores/displaySettings';
import { OddsFilterChips } from './OddsFilterChips';
import { useLt2Access } from '@/hooks/useLt2Access';
import { useLt2Card } from '@/hooks/useLt2Card';
import { useLt2FocusStore } from '@/stores/lt2FocusStore';
import { Lt2FocusBadge, Lt2FocusChipSlot } from '@/components/Lt2FocusBadge';
import { useGrantSlot } from '@/hooks/useGrantSlot';
import { useOneClickStore } from '@/stores/oneClickStore';
import {
  lt2MoneylineGameCardId,
  lt2MoneylineGameTargets,
  type Lt2MoneylineCardScope,
} from './lt2Moneyline';
import { useFeeDisplayStatus, useFeeFor } from '@/services/hydraFees';
import {
  FEE_DISPLAY_MESSAGE,
  FeeScheduleMarker,
  feeDisplayCanHide,
} from '@/components/molecules/FeeScheduleMarker';
import { kalshiLagValueActive } from '@/lib/divergence/kalshiLagValue';
import { DivergenceBadge } from '@/components/cards/DivergenceBadge';
import { BoardInjuryEntry } from '@/components/organisms/InjuryEntries';
import { BoardInjuryReport } from '@/components/organisms/GameInjuries';
import { useCoreGameStateInterests } from '@/services/coreGameStates';
import { isDivergenceActive } from '@/lib/divergence/polyKalshiDivergence';
import { rowDivergence } from './rowDivergence';
import {
  LADDER_LEVELS,
  bestAskCentsForBook,
  bidIsReal,
  crossedBookRead,
  pillPriceCents,
  bestBidDepthForSide,
  consensusDotPlacement,
  consensusDotTone,
  selectFairDotValue,
  formatVolume,
  ladderRowPricePresentation,
  queueDepth,
  orderTooltip,
  restingDollars,
  type BidRowOrder,
  type ConsensusDotPlacement,
  type ConsensusDotTone,
  type FairDotSource,
} from './ladder/helpers';
import { PricePill, STALE_PILL_REASON } from './ladder/PricePill';
import { PosRing } from './ladder/PosRing';
import { heldContractsTitle } from './ladder/heldContractsTitle';
import { RestingOrderGroup } from './ladder/RestingOrderGroup';
import type { LadderLevelIdentity } from './ladder/CancelLevelControl';
import { useMakerRebateActive } from '@/hooks/useMakerRebate';
import { makerRebatePct } from '@/lib/pricing/makerRebate';
import { mirrorCents } from '@/lib/pricing/subCent';

// ── Props ────────────────────────────────────────────────────────────

/**
 * The minimized-game lens for one board (#654).
 *
 * Bundled rather than passed as loose props because the halves are one
 * decision: `isMinimized` hides rows, and the two restore callbacks are the
 * affordances that bring them back. A board wired for the predicate and not the
 * callbacks would hide a trader's games with no visible way to recover them.
 *
 * `isMinimized` is a predicate rather than a set so this component never has to
 * know which of the two identity keys a row needs — that rule lives in
 * `gameMarkIdentity` and is applied by the store's own selector.
 *
 * `onRestore` takes a GameRow rather than an id for a reason worth stating: a
 * persisted mark id alpha-sorts its team codes, so it cannot say which side was
 * AWAY. Only the row knows. That is also why the footer below lists the rows
 * THIS table hid rather than reading the mark list — see MinimizedFooter.
 */
export interface MinimizedLens {
  isMinimized: (game: GameRow) => boolean;
  onRestore: (game: GameRow) => void;
  onRestoreAll: () => void;
}

/** #987 — the board's Polymarket US trade click (opens the US ticket). */
export type UsTradeClick = (
  gameKey: string,
  side: TeamSide,
  opts: { priceCents?: number; initialOrderSide: 'yes' | 'no' },
) => void;

interface GameTableProps {
  games: GameRow[];
  /** #987 — US moneyline quotes by US canonical game key. Quotes do not
   *  decide the column: a moneyline table with `onUsTradeClick` shows it
   *  whenever a game is in a sport US covers. Absent or empty quotes keep the
   *  column and leave that game's pills disabled, with no price shown. */
  usQuotes?: ReadonlyMap<string, UsBoardQuote> | null;
  onUsTradeClick?: UsTradeClick;
  /** #1150 — Novig moneyline quotes by board `GameRow.key`, mapped by the
   *  supplier. Unlike US, the column shows only when quotes AND a click
   *  handler are supplied: nothing supplies them until a Novig producer
   *  exists, so the board is unchanged. */
  novigQuotes?: NovigBoardQuotes | null;
  onNovigTradeClick?: (gameKey: string, side: TeamSide, click: NovigBoardClick) => void;
  /** #826 — id for the reading-position primitive's row namespace. Two boards
   *  can hold the same game; this is what tells their anchors apart. */
  viewAnchorSection?: string;
  titleLabel?: string;
  seriesTicker: string;
  marketType: MarketType;
  /** #647: opt this board's moneyline rows into A/D hotkey focus. Passed only
   *  by the dashboard's MAIN board — the Saved board stays unwired so a game
   *  on both cannot double-register one card id (ambiguous focus refuses). */
  lt2MoneylineCardScope?: Lt2MoneylineCardScope;
  marketSide: MarketSide;
  priceMode: PriceMode;
  expandedGameKey: string | null;
  expandedTeamSide: TeamSide | null;
  onToggleExpand: (key: string, side: TeamSide) => void;
  /** #1389 — NBA team rows carry the board injury entry; it opens a
   *  read-only report inline in this table, one game at a time, and never
   *  calls back into the panel, ticket or aim (see BoardInjuryReport). */
  injuryEntries?: boolean;
  onTradeClick?: (
    gameKey: string,
    side: 'away' | 'home',
    venue: 'kalshi' | 'polymarket',
    opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no'; postOnly?: boolean; liquiditySeed?: BookLiquiditySeed | null },
  ) => void;
  /** Per-Kalshi-ticker POS overlay for the logged-in user. Empty Maps until
   *  the first /api/trader/positions + /api/kalshi/orders responses arrive. */
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
  /** Per-Kalshi-ticker edge bots (presence markers). Empty/absent = none. */
  botPresenceByTicker?: Map<string, AgentStats[]>;
  /** Hydra canonical gameKeys (`date|TEAM_A|TEAM_B`, alpha-sorted) the user
   *  has any position in. Used by the archived skinny row to draw a left-
   *  border highlight so finished games the user actually traded stand
   *  out in the post-game scan. Default empty set. */
  positionGameKeys?: ReadonlySet<string>;
  /** #1232 — folded US canonical game keys (`foldGameKeySport`) with the
   *  trader's own open Polymarket US order or holding: the operator-risk
   *  invariant keeps those rows through every filter. Default empty set. */
  usActivityGameKeys?: ReadonlySet<string>;
  /** True while the archived (analytics-backed) fetch is in flight for
   *  the selected date. Drives skeleton placeholder rows so the user
   *  sees something is loading instead of a blank gap below the live
   *  rows. */
  archivedLoading?: boolean;
  /** #192/#212 — presentation-only empty-row cleanup, generalized from the
   *  tennis-only boolean to the user's persisted per-sport suppression list.
   *  Defaults to the empty list (filter off) so every other caller preserves
   *  the pre-existing full-list behavior. */
  hiddenEmptySports?: readonly Sport[];
  /** Empty activity maps are only trustworthy after every venue read settles.
   *  Until then the filter fails open and renders every row. */
  activityReady?: boolean;
  /** #509 — board-wide "Kalshi markets only" opt-in. Defaults to false so every
   *  existing caller (notably the Saved table) keeps today's behavior. The
   *  caller is responsible for board scoping; this component only applies it. */
  kalshiMarketsOnly?: boolean;
  /** #654 — the minimized lens. Off (undefined) for every caller that doesn't
   *  opt in, which keeps the Saved table and every non-CFB board exactly as it
   *  is today: no per-row control, no filtering, no footer clause.
   *
   *  Passing this is a single decision with two visible halves, so it arrives
   *  as one object rather than three loose props that could be wired
   *  inconsistently — a board that hides rows without rendering the control to
   *  restore them is precisely the state the lens contract forbids. */
  minimizedLens?: MinimizedLens;
  /** Polymarket condition IDs and token asset IDs with a user position/order. */
  activePolyMarketIds?: ReadonlySet<string>;
  /** Full account-store tickers with a Kalshi position or open order. */
  activeKalshiTickers?: ReadonlySet<string>;
  /** Hide the OP (OddsPapi) column. Omitted ⇒ AUTO: hidden when the
   *  showOpOdds toggle is off OR the current slate has zero OP data
   *  (architecture §8's `sportIsServable` analogue) — so with no OP frames
   *  the table is pixel-identical to pre-feature. Pass explicitly to
   *  override (tests). */
  hideOpColumn?: boolean;
  /** Hide the UNBTD (Unabated) VALUES. Omitted ⇒ AUTO: hidden when the
   *  showUnbtdOdds toggle is off (default OFF — the Odds-filter default is
   *  OP/PNCL only). CONTENT-ONLY: the column slot itself always renders,
   *  because in expanded ladders it hosts the resting-order chips + #523
   *  cancel-level control — order management must survive the odds filter.
   *  Hidden = blank header + blank summary cells; the slot narrows to an
   *  orders gutter. Pass explicitly to override (tests / embedded boards). */
  hideOddsColumn?: boolean;
  /** #902 — keep `games` in the order given, as whole games. The Today / This
   *  week discovery board passes rows already ranked by volume; this table's
   *  own sort (a header click or the trader's pinned default) would otherwise
   *  reorder them. While set, the sort headers show no arrow and take no click,
   *  so they cannot claim an order the rows are not in; the trader's sort
   *  preference is left untouched. Defaults to false: every existing board
   *  sorts exactly as before. */
  preserveGroupOrder?: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────

/** Stable "filter off" value for callers that opt out of empty-row
 *  suppression (the Saved table, and every caller that omits the prop).
 *  A shared const rather than an inline `[]` so omitting the prop doesn't
 *  mint a new array per render and churn the visibleGames memo. */
export const NO_HIDDEN_EMPTY_SPORTS: readonly Sport[] = [];
/** #1389 — a table without injury entries declares no game-state interest. */
const NO_INJURY_KEYS: readonly string[] = [];

/** Stable "nothing minimized here" value, so the no-lens path doesn't mint a
 *  new array per render and churn the footer. */
const NO_HIDDEN_ROWS: GameRow[] = [];
/** Stable default, so a caller without US activity never re-runs the filter memo. */
const NO_US_ACTIVITY_KEYS: ReadonlySet<string> = new Set();

const MARKET_TYPE_LABELS: Record<MarketType, string> = {
  moneyline: 'Moneylines',
  spread: 'Spreads',
  total: 'Totals',
  'spread-total': 'Spread/Total',
  series: 'Futures',
};
const VELOCITY_ALERT_DELTA = 50_000;

/** #665 — full lifecycle for the cell renderers. */
function depthVelocityFor(
  book: VenueBook | null | undefined,
  side: MarketSide,
): DepthVelocityDisplay {
  return depthVelocityDisplay(book?.marketTicker ? depthKeyForBook(book, side) : null);
}

/** Ready-only delta for the velocity-alert magnitude: warming and stalled
 *  streams contribute nothing, so an alert can never fire (or keep firing)
 *  off a window that has no live evidence behind it. */
function depthVelocityDelta(book: VenueBook | null | undefined, side: MarketSide): number | null {
  const display = depthVelocityFor(book, side);
  return display.kind === 'ready' ? display.delta : null;
}

function velocityMagnitude(...deltas: Array<number | null>): number {
  let max = 0;
  for (const delta of deltas) {
    if (delta != null) max = Math.max(max, Math.abs(delta));
  }
  return max;
}

function isUsablePrice(value: number | null | undefined): boolean {
  return value != null && Number.isFinite(value) && value > 0 && value < 100;
}

/** A venue is usable when it has a current BBO quote or at least one real
 *  resting level. Volume, liquidity, midpoint, and last trade are deliberately
 *  not evidence: historical values can remain after executable quotes are gone. */
function hasUsableQuoteOrBook(book: VenueBook | null | undefined): boolean {
  if (!book) return false;
  if (
    isUsablePrice(book.yesBidCents) ||
    isUsablePrice(book.yesAskCents)
  ) {
    return true;
  }
  const yesLast = book.lastPriceCents;
  const noLast = yesLast == null ? null : mirrorCents(yesLast);
  return (
    book.yes.some((level) => isUsablePrice(level.priceCents) && bidIsReal(yesLast)(level)) ||
    book.no.some((level) => isUsablePrice(level.priceCents) && bidIsReal(noLast)(level))
  );
}

/** A venue also counts as non-empty when the row's pills actually PRINT a
 *  price in the active price mode — the guard must never hide a card the user
 *  can see numbers on. This matters in `market` mode, where the pills fall
 *  back to the last trade (and, on a never-traded bookless market, the
 *  sentinel mid) that `hasUsableQuoteOrBook` deliberately ignores. Same
 *  helper the row cells use, so the two can't drift. */
function bookRendersAnyPrice(
  book: VenueBook | null | undefined,
  priceMode: PriceMode,
): boolean {
  if (!book) return false;
  return (
    pillPriceCents(book, 'yes', priceMode) != null ||
    pillPriceCents(book, 'no', priceMode) != null
  );
}

/** #509 — does this row carry an ACTIONABLE Kalshi book?
 *
 *  Three deliberate choices, each traceable to a line of the contract:
 *
 *  1. **Kalshi books only.** A Polymarket book, however healthy, is not
 *     evidence here — that is the whole point of the filter, and the one place
 *     it parts company with "Hide empty", which counts either venue.
 *  2. **`hasUsableQuoteOrBook` and NOT `bookRendersAnyPrice`.** The contract
 *     says a placeholder or stale cached value is not an actionable book.
 *     `bookRendersAnyPrice` deliberately counts a historical last trade and the
 *     never-traded sentinel mid, because "Hide empty" must never hide a card
 *     showing numbers. Those are exactly the values this filter must discount,
 *     so it uses the stricter helper — whose own contract already excludes
 *     volume, liquidity, midpoint and last trade for the same reason.
 *  3. **`freshness === 'stale'` disqualifies.** #358 already treats a stale
 *     book as untradable — it disables that side's trade buttons — so a stale
 *     book is not "actionable" by the app's own behaviour. The bound is
 *     generous (per-sport game length + 30m grace: 4.5h for ATP), so this
 *     excludes abandoned books, not briefly-quiet ones. 'unknown' is NOT
 *     stale: it means "never classified", and #358 leaves unknown rows
 *     tradable. Absent evidence must not masquerade as negative evidence.
 *
 *  A row that loses its Kalshi evidence this way is still preserved by the
 *  caller when the user holds exposure or has it open — that pairing is what
 *  keeps "stays visible through temporary feed unavailability" true. */
function hasActionableKalshiBook(game: GameRow): boolean {
  return [game.kalshi?.away, game.kalshi?.home].some(
    (book) => !!book && book.freshness !== 'stale' && hasUsableQuoteOrBook(book),
  );
}

function venueBooks(game: GameRow): Array<VenueBook | null | undefined> {
  return [
    game.kalshi?.away,
    game.kalshi?.home,
    game.polymarket?.away,
    game.polymarket?.home,
  ];
}

function gameHasUserActivity(
  game: GameRow,
  positionByTicker: ReadonlyMap<string, UserPositionSlice>,
  ordersByTicker: ReadonlyMap<string, UserOrderSlice[]>,
  activePolyMarketIds: ReadonlySet<string>,
  activeKalshiTickers: ReadonlySet<string>,
): boolean {
  for (const book of venueBooks(game)) {
    if (!book) continue;
    if (activeKalshiTickers.has(book.marketTicker)) return true;
    if (positionByTicker.has(book.marketTicker)) return true;
    if ((ordersByTicker.get(book.marketTicker)?.length ?? 0) > 0) return true;
    if (book.conditionId && activePolyMarketIds.has(book.conditionId)) return true;
    if (book.tokenId && activePolyMarketIds.has(book.tokenId)) return true;
    if (book.noTokenId && activePolyMarketIds.has(book.noTokenId)) return true;
  }
  return false;
}

// ── GameTable ────────────────────────────────────────────────────────

/**
 * #730 — the dashboard's row boundary. `GameRows` stays exported UNWRAPPED
 * for GameDetail, SpreadsTotals and the multi-outcome table; only this
 * table's call site renders through the memo. The default shallow comparator
 * is sufficient because every prop passed below is a primitive, a
 * snapshot-stable map, a stable callback (`onTradeClick` reads its context
 * through a ref, #730 Task 6) or an identity-stabilised row (`stableGameRows`,
 * Task 5). Everything inside a row that can change without its props — score,
 * consensus, OP odds, fees, Lt2 focus, the age-derived cells — is an internal
 * subscription or the row's own 1 s clock (Task 3), so a bailout here can
 * never strand a stale value. `GameRows.renderContract.test.tsx` pins each
 * of those freshness paths beside its zero-fan-out assertion.
 */
const DashboardGameRows = memo(GameRows);

/**
 * The board's horizontal scroller frame (EdgeFadeScroller's outer element),
 * shared with the Spread/Total board (#1246).
 * - Its two edge fades are its only direct `aria-hidden` children; the stock
 *   fade is the page background, and this table sits on `bg-card`, so they
 *   are recoloured to the card surface.
 * - Its owned scrollbar track (mt-1 + h-1.5 = 10px) mounts only while the
 *   table overflows. `pb-2.5` holds that height while the track is absent,
 *   so overflow starting or ending (OP toggled, Game Detail opened, a window
 *   resize) does not move the footer or a board below this one.
 */
export const BOARD_SCROLL_FRAME_CLASS = [
  '[&>[aria-hidden]]:from-card [&>[aria-hidden]]:via-card/60',
  'pb-2.5 has-[>div>[role=scrollbar]]:pb-0',
].join(' ');

export function GameTable({
  games,
  viewAnchorSection,
  titleLabel,
  seriesTicker,
  marketType,
  marketSide,
  priceMode,
  expandedGameKey,
  expandedTeamSide,
  onToggleExpand,
  injuryEntries = false,
  onTradeClick,
  positionByTicker,
  ordersByTicker,
  botPresenceByTicker,
  positionGameKeys,
  usActivityGameKeys = NO_US_ACTIVITY_KEYS,
  archivedLoading,
  hiddenEmptySports = NO_HIDDEN_EMPTY_SPORTS,
  activityReady = false,
  kalshiMarketsOnly = false,
  minimizedLens,
  activePolyMarketIds = new Set<string>(),
  activeKalshiTickers = new Set<string>(),
  hideOpColumn: hideOpColumnProp,
  hideOddsColumn: hideOddsColumnProp,
  lt2MoneylineCardScope,
  preserveGroupOrder = false,
  usQuotes = null,
  onUsTradeClick,
  novigQuotes = null,
  onNovigTradeClick,
}: GameTableProps) {
  // #1389 — the board's injury entries read the game-state lane's injury
  // blocks; one interest for the table's NBA games (the dashboard usually
  // declares them already, so the shared union rarely changes).
  useCoreGameStateInterests(
    injuryEntries ? games.filter((g) => g.sport === 'nba').map((g) => baseGameKey(g.key)) : NO_INJURY_KEYS,
  );
  // #1389 — the one inline injury report this table has open, and the entry
  // that opened it (Close / Escape return focus there). Table-local state:
  // opening, reading and closing it touch nothing the dashboard owns.
  const [injuryReportKey, setInjuryReportKey] = useState<string | null>(null);
  const injuryOriginRef = useRef<HTMLElement | null>(null);
  const toggleInjuryReport = useCallback((gameKey: string, origin: HTMLElement) => {
    injuryOriginRef.current = origin;
    setInjuryReportKey((open) => (open === gameKey ? null : gameKey));
  }, []);
  const closeInjuryReport = useCallback(() => {
    setInjuryReportKey(null);
    const origin = injuryOriginRef.current;
    injuryOriginRef.current = null;
    if (origin?.isConnected) origin.focus();
  }, []);
  // US quotes are full-game moneylines: only a moneyline table carries the column.
  const showUs = marketType === 'moneyline' && !!onUsTradeClick
    && games.some((game) => usVenuePlanForBoardKey(game.key) !== null);
  const showNovig = marketType === 'moneyline' && novigQuotes != null && !!onNovigTradeClick;
  const usMarketFor = (game: GameRow) => showUs && gameMarketKind(game) === 'moneyline'
    ? (usQuotes?.get(usGameKeyForBoardKey(game.key) ?? '') ?? null) : null;
  // #1234 — the expanded game's US depth for its inline ladder. One game
  // expands at a time, so the board reads at most one US book.
  const expandedGame = expandedGameKey !== null && expandedTeamSide !== null
    ? games.find((game) => game.key === expandedGameKey && !game.archived) : undefined;
  const usLadderBook = useUsLadderBook(expandedGame ? usMarketFor(expandedGame)?.marketSlug ?? null : null);
  // OP column visibility (architecture §8): user toggle AND slate presence.
  // Selector returns primitives, so re-renders happen only on real flips.
  const showOpOdds = useDisplaySettings((s) => s.showOpOdds);
  // Odds filter (Dan 2026-08-23): per-source visibility. U defaults OFF —
  // the dash defaults to OP/PNCL only; Unabated is the phasing-out vendor
  // and its column is opt-in for advanced users via the toolbar chips.
  const showUnbtdOdds = useDisplaySettings((s) => s.showUnbtdOdds);
  const hideOdds = hideOddsColumnProp ?? !showUnbtdOdds;
  const slateHasOpData = useMarketStore((s) =>
    showOpOdds && s.hasOpOddsForAny(games.map((g) => g.key.split('|multi|')[0])),
  );
  const hideOp = hideOpColumnProp ?? (!showOpOdds || !slateHasOpData);
  // Initial sort is sourced from the user's persisted preference in
  // displaySettings; factory default is `{ key: null, dir: 'asc' }` which
  // preserves the prior data-natural order (no applied sort). Read at mount
  // time only — header clicks during the session don't propagate back into
  // the setting, matching the convention used by defaultSport / defaultPriceMode
  // / defaultExpiryMode. Per-key direction defaults on first click live in
  // handleSort below; date/team are asc-first, queue is desc-first (a
  // market-maker scanning for big stacks at the front of the book wants
  // the deepest queues at the top).
  const [sortKey, setSortKey] = useState<string | null>(
    () => useDisplaySettings.getState().defaultDashboardSort.key,
  );
  const [sortDir, setSortDir] = useState<SortDirection>(
    () => useDisplaySettings.getState().defaultDashboardSort.dir,
  );
  // Read the persisted default reactively (not via lazy useState) so the
  // pin-icon's filled/outlined state updates when the trader toggles the
  // pin from the dashboard, without forcing a remount.
  const persistedSort = useDisplaySettings((s) => s.defaultDashboardSort);
  const setDefaultDashboardSort = useDisplaySettings(
    (s) => s.setDefaultDashboardSort,
  );

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'queue' || key === 'velocity' ? 'desc' : 'asc');
    }
  };

  // Pin / unpin the current sort as the persisted default. If the active
  // sort already matches the saved default, click clears the default (back
  // to factory "unsorted"). Otherwise click saves the active sort+dir as
  // the new default — the trader gets the same sort the next time they
  // open the dashboard, without opening Settings.
  const isCurrentSortPinned =
    sortKey !== null &&
    persistedSort.key === sortKey &&
    persistedSort.dir === sortDir;
  const handlePinSort = () => {
    if (sortKey === null) return; // no sort to pin
    if (isCurrentSortPinned) {
      setDefaultDashboardSort({ key: null, dir: 'asc' });
    } else {
      setDefaultDashboardSort({
        key: sortKey as DashboardSortKey,
        dir: sortDir === 'asc' ? 'asc' : 'desc',
      });
    }
  };

  // Row visibility for the three independent board filters (#212 empty-row
  // suppression, #509 Kalshi-markets-only, #654 minimized games). They are
  // evaluated together in one pass so a row hidden by more than one is counted
  // ONCE — the footer below reports a count per reason, and double-counting
  // would overstate what was hidden.
  //
  // All three share the same preservation set and the same fail-open gate:
  // an empty activity map is only trustworthy once every venue read has
  // answered, so `!activityReady` renders everything. See emptyTennisEvidence.
  const {
    visibleGames,
    hiddenEmptySportsShown,
    hiddenEmptyCount,
    hiddenKalshiCount,
    hiddenMinimizedRows,
  } = useMemo(() => {
    const emptyFilterOn = hiddenEmptySports.length > 0;
    const usRowHasCurrentPrice = (game: GameRow): boolean => {
      const quote = usQuotes?.get(usGameKeyForBoardKey(game.key) ?? '');
      return !!quote && usQuoteHasCurrentPrice(quote);
    };
    if ((!emptyFilterOn && !kalshiMarketsOnly && !minimizedLens) || !activityReady) {
      return {
        visibleGames: games,
        hiddenEmptySportsShown: NO_HIDDEN_EMPTY_SPORTS,
        hiddenEmptyCount: 0,
        hiddenKalshiCount: 0,
        hiddenMinimizedRows: NO_HIDDEN_ROWS,
      };
    }
    const visible: GameRow[] = [];
    const emptyHiddenSports = new Set<Sport>();
    let emptyHidden = 0;
    let kalshiHidden = 0;
    // The ROWS, not a tally: the footer names each one, and only a row can say
    // which team was away (a mark id alpha-sorts its codes).
    const minimizedHidden: GameRow[] = [];
    for (const game of games) {
      // #654 — the minimized rule is evaluated BEFORE the archived exemption
      // below, and it is the only one that is. The other two infer from a
      // row's book, and #192's reasoning is that a finished game is not a
      // stale quote. A minimize is not an inference: it is a direct
      // instruction about this game, and the game finishing does not un-mean
      // it. Without this, dismissing a game would quietly un-dismiss it the
      // moment it went FINAL and returned as an archived row.
      const minimizedRuleHides = minimizedLens?.isMinimized(game) ?? false;
      // Archived rows are outside the two book-derived filters (#192): a
      // finished game is not a stale quote, and the archived strip is its own
      // presentation.
      if (game.archived && !minimizedRuleHides) {
        visible.push(game);
        continue;
      }
      const emptyRuleHides =
        !game.archived &&
        emptyFilterOn &&
        hiddenEmptySports.includes(game.sport) &&
        !venueBooks(game).some(
          (book) => hasUsableQuoteOrBook(book) || bookRendersAnyPrice(book, priceMode),
        ) &&
        // #1232 — a current US quote with a price is not empty either.
        !usRowHasCurrentPrice(game);
      const kalshiRuleHides =
        !game.archived && kalshiMarketsOnly && !hasActionableKalshiBook(game);
      if (!emptyRuleHides && !kalshiRuleHides && !minimizedRuleHides) {
        visible.push(game);
        continue;
      }
      // A filter wants this row gone — visible operator risk gets the last
      // word. This is the invariant no filter may ever break: an open
      // position, a resting order, or a row the user currently has expanded
      // (which is also the order ticket) keeps the row on screen with whatever
      // truthful unavailable/stale state it is carrying.
      //
      // `positionGameKeys` is checked SEPARATELY from gameHasUserActivity, and
      // it is the only channel that works for an analytics-projected archived
      // row: archivedToGameRow builds those with `kalshi: null,
      // polymarket: null`, so gameHasUserActivity iterates no books and can
      // never return true for one. That set is keyed by the same alpha-sorted
      // canonical gameKey those rows carry (it is what ArchivedRow's own
      // hasPosition highlight reads), so it matches them directly. Without it,
      // minimizing a game you hold and then watching it finish and fall out of
      // the live feed would hide your position's row.
      if (
        expandedGameKey === game.key ||
        (positionGameKeys?.has(game.key) ?? false) ||
        // #1232 — the trader's own open US order or holding on this game.
        usActivityGameKeys.has(foldGameKeySport(game.key)) ||
        gameHasUserActivity(
          game,
          positionByTicker,
          ordersByTicker,
          activePolyMarketIds,
          activeKalshiTickers,
        )
      ) {
        visible.push(game);
        continue;
      }
      // Attribute each hidden row to exactly ONE reason, in the order the
      // rules shipped. The empty rule wins a tie: it is the narrowest, oldest
      // claim and its sentence is the one already in production, so a row that
      // several rules would hide keeps reading the way it does today. The
      // minimized clause is last for the same reason it is newest.
      if (emptyRuleHides) {
        emptyHidden += 1;
        emptyHiddenSports.add(game.sport);
      } else if (kalshiRuleHides) {
        kalshiHidden += 1;
      } else {
        minimizedHidden.push(game);
      }
    }
    return {
      visibleGames: visible,
      // Sports whose rows were ACTUALLY hidden by the empty rule in this table
      // (#213). The footer names these, not the persisted `hiddenEmptySports`
      // list: with per-sport suppression a user can configure sports that have
      // no empty row on the current board (or none at all right now), and
      // echoing those would claim hides that didn't happen. Emitted in
      // canonical SPORT_TABS order so multi-sport reads stably (e.g. "ATP/WTA").
      hiddenEmptySportsShown: SPORT_TABS.map((t) => t.id).filter((sport) =>
        emptyHiddenSports.has(sport),
      ),
      hiddenEmptyCount: emptyHidden,
      hiddenKalshiCount: kalshiHidden,
      hiddenMinimizedRows: minimizedHidden,
    };
  }, [
    games,
    hiddenEmptySports,
    kalshiMarketsOnly,
    minimizedLens,
    activityReady,
    expandedGameKey,
    priceMode,
    positionByTicker,
    ordersByTicker,
    activePolyMarketIds,
    activeKalshiTickers,
    positionGameKeys,
    usActivityGameKeys,
    usQuotes,
  ]);
  const hiddenMinimizedCount = hiddenMinimizedRows.length;
  const hiddenRowCount = hiddenEmptyCount + hiddenKalshiCount + hiddenMinimizedCount;

  // Apply the active sort. The previous version held sortKey/sortDir state
  // but never used it — header clicks just toggled the indicator arrow while
  // the row order stayed at whatever ValueDashboard handed us. useMemo so
  // resorting only happens when the inputs actually change; archived rows
  // always sink to the bottom regardless of sort key so finished games
  // don't pollute the live scan.
  // #730 — the velocity sort reads the depth-velocity HISTORY, which is time as
  // well as data: a stream drops to "unavailable" 65 s after its last sample
  // with no publication involved. Re-derive the order on the shared 1 s clock
  // while (and only while) that sort is selected; the queue and date sorts read
  // nothing that moves without a row change.
  const velocityNow = useSharedNow(1000, sortKey === 'velocity');
  const sortedGames = useMemo(() => {
    void velocityNow; // explicit dependency
    if (sortKey == null || preserveGroupOrder) return visibleGames;
    const dirMult = sortDir === 'asc' ? 1 : -1;
    const gameQueueDepth = (g: GameRow): number => {
      const a = bestBidDepthForSide(g.kalshi?.away, marketSide) ?? 0;
      const h = bestBidDepthForSide(g.kalshi?.home, marketSide) ?? 0;
      return Math.max(a, h);
    };
    const gameVelocity = (g: GameRow): number =>
      velocityMagnitude(
        depthVelocityDelta(g.kalshi?.away, marketSide),
        depthVelocityDelta(g.kalshi?.home, marketSide),
        depthVelocityDelta(g.polymarket?.away, marketSide),
        depthVelocityDelta(g.polymarket?.home, marketSide),
      );
    const copy = [...visibleGames];
    copy.sort((g1, g2) => {
      if (g1.archived && !g2.archived) return 1;
      if (!g1.archived && g2.archived) return -1;
      if (sortKey === 'date') {
        const t1 = g1.startTimeMs ?? Number.POSITIVE_INFINITY;
        const t2 = g2.startTimeMs ?? Number.POSITIVE_INFINITY;
        return (t1 - t2) * dirMult;
      }
      if (sortKey === 'team') {
        const cmp =
          g1.awayCode.localeCompare(g2.awayCode) ||
          g1.homeCode.localeCompare(g2.homeCode);
        return cmp * dirMult;
      }
      if (sortKey === 'queue') {
        return (gameQueueDepth(g1) - gameQueueDepth(g2)) * dirMult;
      }
      if (sortKey === 'velocity') {
        return (gameVelocity(g1) - gameVelocity(g2)) * dirMult;
      }
      return 0;
    });
    return copy;
  }, [visibleGames, sortKey, sortDir, marketSide, velocityNow, preserveGroupOrder]);

  // Score/Time column label mirrors what the cells actually render: pre-game
  // rows show kickoff time + countdown, started (live/final) rows show the
  // score, archived rows a dash (excluded). The old `.some(started)` rule
  // flipped the whole header to "Score" while every visible row still showed
  // a countdown, because one afternoon final poisons the evening slate.
  const liveRows = visibleGames.filter((g) => !g.archived);
  const startedCount = liveRows.filter(
    (g) => g.gameState === 'live' || g.gameState === 'final',
  ).length;
  const scoreColLabel =
    liveRows.length === 0 || startedCount === liveRows.length
      ? 'Score'
      : startedCount === 0
        ? 'Time'
        : 'Time / Score';

  return (
    // #826 — a <section> so the reading-position primitive can NAMESPACE this
    // table's row anchors. The same game can be on the Saved board and the
    // main board at once; without the section id both copies answer to one
    // anchor and a return journey seeks the wrong one.
    <section data-view-anchor={viewAnchorSection} className="bg-card border-border rounded-lg border">
      {/* Title bar — sport + market type. YES/NO + price-mode toggles live on
          the toolbar's row 2 above. */}
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-foreground text-lg font-semibold">
            {titleLabel ?? `${games[0]?.sport?.toUpperCase() ?? ''} ${MARKET_TYPE_LABELS[marketType]}`}
          </h2>
          {seriesTicker && (
            <>
              <span className="text-muted-foreground text-xs">&mdash;</span>
              <span className="text-muted-foreground font-mono text-xs">{seriesTicker}</span>
            </>
          )}
        </div>
        {/* Odds filter — discreet, right-aligned in the table's own header
            (Dan 2026-08-23): the chips govern THIS table's columns. */}
        <OddsFilterChips />
      </div>

      {/* Table — inside EdgeFadeScroller so columns past the edge (OP and US
          together, or any column once Game Detail narrows the board) are
          signalled by an edge fade down every visible row, not only by a
          scrollbar below the last row. */}
      <EdgeFadeScroller className={BOARD_SCROLL_FRAME_CLASS}>
        {/* Auto layout (NOT table-fixed — the Th width hints are smaller than
            real pill content, so fixed layout overlaps columns on narrow
            screens). Tick-to-tick jitter is prevented by rendering every
            live numeric cell in a constant-width span (w-[Nch]) so column
            max-content never changes between frames. */}
        <table className="w-full border-collapse border-spacing-0">
          <thead className="bg-muted/50 border-border border-b">
            <tr>
              <SortableTh
                className="w-28"
                sortKey="date"
                activeSortKey={sortKey}
                activeSortDir={sortDir}
                onSort={handleSort}
                sortable={!preserveGroupOrder}
                isPinned={isCurrentSortPinned && sortKey === 'date'}
                onPin={sortKey === 'date' ? handlePinSort : undefined}
              >
                Date
              </SortableTh>
              <SortableTh
                className="w-48"
                sortKey="team"
                activeSortKey={sortKey}
                activeSortDir={sortDir}
                onSort={handleSort}
                sortable={!preserveGroupOrder}
                isPinned={isCurrentSortPinned && sortKey === 'team'}
                onPin={sortKey === 'team' ? handlePinSort : undefined}
              >
                Team
              </SortableTh>
              <Th
                className="w-28 text-center"
                title="Kickoff time + countdown until the game starts, then the live/final score."
              >
                {scoreColLabel}
              </Th>
              {!hideOdds ? (
                <Th
                  className="w-16 text-center"
                  title="Unabated consensus win probability (pre-game / live); blank once the game is final."
                >
                  UNBTD
                </Th>
              ) : (
                // Odds filter: U values hidden — the slot stays as a slim
                // gutter because expanded ladders host resting-order chips
                // + the cancel-level control in this column.
                <Th className="w-10" title="Your resting orders at each ladder price level">{''}</Th>
              )}
              {!hideOp && (
                <Th
                  className="w-24 text-center"
                  title={opHeaderTooltip()}
                >
                  {OP_HEADER_LABEL}
                </Th>
              )}
              <Th className="w-32 text-center">Kalshi</Th>
              <Th
                className="w-24 text-right"
                title={VOL_HEADER_TOOLTIP_KALSHI}
              >{KALSHI_TOTAL_VOLUME_LABEL}</Th>
              <Th
                className="w-24 text-right"
                title={LIQ_HEADER_TOOLTIP_KALSHI}
              >K Liq</Th>
              <SortableTh
                className="w-16 text-right"
                sortKey="queue"
                activeSortKey={sortKey}
                activeSortDir={sortDir}
                onSort={handleSort}
                sortable={!preserveGroupOrder}
                isPinned={isCurrentSortPinned && sortKey === 'queue'}
                onPin={sortKey === 'queue' ? handlePinSort : undefined}
                title={`Best-bid depth on the active ${marketSide.toUpperCase()} side. Green <50k / Yellow 50–300k / Red ≥300k.`}
              >
                K Q
              </SortableTh>
              <SortableTh
                className="w-16 text-right"
                sortKey="velocity"
                activeSortKey={sortKey}
                activeSortDir={sortDir}
                onSort={handleSort}
                sortable={!preserveGroupOrder}
                isPinned={isCurrentSortPinned && sortKey === 'velocity'}
                onPin={sortKey === 'velocity' ? handlePinSort : undefined}
                title="Kalshi best-bid depth change over the last 1m on the active side. Positive = depth building; negative = draining. Empty until 1m of samples accumulates."
              >
                K Δ/m
              </SortableTh>
              <Th className="w-32 text-center">Poly</Th>
              <Th
                className="w-24 text-right"
                title={VOL_HEADER_TOOLTIP_POLY}
              >{POLY_TOTAL_VOLUME_LABEL}</Th>
              <Th
                className="w-24 text-right"
                title={LIQ_HEADER_TOOLTIP_POLY}
              >P Liq</Th>
              <Th
                className="w-16 text-right"
                title="Polymarket best-bid depth change over the last 1m on the active side. Same semantic as K Δ/m — pair them to spot cross-exchange sweep imbalance."
              >
                P Δ/m
              </Th>
              {showUs && (<>
                <Th
                  className="w-80 text-center"
                  title="Polymarket US moneyline: maker shows the best bid, taker the best ask. No last-trade price in this source."
                >
                  US
                </Th>
                <UsMetricHeaders />
              </>)}
              {showNovig && (
                <Th
                  className="w-20 text-center"
                  title="Novig moneyline as probability (each contract pays $0.01): maker shows the best bid, taker the best ask."
                >
                  Novig
                </Th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedGames.map((game, gameIndex) =>
              game.archived ? (
                <ArchivedRow
                  key={game.key}
                  game={game}
                  hasPosition={positionGameKeys?.has(game.key) ?? false}
                  hideOpColumn={hideOp}
                  hideOddsColumn={hideOdds}
                  showMinimizeToggle={minimizedLens != null}
                  showUsColumn={showUs}
                  showNovigColumn={showNovig}
                />
              ) : (
                <DashboardGameRows
                  key={game.key}
                  ringSlot="label"
                  game={game}
                  gameIndex={gameIndex}
                  marketSide={marketSide}
                  priceMode={priceMode}
                  expandedTeamSide={expandedGameKey === game.key ? expandedTeamSide : null}
                  onToggleExpand={onToggleExpand}
                  injuryEntries={injuryEntries}
                  injuryReportOpen={injuryReportKey === game.key}
                  onToggleInjuryReport={toggleInjuryReport}
                  onCloseInjuryReport={closeInjuryReport}
                  onTradeClick={onTradeClick}
                  positionByTicker={positionByTicker}
                  ordersByTicker={ordersByTicker}
                  botPresenceByTicker={botPresenceByTicker}
                  showScores
                  showRowDivergence
                  hideOpColumn={hideOp}
                  hideOddsColumn={hideOdds}
                  lt2CardScope={lt2MoneylineCardScope}
                  showMinimizeToggle={minimizedLens != null}
                  showUsColumn={showUs}
                  usMarket={usMarketFor(game)}
                  usLadderBook={game.key === expandedGame?.key ? usLadderBook : null}
                  onUsTradeClick={showUs ? onUsTradeClick : undefined}
                  showNovigColumn={showNovig}
                  novigQuote={showNovig ? (novigQuotes?.get(game.key) ?? null) : null}
                  onNovigTradeClick={showNovig ? onNovigTradeClick : undefined}
                />
              ),
            )}
            {archivedLoading && <ArchivedSkeletonRow hideOpColumn={hideOp} hideOddsColumn={hideOdds} showUsColumn={showUs} showNovigColumn={showNovig} />}
          </tbody>
        </table>
      </EdgeFadeScroller>

      {/* Empty state — suppressed while archived analytics is in flight
          so the skeleton row in the tbody gets to be the loading
          signal instead of a competing "No games available" message. */}
      {visibleGames.length === 0 && hiddenRowCount === 0 && !archivedLoading && (
        <div className="text-muted-foreground p-8 text-center">No games available</div>
      )}
      {hiddenRowCount > 0 && (
        <MinimizedFooter
          hiddenEmptyCount={hiddenEmptyCount}
          hiddenEmptySportsShown={hiddenEmptySportsShown}
          hiddenKalshiCount={hiddenKalshiCount}
          hiddenMinimizedRows={hiddenMinimizedRows}
          minimizedLens={minimizedLens}
        />
      )}
    </section>
  );
}

/**
 * The hidden-rows footer: one clause per REASON, plus the minimized list.
 *
 * One clause per reason because the three filters make different claims and a
 * merged count would assert the wrong one. The #212 clause names only the
 * sports whose rows were actually hidden here (#213), in canonical SPORT_TABS
 * order — never the configured suppression list, which may include sports
 * absent from this board. The #509 clause deliberately says "actionable":
 * those rows are not empty (they generally have a live Polymarket book) and
 * some do have a Kalshi market that is merely stale or unquoted. The #654
 * clause says "you minimized" rather than naming a rule, because that count is
 * the only one attributable to a deliberate per-game act rather than a board
 * setting.
 *
 * Why the minimized clause is a DISCLOSURE and the others are not (#654): the
 * lens contract asks that a minimized game stay "discoverable and restorable".
 * A count plus Show-all is restorable but not discoverable — it can tell you
 * that three games are gone but not which, so the only way to find out is to
 * restore all of them and lose the set you built. Expanding names them and
 * gives each its own restore. The other two reasons need no such list: they are
 * board-wide rules a trader turned on deliberately, reversed by the same chip.
 *
 * It lists the rows THIS TABLE hid, not the persisted mark list, for two
 * reasons. A mark id alpha-sorts its team codes, so only the row can say which
 * side was away; and a mark whose game isn't on this board is hiding nothing
 * here, so it has nothing to explain.
 */
function MinimizedFooter({
  hiddenEmptyCount,
  hiddenEmptySportsShown,
  hiddenKalshiCount,
  hiddenMinimizedRows,
  minimizedLens,
}: {
  hiddenEmptyCount: number;
  hiddenEmptySportsShown: readonly Sport[];
  hiddenKalshiCount: number;
  hiddenMinimizedRows: readonly GameRow[];
  minimizedLens?: MinimizedLens;
}) {
  const [open, setOpen] = useState(false);
  const minimizedCount = hiddenMinimizedRows.length;
  // Gates BOTH the trigger and the panel, so restoring the last row closes the
  // list without an effect to synchronise `open` — and when minimized is the
  // only reason anything is hidden, the whole footer unmounts and `open` resets
  // with it.
  const canList = minimizedCount > 0 && minimizedLens != null;

  const clauses = [
    hiddenEmptyCount > 0
      ? `${hiddenEmptyCount} empty ${hiddenEmptySportsShown.map((sport) => SPORT_LABEL[sport]).join('/')} matchup${hiddenEmptyCount === 1 ? '' : 's'} hidden`
      : null,
    hiddenKalshiCount > 0
      ? `${hiddenKalshiCount} matchup${hiddenKalshiCount === 1 ? '' : 's'} without an actionable Kalshi market hidden`
      : null,
  ].filter(Boolean);

  return (
    <div className="text-muted-foreground border-border/60 border-t px-4 py-3 text-xs">
      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center">
        {clauses.length > 0 && <span>{clauses.join(' · ')}</span>}
        {canList && (
          <>
            {clauses.length > 0 && <span aria-hidden>·</span>}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className="text-foreground/80 hover:text-foreground flex items-center gap-1 rounded font-semibold transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
            >
              {minimizedCount} matchup{minimizedCount === 1 ? '' : 's'} you minimized
              <ChevronDown size={12} className={cn('transition-transform', open && 'rotate-180')} />
            </button>
            <button
              type="button"
              onClick={minimizedLens.onRestoreAll}
              className="text-primary hover:text-primary/80 rounded font-semibold underline underline-offset-2 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
            >
              Show all
            </button>
          </>
        )}
      </div>

      {canList && open && (
        <ul className="border-border/60 mx-auto mt-2 flex max-w-md flex-col gap-1 border-t pt-2">
          {hiddenMinimizedRows.map((game) => (
            <li key={game.key} className="flex items-center justify-between gap-3">
              <span className="text-foreground/80 font-mono">
                {game.awayCode} @ {game.homeCode}
              </span>
              <button
                type="button"
                onClick={() => minimizedLens.onRestore(game)}
                className="text-primary hover:text-primary/80 rounded font-semibold underline underline-offset-2 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
              >
                Restore
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── ArchivedRow — single skinny row for closed games sourced from
//    /api/analytics/games. The date and each team code are deep-links into
//    Game Detail, and a team click seeds its selected team via ?team=. Tennis
//    links too since #1284 (#631 had made it plain text while the page was a
//    shell) — except an unstamped tennis row, which cannot name its tour and
//    renders plain text (gameDetailLinkForRow). No replacement tooltip: per
//    GameDetailLink's rule, the ABSENCE of the affordance is itself the
//    "no detail view exists" signal. Only K Vol / P Vol carry data — every
//    other column dashes since the analytics summary endpoint doesn't expose
//    closing prices/liquidity (tracked in
//    [[project_hydra_ask_analytics_games_with_prices]]).

interface ArchivedRowProps {
  game: GameRow;
  /** True when the user holds any position in this game — drives the
   *  left-border highlight so finished games the user actually traded
   *  pop in the post-game scan. */
  hasPosition: boolean;
  /** Match the table's OP-column state (final games always render "—"). */
  hideOpColumn?: boolean;
  /** US price + three metric columns; keep every row aligned. */
  showUsColumn?: boolean;
  /** #1150 — likewise the trailing Novig column. */
  showNovigColumn?: boolean;
  /** UNBTD sibling — Odds-filter default hides it. */
  hideOddsColumn?: boolean;
  /** #654 — render the minimize control beside the bookmark. A finished game
   *  is still minimizable, and must be: the lens hides minimized archived rows
   *  too, so this is where such a row is restored from. */
  showMinimizeToggle?: boolean;
}

function ArchivedRow({
  game,
  hasPosition,
  hideOpColumn = true,
  hideOddsColumn = false,
  showMinimizeToggle = false,
  showUsColumn = false,
  showNovigColumn = false,
}: ArchivedRowProps) {
  // #1400: collapsing a finished live row must not discard its available
  // final score. Keep the exact key (including gameNo) and orient by named
  // teams; legacy archive keys cannot safely identify a doubleheader game.
  const finalState = usePrimaryGameState(game.key, [game.awayCode, game.homeCode]);
  const finalScore = finalState?.statusId === 3
    && finalState.awayScore != null && finalState.homeScore != null
    ? `${finalState.awayScore} – ${finalState.homeScore}` : null;
  const archived = game.archived;
  if (!archived) return null;
  // Null for an unstamped tennis row: its codes render as plain text.
  const baseHref = gameDetailLinkForRow(game);
  // The row itself is `text-muted-foreground`; this lifts the date and team
  // codes back to near-full contrast because they are the row's identity.
  const labelClass = 'text-foreground/80';
  const linkClass =
    `${labelClass} hover:text-foreground hover:underline focus:outline-none focus-visible:underline`;
  const detailLink = (to: string, title: string, text: string) =>
    baseHref ? (
      <Link to={to} className={linkClass} title={title}>
        {text}
      </Link>
    ) : (
      <span className={labelClass}>{text}</span>
    );
  const dashCell = <span className="text-muted-foreground/40">—</span>;
  return (
    <tr
      className={cn(
        'bg-muted/10 text-muted-foreground border-t border-border/40 text-xs',
        hasPosition && 'border-l-2 border-l-cyan-400/60',
      )}
    >
      <Td className="whitespace-nowrap font-mono">
        <div className="flex items-center gap-1">
          <span className="flex items-center gap-1.5">
            <SaveGameToggle game={game} />
            {showMinimizeToggle && <MinimizeGameToggle game={game} />}
          </span>
          {detailLink(
            baseHref ?? '',
            'Open game detail (auto-picks winner)',
            formatGameDateLabel(game.date),
          )}
        </div>
      </Td>
      <Td className="whitespace-nowrap">
        {detailLink(
          `${baseHref}?team=${encodeURIComponent(game.awayCode)}`,
          `Open game detail from ${game.awayCode}'s perspective`,
          game.awayCode,
        )}
        <span className="text-muted-foreground/40 px-1">@</span>
        {detailLink(
          `${baseHref}?team=${encodeURIComponent(game.homeCode)}`,
          `Open game detail from ${game.homeCode}'s perspective`,
          game.homeCode,
        )}
        <span className="text-muted-foreground/60 ml-2 rounded bg-muted/40 px-1 py-px text-[9px] uppercase tracking-wider">
          Final
        </span>
      </Td>
      <Td className="text-left font-mono">
        {finalScore ? <span aria-label={`Final score: ${game.awayCode} ${finalState!.awayScore}, ${game.homeCode} ${finalState!.homeScore}`}>{finalScore}</span> : dashCell}
      </Td>
      {/* Odds placeholder — archived rows are one-line "AWAY @ HOME"
          summaries with no per-team consensus split. Slot is structural
          (orders gutter) — renders regardless of the odds filter. */}
      <Td className="text-right">{hideOddsColumn ? '' : dashCell}</Td>
      {/* OP placeholder — final games never show an OP price. */}
      {!hideOpColumn && <Td className="text-center">{dashCell}</Td>}
      <Td className="text-center">{dashCell}</Td>
      <Td className="text-right font-mono">
        {archived.kalshiVolume > 0 ? formatVolume(archived.kalshiVolume) : dashCell}
      </Td>
      <Td className="text-right">{dashCell}</Td>
      <Td className="text-right">{dashCell}</Td>
      {/* K Δ/m placeholder — archived rows have no live book to sample. */}
      <Td className="text-right">{dashCell}</Td>
      <Td className="text-center">{dashCell}</Td>
      <Td className="text-right font-mono">
        {archived.polyVolume > 0 ? formatVolume(archived.polyVolume) : dashCell}
      </Td>
      <Td className="text-right">{dashCell}</Td>
      {/* P Δ/m placeholder. */}
      <Td className="text-right">{dashCell}</Td>
      {showUsColumn && <><Td className="text-center">{dashCell}</Td><UsMetricCells /></>}
      {showNovigColumn && <Td className="text-center">{dashCell}</Td>}
    </tr>
  );
}

// Placeholder row shown while the analytics-backed archived fetch is in
// flight. Same column layout as ArchivedRow so the layout doesn't jump
// when real rows replace it. Pulse animation hints at "loading".
function ArchivedSkeletonRow({ hideOpColumn = true, hideOddsColumn = false, showUsColumn = false, showNovigColumn = false }: { hideOpColumn?: boolean; hideOddsColumn?: boolean; showUsColumn?: boolean; showNovigColumn?: boolean }) {
  const bar = (w: string) => (
    <span
      className={`inline-block h-2 ${w} animate-pulse rounded bg-muted-foreground/20`}
    />
  );
  return (
    <tr className="bg-muted/10 text-muted-foreground border-t border-border/40 text-xs">
      <Td className="whitespace-nowrap font-mono">{bar('w-10')}</Td>
      <Td className="whitespace-nowrap">{bar('w-24')}</Td>
      <Td className="text-left">{bar('w-10')}</Td>{/* Score */}
      <Td className="text-right">{hideOddsColumn ? '' : bar('w-10')}</Td>{/* Odds */}
      {!hideOpColumn && <Td className="text-right">{bar('w-10')}</Td>}{/* OP */}
      <Td className="text-center">{bar('w-4')}</Td>
      <Td className="text-right">{bar('w-10')}</Td>
      <Td className="text-right">{bar('w-4')}</Td>
      <Td className="text-right">{bar('w-4')}</Td>
      <Td className="text-right">{bar('w-4')}</Td>{/* K Δ/m */}
      <Td className="text-center">{bar('w-4')}</Td>
      <Td className="text-right">{bar('w-10')}</Td>
      <Td className="text-right">{bar('w-4')}</Td>
      <Td className="text-right">{bar('w-4')}</Td>{/* P Δ/m */}
      {showUsColumn && <><Td className="text-center">{bar('w-4')}</Td><UsMetricCells /></>}
      {showNovigColumn && <Td className="text-center">{bar('w-4')}</Td>}
    </tr>
  );
}

/** Rank-based consensus tone for the Odds column. `value` is this row's
 *  rounded fair-prob; `all` is every outcome's rounded fair-prob for the
 *  same game (2 entries binary, 3 with a soccer Draw). Highest renders
 *  green and the LOWEST blue — the same green-leader/blue-trailer read
 *  as the GameDetail title and chart — while the middle (3-way only)
 *  and any tied values render grey. */
function consensusRankTone(value: number, all: number[]): string {
  if (all.length === 0) return 'text-muted-foreground';
  if (all.filter((v) => v === value).length >= 2) return 'text-muted-foreground';
  const distinct = [...new Set(all)].sort((a, b) => b - a);
  const rank = distinct.indexOf(value);
  if (rank === 0) return 'text-positive';
  if (rank === distinct.length - 1) return 'text-blue-400';
  return 'text-muted-foreground';
}

// ── GameRows — team rows + paired ladders for the expanded game ──────

export interface GameRowsProps {
  game: GameRow;
  /** Render the trailing US price and metric cells. */
  showUsColumn?: boolean;
  /** The game's single US moneyline, or null when US lists none. */
  usMarket?: UsBoardQuote | null;
  /** Exact full-game spread/total binding; never reinterpret it as a moneyline. */
  usLine?: UsLineCell;
  /** #1234 — `usMarket`'s depth, for the US cells of the expanded ladder.
   *  Absent (as off the board), the US cells stay blank. */
  usLadderBook?: UsMarketBookData | null;
  onUsTradeClick?: UsTradeClick;
  /** #1150 — render the trailing Novig cell (board only). */
  showNovigColumn?: boolean;
  /** This game's Novig moneyline, or null when none was supplied. */
  novigQuote?: NovigBoardQuote | null;
  onNovigTradeClick?: (gameKey: string, side: TeamSide, click: NovigBoardClick) => void;
  gameIndex: number;
  marketSide: MarketSide;
  priceMode: PriceMode;
  /** null when no team in this game is expanded */
  expandedTeamSide: TeamSide | null;
  onToggleExpand: (key: string, side: TeamSide) => void;
  /** #1389 — an NBA game's team rows carry the board injury entry (a held
   *  line under the team label; see InjuryEntries) and, while open, its
   *  inline read-only report row. The table owns which game is open. */
  injuryEntries?: boolean;
  injuryReportOpen?: boolean;
  onToggleInjuryReport?: (gameKey: string, origin: HTMLElement) => void;
  onCloseInjuryReport?: () => void;
  /** GameDetail YES summaries select a book; other callers retain trading. */
  onSummaryYesSelect?: (gameKey: string, side: TeamSide, venue: 'kalshi' | 'polymarket') => void;
  onSummaryNoSelect?: (gameKey: string, side: TeamSide, venue: 'kalshi' | 'polymarket') => void;
  /** Game Detail depth override; the dashboard keeps its existing depth. */
  ladderLevels?: number;
  dualOutcomeLadder?: boolean;
  /** #887 — names the two outcome columns of a `dualOutcomeLadder`. Omitted,
   *  they read YES and NO (every derivative caller). The optional moneyline
   *  experiment supplies the team, since a bare YES does not say whose
   *  contract it is once both teams' books are open at once. */
  dualOutcomeLabels?: (teamSide: TeamSide) => { yes: string; no: string; caption?: string };
  lineReference?: LadderReference;
  onTradeClick?: (
    gameKey: string,
    side: 'away' | 'home',
    venue: 'kalshi' | 'polymarket',
    opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no'; postOnly?: boolean; liquiditySeed?: BookLiquiditySeed | null },
  ) => void;
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
  botPresenceByTicker?: Map<string, AgentStats[]>;
  /** Drop the leading Date column from the row + ladder. Consumers like
   *  GameDetail show the date in the page header and don't need it
   *  repeated in every table row. Caller is responsible for matching
   *  the column-count in their own <thead>. */
  hideDateColumn?: boolean;
  /** Drop the Score/Time column (GameDetail's Game Lines table). Caller
   *  matches the column-count in its own <thead>. */
  hideScoreColumn?: boolean;
  /** Drop the Odds (consensus %) column — in the ladder rows this is the
   *  user-orders chip column, also dropped. Game Lines table. */
  hideOddsColumn?: boolean;
  /** Drop the OP (OddsPapi) column. DEFAULTS TRUE — the opposite of the
   *  sibling hide flags, deliberately: OP is additive and opt-in per
   *  surface, so a GameRows consumer that predates (or ignores) the OP
   *  column keeps its exact pre-feature column set instead of silently
   *  growing a 14th cell under a 13-column thead. Surfaces that render
   *  the OP header pass their computed visibility here. */
  hideOpColumn?: boolean;
  /** #1112 — drop the four Polymarket columns (price, P Vol, P Liq, P Δ/m)
   *  from the team rows, the expanded book rows and the book caption.
   *  Defaults off, so every existing caller keeps its column set; Game
   *  Detail's prop tabs pass it when no row there carries a Polymarket
   *  market. Caller drops the matching headers in its own <thead>. */
  hidePolyColumns?: boolean;
  /** #1112 — expanding a row moves no column and no row above it: the blue
   *  selection box draws as inset shadows instead of 2px borders, a
   *  group's first row keeps its 2px divider slot, the expanded label chip
   *  keeps the plain label's footprint, and the ladder caption's "Resting
   *  Qty" / "Resting $" labels stop sizing their columns (under auto
   *  table-layout any wider cell takes a bigger share of the table's spare
   *  width). One exception stays: a rung holding the trader's own resting
   *  orders still widens the orders column wherever #1070 reserves no
   *  gutter: no Score column to span (Game Lines), no one-click grant, or
   *  the expanded order display.
   *  Defaults off, so every existing caller renders as before; Game
   *  Detail's Kalshi-only prop tabs pass it. */
  holdColumnsOnExpand?: boolean;
  /** Render ONLY this outcome's row + ladder instead of the away/home pair.
   *  For non-binary period bets (the 1H winner's Tie, single spread/total
   *  lines) that don't fit the two-team model. */
  singleSide?: TeamSide;
  /** #651 — persistent Yes/No side tint on this row's price pills. Opt-in and
   *  default off, so every existing caller (dashboard moneyline, the Moneyline
   *  section, props) renders byte-identical. The spread/total DETAILED views
   *  pass it because they show the same markets as the compact chip-strip
   *  table, which carries the tint — without this the two modes of one section
   *  disagree about what green means. */
  sideTint?: boolean;
  /** Width budget (px) for the team/bet label. Default 150 (dashboard). Game
   *  Lines passes a wider value because its labels are full sentences ("NYK
   *  wins by over 7.5") that the expanded selection box would otherwise
   *  truncate. A cap normally; the exact width on rows that hold the ring
   *  slot (#1108). */
  labelWidthPx?: number;
  /** Prop labels carry the threshold and period: keep the whole contract
   * visible inside the existing width budget, rather than hiding it on hover.
   * Dashboard competitor names keep their existing single-line treatment. */
  wrapLabel?: boolean;
  /** Where the position/order rings get a held slot, so a fill or a resting
   *  order never moves a pill. Without it the rings are appended after the
   *  label and widen the Team column when they appear.
   *  - `label` (#1108, main board, one-click traders): the label is exactly
   *    `labelWidthPx` wide and the rings sit inside it, so a ring only
   *    shortens the (truncating) name. Costs up to (budget − widest name) px
   *    on a short-name slate.
   *  - `trailing` (#1070, Game Detail, every trader): a fixed two-ring-wide
   *    slot after the label. Game Detail's single game would pay the whole
   *    label budget for its short names (+31px of overflow at 1920 with the
   *    ladder open); this costs exactly what two rings already cost on main. */
  ringSlot?: 'label' | 'trailing';
  /** Suppress the blue selection box around the expanded game. Game
   *  Detail's moneyline section passes it: that table only ever shows a
   *  single moneyline, so the box is redundant noise there — the darker
   *  selected tint is kept, just the border is dropped. */
  hideSelectionBox?: boolean;
  /** Render the Unabated live score on the right edge of the Team cell
   *  for live + final games. Caller is responsible for mounting
   *  `useGameStatesPoll(...)` upstream so `gameStatesStore` has fresh
   *  data; GameRows just reads from the store. */
  showScores?: boolean;
  /** Fresh game state from the per-game WS odds stream (GameDetail). When
   *  provided it takes precedence over the analytics-REST gameStatesStore
   *  lookup, so the moneyline score tracks the live header instead of the
   *  laggier poll. */
  gameStateOverride?: OddsStreamGameState | null;
  /** Caller-determined "this market has resolved" flag (e.g. GameDetail's
   *  Game Lines table checks marketStore status per ticker). Dims the row
   *  and renders a SETTLED chip beside the bet label. Independent of
   *  `game.gameState === 'final'` on purpose: period synth rows never
   *  carry a final gameState (synthesize.ts), and the FINAL chip's home
   *  (the Date column) is hidden on those surfaces. */
  settled?: boolean;
  /** Marks a singleSide row that visually CONTINUES the previous GameRows
   *  group — the soccer Draw row under its away/home pair, the 1H Tie under
   *  the 1H moneyline pair. The row renders as a non-first row: no thick
   *  game-boundary top border, no repeated date / kickoff time, and (since
   *  zebra striping keys off gameIndex) the caller passes the SAME gameIndex
   *  as the group being continued so all three outcomes share one
   *  background. */
  continuation?: boolean;
  /** True when ANOTHER GameRows instance in this game's visual group is the
   *  expanded one (the Draw/Tie continuation when the pair is open, or the
   *  pair when the Draw/Tie is open). Applies the selected treatment — dark
   *  tint, blue side rails, zebra/boundary suppression — WITHOUT expanding
   *  this instance's own ladders, so the blue box wraps the whole 3-way
   *  market instead of just the clicked outcome's rows. */
  groupSelected?: boolean;
  /** The selection box continues below this instance (a Draw/Tie
   *  continuation row follows in the same group): leave the box open at the
   *  bottom — the continuation row (or its ladder) carries the bottom rail. */
  groupExtendsBelow?: boolean;
  /** This row IS the 3-way Draw outcome. The Odds column reads the game
   *  consensus' drawFairProbCents, and the per-team score cell stays blank —
   *  the synth's away slot holds the DRAW market, so the away team's fair
   *  prob / live goals would render on the wrong row otherwise. */
  oddsOutcome?: 'draw';
  /** This row IS a knockout "To Advance" 2-way. Read the dedicated advance
   *  consensus (marketType 'advance') instead of the 90-minute result one — they
   *  share the canonicalGameKey but are DIFFERENT markets ("to advance" incl. ET
   *  + pens). When hydra hasn't quoted an advance line, the Odds column shows
   *  "—" (never the 1X2 value). The live score is unaffected. */
  advanceConsensus?: boolean;
  /** Rendered directly BENEATH the bet label, inside the same cell — the
   *  arrangement compact Spread/Total uses in its "Side & Line" cell
   *  (description on top, line strip under it). GameDetail's compact Player
   *  Props passes the threshold MiniLineStrip here so a player's whole ladder
   *  is one row of the NORMAL table, analytics columns intact. First row of a
   *  group only, like the SETTLED chip. */
  labelAccessory?: ReactNode;
  /** #1389 — a held status line under the label (Player Props: the player's
   *  exact-match injury status). First row of a group only, like the accessory. */
  labelInjury?: ReactNode;
  /** Explicit canonical game key for the consensus lookup, overriding the one
   *  derived from `game.key`. Use when the row key carries a synth suffix that
   *  wouldn't resolve AND the row doesn't show scores — e.g. GameDetail's
   *  "90 min" Game Lines moneyline (the demoted 1X2, keyed `${game.key}|full-ml`):
   *  it's the full-game result, so it owns the same Unabated consensus the
   *  round-robin moneyline pairs. Pass the bare `game.key`. Undefined = derive
   *  as usual (gated on showScores); null = no consensus. */
  consensusKey?: string | null;
  /** Full-feed B: pin this row's OP/PNCL cell to an explicit OP bucket
   *  (period bet, line family) instead of the full-game moneyline. A pinned
   *  bucket with no data renders "—" — never a fallback to a different
   *  market's price. Trend arrows / anchor-withdrawal strike stay full-game
   *  vocabulary and are suppressed on pinned rows (the slice's own
   *  withdrawnBooks still render). */
  opBucket?: import('./types').OpBucketRef | null;
  /** #179: dashboard moneyline-only Poly/Kalshi divergence cue. Shared
   *  consumers default off — GameRows also backs Game Detail, spreads/totals,
   *  props/events and the multi-outcome table, and the ask was scoped to the
   *  Value Dashboard. Opting in explicitly (rather than inferring from
   *  `hideDateColumn`/`singleSide`) is what keeps it from leaking. */
  showRowDivergence?: boolean;
  /** #647: opt this instance's Kalshi moneyline rows into the A/D hotkey
   *  focus contract. Same leak rule as `showRowDivergence` — shared consumers
   *  (multi-outcome table, spread/total, props/events, Saved board) default
   *  off and stay byte-identical. Rows without a trustworthy Kalshi ticker
   *  and FINAL rows never register. */
  lt2CardScope?: Lt2MoneylineCardScope;
  /** #654 — render the minimize control beside the bookmark. Off by default so
   *  every other consumer of this component (and every board without the lens)
   *  keeps a Date cell that is pixel-identical to today. */
  showMinimizeToggle?: boolean;
}

/**
 * Market-level aggregate "Vol" cell (#154). The value is cumulative TRADED
 * volume when the venue reported it (`mkt.volume` / `polyMkt.volume`) — kept
 * labeled "Volume". Two truthfulness cues, driven by flags the marketStore
 * builders set, so the number isn't read as something it isn't:
 *   - `volumeIsDepthDerived` (buildVenueBookFromMarket fell back to YES+NO
 *     resting depth because no traded volume was on the frame) → a leading "~"
 *     + a "resting depth" tooltip, visibly distinct from executed volume.
 *   - `isSharedBinaryBook` (one binary market underlies both rendered outcomes,
 *     so this is the WHOLE BOOK's total) → a superscript "ˢ" + a "shared"
 *     tooltip, so complementary rows don't read as independent per-outcome
 *     totals. Set by `buildPolyMoneylinePair` and by the poly spread/total
 *     lines; see the flag's docstring in types.ts for why the claim is about
 *     the book rather than about how many rows happen to show it.
 * Presentation only — `book.volume` itself is unchanged.
 *
 * The same flag drives `AggregateLiquidityCell` below: a shared binary book
 * shares BOTH figures, and cueing only one was the gap flagged in review.
 *
 * Exported for #154's focused label/marker test.
 */
export function AggregateVolumeCell({ book }: { book: VenueBook | null | undefined }) {
  // Ticker-scalar authority: traded volume is a ticker/trade-channel value.
  // Until this session has observed one for the row it renders as a dash —
  // never a muted historical figure, which can look actionable.
  if (book?.tickerSourceValidity === 'unconfirmed') {
    return (
      <span className="inline-block w-[6ch] text-right" title={TICKER_SCALAR_UNOBSERVED_TOOLTIP}>—</span>
    );
  }
  const hasVal = book != null && book.volume > 0;
  const depthDerived = hasVal && book!.volumeIsDepthDerived === true;
  // NOT gated on a non-zero value: `polyMkt.volume ?? 0` makes zero a routine
  // state for a genuinely shared book, and gating dropped the cue exactly there
  // while the Liq cell beside it kept it.
  const shared = book != null && book.isSharedBinaryBook === true;
  const title = depthDerived
    ? 'Resting depth (bids + asks) — no cumulative traded volume was reported for this market; this is book depth, NOT executed volume'
    : shared
      ? SHARED_VOL_TOOLTIP
      : undefined;
  return (
    <span
      key={book?.volume}
      className={cn(
        'animate-flash-neutral inline-block w-[6ch] text-right',
        depthDerived && 'italic',
      )}
      title={title}
    >
      {depthDerived && <span aria-hidden>~</span>}
      {book ? formatVolume(book.volume) : '—'}
      {shared && <SharedBookMarker />}
    </span>
  );
}

/** Tooltip for a ticker-only scalar (last price / volume) that this session
 *  has not observed for the row: the book proves the line; the ticker quote
 *  has simply not arrived since the feed's current generation began. */
export const TICKER_SCALAR_UNOBSERVED_TOOLTIP =
  'Not yet observed — no Kalshi ticker quote for this market since the feed\u2019s current generation began; the order book is confirmed and governs the line';

/** The shared-binary-book marker itself. One component so the Vol and Liq cells
 *  can't drift apart in size, colour or alignment — they are cueing the same
 *  fact about the same book. `aria-hidden` because the tooltip on the enclosing
 *  cell already carries the meaning in words. */
function SharedBookMarker() {
  return (
    <span className="text-muted-foreground/70 align-super text-[0.6em]" aria-hidden>
      s
    </span>
  );
}

/**
 * Market-level aggregate "Liq" cell (#154). Mirrors `AggregateVolumeCell`'s
 * shared-book cue for liquidity: a Polymarket binary book shares its `liquidity`
 * across both rendered outcomes exactly as it shares `volume`, and marking only
 * the volume left the duplicated liquidity reading as an independent per-outcome
 * total — the gap raised in review.
 *
 * There is no depth-derived branch here: `liquidity` is always resting book
 * depth, so it has no traded-vs-resting ambiguity to disambiguate.
 *
 * Exported so SpreadsTotalsTable renders the identical cell rather than its own
 * copy, and for #154's focused marker test.
 */
export function AggregateLiquidityCell({ book }: { book: VenueBook | null | undefined }) {
  const shared = book != null && book.isSharedBinaryBook === true;
  return (
    <span
      className="inline-block w-[7ch] text-right"
      title={shared ? SHARED_LIQ_TOOLTIP : undefined}
    >
      {book ? formatCompactCurrency(book.liquidity) : '—'}
      {shared && <SharedBookMarker />}
    </span>
  );
}

/** Qualified Kalshi depth display on the moneyline row only. */
function KalshiLiquidityCell({ book }: { book: VenueBook | null | undefined }) {
  const shared = book != null && book.isSharedBinaryBook === true;
  const display = bookForDisplayOnly(book);
  const unavailable = book?.bookSourceValidity === 'unconfirmed' && !book.retainedDepth;
  return (
    <span
      className="relative inline-block w-[7ch] text-right"
      title={shared ? SHARED_LIQ_TOOLTIP : undefined}
    >
      {display && !unavailable ? formatCompactCurrency(display.liquidity) : '—'}
      <span className="absolute right-0 top-full text-[9px] leading-none text-amber-400" aria-hidden={!book?.retainedDepth}>{book?.retainedDepth ? 'last' : '\u00a0'}</span>
      {shared && <SharedBookMarker />}
    </span>
  );
}

/** #1112 — the blue selection box's rails as inset shadows: same 2px blue
 *  lines as the borders, but a shadow takes no layout space, so the rails
 *  appearing on expand can't widen the table or push a row's content.
 *  Literal strings so Tailwind's scanner sees each one. */
const SELECTION_RAIL_SHADOW = {
  sides: 'shadow-[inset_2px_0_0_0_theme(colors.blue.500),inset_-2px_0_0_0_theme(colors.blue.500)]',
  top: 'shadow-[inset_2px_0_0_0_theme(colors.blue.500),inset_-2px_0_0_0_theme(colors.blue.500),inset_0_2px_0_0_theme(colors.blue.500)]',
  bottom: 'shadow-[inset_2px_0_0_0_theme(colors.blue.500),inset_-2px_0_0_0_theme(colors.blue.500),inset_0_-2px_0_0_theme(colors.blue.500)]',
  both: 'shadow-[inset_2px_0_0_0_theme(colors.blue.500),inset_-2px_0_0_0_theme(colors.blue.500),inset_0_2px_0_0_theme(colors.blue.500),inset_0_-2px_0_0_theme(colors.blue.500)]',
} as const;

/** The selection box's rails for one row: side rails always, plus the top /
 *  bottom rail where this row opens / closes the box. Borders by default
 *  (the class string every surface has always rendered); inset shadows
 *  under `holdColumnsOnExpand`. */
function selectionRails(top: boolean, bottom: boolean, layoutFree: boolean): string {
  if (layoutFree) {
    return SELECTION_RAIL_SHADOW[top && bottom ? 'both' : top ? 'top' : bottom ? 'bottom' : 'sides'];
  }
  return cn('border-blue-500 border-l-2 border-r-2', top && 'border-t-2', bottom && 'border-b-2');
}

export function GameRows(props: GameRowsProps) {
  observeGameRowsRender(props);
  const {
  game,
  gameIndex,
  marketSide,
  priceMode,
  expandedTeamSide,
  onToggleExpand,
  injuryEntries = false,
  injuryReportOpen = false,
  onToggleInjuryReport,
  onCloseInjuryReport,
  onTradeClick,
  onSummaryYesSelect,
  onSummaryNoSelect,
  ladderLevels = LADDER_LEVELS,
  dualOutcomeLadder = false,
  dualOutcomeLabels,
  lineReference,
  positionByTicker,
  ordersByTicker,
  botPresenceByTicker,
  hideDateColumn = false,
  hideScoreColumn = false,
  hideOddsColumn = false,
  hideOpColumn = true,
  hidePolyColumns = false,
  holdColumnsOnExpand = false,
  singleSide,
  sideTint = false,
  labelWidthPx = 150,
  wrapLabel = false,
  ringSlot,
  hideSelectionBox = false,
  showScores = false,
  gameStateOverride = null,
  settled = false,
  continuation = false,
  groupSelected = false,
  groupExtendsBelow = false,
  oddsOutcome,
  advanceConsensus = false,
  labelAccessory,
  labelInjury,
  consensusKey,
  opBucket = null,
  showRowDivergence = false,
  lt2CardScope,
  showMinimizeToggle = false,
  showUsColumn = false,
  usMarket = null,
  usLine,
  usLadderBook = null,
  onUsTradeClick,
  showNovigColumn = false,
  novigQuote = null,
  onNovigTradeClick,
  } = props;
  // #1389 — the inline injury report's id (unique per rendered game, so a
  // game on both the Saved and main boards never shares one).
  const injuryReportId = useId();
  const showInjuryEntries = injuryEntries && game.sport === 'nba' && !singleSide;
  // Maker-fee rebate fraction (0..1) for this game's maker-perspective odds.
  // 0 unless the org is enrolled in Kalshi's MM Program AND the MM Rebate
  // toggle is on. Live games rebate 100% of the maker fee, pregame 50%.
  // Same fraction for every row in the group (one game) and both venues —
  // Polymarket has no fee schedule so it's a no-op there.
  const makerRebateActive = useMakerRebateActive();
  // #730 — the row's own clock. Four cells here are functions of time as well
  // as data: the #179 divergence badge (poly book age), the Kalshi-lag value
  // signal (poly + consensus age), the consensus age chip, and the K/P Δ/m
  // depth-velocity window. They used to be fresh only because the dashboard
  // re-rendered every row on every ~30 Hz publication; a row that can bail out
  // of a parent render has to tick for itself or a badge could outlive its own
  // expiry until unrelated activity happened to re-render it. One shared 1 s
  // registry (not a timer per row), off for final/settled rows — nothing on
  // them ages. Every threshold below is ≥ 60 s, so a 1 s grid is invisible.
  const rowNow = useSharedNow(1000, game.gameState !== 'final' && !settled);
  const makerRebateFraction = makerRebateActive
    ? makerRebatePct({ isLive: game.gameState === 'live' })
    : 0;
  // Pull Unabated state for this game when scores are wanted. The
  // dashboard's poll keeps the store fresh; here we just consume.
  // `useGameStateFor` returns null when the gameKey isn't in the
  // store yet — caller's showScores=false code path doesn't run the
  // lookup at all.
  // Multi-outcome synth keys carry a `|multi|<code>` suffix — strip it so
  // the Draw row's consensus lookup resolves to the game (binary keys pass
  // through untouched).
  const hydraGameKey = showScores ? toHydraGameKeyLive(game.key.split('|multi|')[0]) : null;
  const storeGameState = useGameStateFor(hydraGameKey);
  const gameStateLastFetchMs = useGameStatesStore((s) => s.lastFetchMs);
  const gameStateLastAttemptMs = useGameStatesStore((s) => s.lastAttemptMs);
  const gameStateError = useGameStatesStore((s) => s.error);
  // #163: the dashboard already renders score/period text from this batched
  // game-state snapshot. The adjacent Date chip must not call that same row
  // UNKNOWN while the poll is current. A failed poll may retain the last good
  // map for score continuity, so stop trusting it for LIVE once the snapshot
  // has aged past the bounded stale window. lastAttemptMs advances on every
  // retry, which makes this transition without a timer per table row.
  const storeGameStateIsCurrent =
    gameStateLastFetchMs > 0 &&
    !isGameStateFeedStale(gameStateError, gameStateLastFetchMs, gameStateLastAttemptMs);
  // Prefer the per-game WS odds-stream state (GameDetail) when present — it
  // updates in real time; the analytics-REST store lags. Falls back to the
  // store for the dashboard (no override) and during the WS connect window.
  const primaryGameState = usePrimaryGameState(hydraGameKey, [game.awayCode, game.homeCode]);
  const liveGameState = primaryGameStateEnabled() ? primaryGameState : gameStateOverride ?? storeGameState;
  // Consensus is cached per the doubleheader-safe v2 key, which IS the row key
  // (`game.key`). Pass it straight through (minus the `|multi|` synth suffix) so
  // a doubleheader's two rows read their own un-blended consensus instead of
  // collapsing onto one legacy `${sport}|${date}|${A}|${B}` key. The gameStates
  // REST poll above still keys by the legacy 3-seg shape (the analytics surface
  // is gameNo-blind) — that's the only doubleheader blend left, and only on the
  // dashboard score, not the per-game page.
  // Canonical key for the consensus lookup. An explicit `consensusKey` prop wins
  // (Game Lines' "90 min" moneyline passes the bare game key — its synth row key
  // has a `|full-ml` suffix and it shows no scores). Otherwise derive from the
  // row key: strip the `|multi|<code>` synth suffix AND the advance headline's
  // `|advance` suffix, gated on showScores like the live score is.
  const derivedConsensusKey = showScores
    ? game.key.split('|multi|')[0].replace(/\|advance$/, '')
    : null;
  const effectiveConsensusKey = consensusKey !== undefined ? consensusKey : derivedConsensusKey;
  // Advance rows read the 2-way "To Advance" consensus (marketType 'advance');
  // every other row reads the regulation result. Both hooks always run (hooks
  // rule) — the inactive one gets a null key and no-ops. A missing advance
  // consensus stays null → "—", never the 90-min value.
  const resultConsensus = useConsensusFor(advanceConsensus ? null : effectiveConsensusKey);
  const advanceConsensusValue = useAdvanceConsensusFor(advanceConsensus ? effectiveConsensusKey : null);
  const liveConsensus = advanceConsensus ? advanceConsensusValue : resultConsensus;
  // Sub-cent trend/recency state for the same bucket the value above came
  // from. Same null-key hooks-rule dance; useConsensusGlow re-renders this
  // row on the shared 4 Hz ticker only while a fade is visibly in flight.
  const resultTrend = useConsensusTrendFor(advanceConsensus ? null : effectiveConsensusKey);
  const advanceTrend = useAdvanceConsensusTrendFor(advanceConsensus ? effectiveConsensusKey : null);
  const consensusGlow = useConsensusGlow(advanceConsensus ? advanceTrend : resultTrend);
  // OP odds — same key vocabulary as the consensus lookup, independent
  // store map. Advance rows read null on purpose: OP's moneyline is the
  // RESULT market; showing it on the "To Advance" row would present a
  // different market's price under that row's label ("—" instead, exactly
  // like a missing advance consensus). Hooks rule: always runs, null key
  // no-ops.
  // Full-feed B: a pinned bucket reads through the generalized lookup; the
  // default (1, 'moneyline') is byte-identical to the historical
  // full-game-moneyline read, so unpinned rows are untouched.
  const opSlice = useOpMarketFor(
    hideOpColumn || advanceConsensus ? null : (opBucket?.gameKey ?? effectiveConsensusKey),
    opBucket?.periodKey ?? 1,
    opBucket?.marketType ?? 'moneyline',
    opBucket?.points,
    opBucket?.prop,
  );
  // OP's own trend feed (velocity arrows + recency brightness) — separate
  // map, same engine + shared 4 Hz decay ticker as the UNBTD column. The
  // grey arrow rests OP_STALE_MS (vs U's ~10s): OP consensus updates
  // minutes apart, so the U lifetime would leave the arrow invisible in
  // practice — the long rest keeps the LAST move's direction readable
  // between updates (Dan 2026-08-04).
  const opTrend = useOpTrendFor(hideOpColumn || advanceConsensus || opBucket != null ? null : effectiveConsensusKey);
  const opGlow = useConsensusGlow(opTrend, OP_STALE_MS);
  // Last-known anchor fair values — the phase-3 strikethrough's number.
  const opLastAnchor = useOpLastAnchorFairFor(
    hideOpColumn || advanceConsensus || opBucket != null ? null : effectiveConsensusKey,
  );
  // Clock authority for the OP arrow's grey-rest HIDE (round 3): the
  // 5-min rest sits outside the engine's hot ticker (which covers only
  // the bright decay), so arm one bounded re-render per side boundary —
  // otherwise a resting arrow could outlive its lifetime until unrelated
  // activity re-rendered the rows.
  useDeadlineTick(
    (['away', 'home', 'draw'] as const).map((s) => {
      const ms = opTrend?.[s].lastDeltaMs;
      return ms != null && ms > 0 ? ms + CONSENSUS_TREND_WINDOW_MS + OP_STALE_MS : null;
    }),
  );
  // ── Consensus fair-prob reads (shared: Odds column + ladder fair dot) ──
  // consensus.awayTeam may land in the row's homeCode slot under the
  // alpha-sorted canonical key, so swap when it does. Draw rows read the
  // 3-way draw prob directly — there's no away/home slot to swap.
  const consensusSwapped = liveConsensus != null && liveConsensus.awayTeam === game.homeCode;
  const fairFor = (teamSide: TeamSide): { cents: number | null; tenths: number | null } => {
    const c = liveConsensus;
    if (c == null) return { cents: null, tenths: null };
    if (oddsOutcome === 'draw') {
      return { cents: c.drawFairProbCents ?? null, tenths: c.drawFairProbTenths ?? null };
    }
    // Tenths (sub-cent feed, since 2026-07-05) are null on pre-rollout
    // hydra — integer cents fall back in wherever tenths are consumed.
    const useAway = (teamSide === 'away') !== consensusSwapped;
    return {
      cents: useAway ? c.awayFairProbCents : c.homeFairProbCents,
      tenths: (useAway ? c.awayFairProbTenths : c.homeFairProbTenths) ?? null,
    };
  };
  const showLiveScore =
    showScores &&
    liveGameState != null &&
    (liveGameState.statusId === 2 || liveGameState.statusId === 3);
  const awayScoreDisplay = showLiveScore ? liveGameState.awayScore : null;
  const homeScoreDisplay = showLiveScore ? liveGameState.homeScore : null;
  // Period status replaces the scheduled event start time in the date column
  // once the game is actually live (statusId === 2). Source comes from
  // the same gameStates poll the score uses; renders null pre-game and
  // for finals (those get their FINAL chip in the first line instead).
  const liveStatusText =
    showScores && liveGameState && liveGameState.statusId === 2
      ? formatGameStatus(liveGameState, game.sport)
      : null;
  // ── Status display state (#121) ─────────────────────────────────────
  // Which of LIVE / FINAL / UNKNOWN / date the Date cell shows, and which
  // branch the Score cell takes. Derived from canonical game state, NOT from
  // `game.gameState` — that field is marketStore's "book is tradable AND the
  // clock passed the scheduled start" heuristic, which calls a postponed game
  // live for as long as its book stays open.
  //
  // ── The canonical state is INJECTED, deliberately ───────────────────
  // The focused GameDetail odds stream remains the first choice. On dashboard
  // rows, #163 permits the already-rendered batched state to drive this chip
  // only while its poll is current. This is deliberately a consistency fix,
  // not a new status source or a clock-derived LIVE inference.
  //
  // Scoped to those two cells on purpose. `isFinal` below still drives row
  // treatment (price pills, dimming, the winner check, velocity alerts) off
  // the row state, exactly as today — re-sourcing that would reach well past
  // the indicator this issue asks for.
  const chipState = resolveLiveDisplayState(
    primaryGameStateEnabled() ? primaryGameState : gameStateOverride ?? (storeGameStateIsCurrent ? storeGameState : null),
    game.gameState,
  );
  // Keep the expanded group reachable without re-introducing the jarring
  // page shift this code used to have (full auto-scroll was removed for
  // that). The away ladder inserts ~7 rows ABOVE the clicked row, so a
  // click low in the viewport pushed the row + entire home ladder below
  // the fold and the book looked like it failed to open (Prof's Loom,
  // Jun 12). block:'nearest' is the compromise: zero motion when the
  // group already fits, minimal nudge when it doesn't. rAF so the
  // freshly inserted ladder rows are laid out before measuring.
  const groupTopRef = useRef<HTMLTableRowElement | null>(null);
  const isExpandedNow = expandedTeamSide !== null;
  useEffect(() => {
    if (!isExpandedNow) return;
    const raf = requestAnimationFrame(() => {
      groupTopRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [isExpandedNow]);
  const rowHasStaleMarket =
    game.kalshi?.away.freshness === 'stale' ||
    game.kalshi?.home.freshness === 'stale' ||
    game.polymarket?.away.freshness === 'stale' ||
    game.polymarket?.home.freshness === 'stale';

  // ── #647 — A/D hotkey-focus registration (Kalshi moneyline rows) ─────
  // Resolver precedent from SpreadsTotalsTable: the fee-hidden display
  // decision is computed where the row renders and captured in the resolver
  // closure — a hidden price is unavailable to keyboard input too. FINAL
  // rows and rows without a trustworthy Kalshi ticker never register, and a
  // vanished ticker un-registers, taking a held focus with it.
  const lt2Access = useLt2Access();
  const lt2HotkeysEnabled = useDisplaySettings((s) => s.lt2HotkeysEnabled);
  // The ghost focus chip renders only while one-click is ARMED — armed is
  // exactly when A/D matter, and the idle board stays uncluttered.
  const lt2Armed = useOneClickStore((s) => s.armed);
  // #1070: a trader who can one-click from this board (grant slot, so a
  // restored grant holds it too) gets the Kalshi pills' odds width held.
  const oneClickSlot = useGrantSlot('oneClickAccess') !== 'hidden';
  const lt2FeeAdjusted = useDisplaySettings((s) => s.feeAdjustedOdds);
  const lt2HideUnknownFees = useDisplaySettings((s) => s.hideUnknownFeePrices);
  const lt2On = lt2Access && lt2HotkeysEnabled && lt2CardScope != null;
  const lt2AwayTicker = game.kalshi?.away.marketTicker || null;
  const lt2HomeTicker = game.kalshi?.home.marketTicker || null;
  const lt2AwayFeeEntry = useFeeFor(lt2On ? lt2AwayTicker : null);
  const lt2HomeFeeEntry = useFeeFor(lt2On ? lt2HomeTicker : null);
  const lt2AwayFeeStatus = useFeeDisplayStatus(lt2On ? lt2AwayTicker : null, lt2AwayFeeEntry);
  const lt2HomeFeeStatus = useFeeDisplayStatus(lt2On ? lt2HomeTicker : null, lt2HomeFeeEntry);
  const lt2GameFinal = game.gameState === 'final';
  // GAME-level A/D (packet §V2B): one card per two-team game — A is the
  // visually top/away team's NO, D the bottom/home team's NO. Registration
  // requires the full pair on this renderer: a singleSide or continuation
  // GameRows (period bets, the soccer Draw synth) is not a two-team layout
  // and must never register or inherit a card — the Draw stays mouse-only.
  const lt2GameCardId =
    lt2CardScope && singleSide == null && !continuation
      ? lt2MoneylineGameCardId(lt2CardScope.keyPrefix, game.key)
      : null;
  const lt2GameEnabled =
    lt2On &&
    lt2GameCardId != null &&
    !lt2GameFinal &&
    lt2AwayTicker != null &&
    lt2HomeTicker != null;
  // #1070: the ⌨ chip's (invisible) slot follows the lt2 grant SLOT, so a
  // restored grant confirming doesn't widen the Team cell under the pointer.
  // The chip itself still needs the confirmed grant (`lt2GameEnabled`).
  const lt2Slot = useGrantSlot('lt2Access');
  const lt2ChipSlotHeld =
    lt2GameEnabled ||
    (lt2Slot !== 'hidden' &&
      lt2HotkeysEnabled &&
      lt2CardScope != null &&
      lt2GameCardId != null &&
      !lt2GameFinal &&
      lt2AwayTicker != null &&
      lt2HomeTicker != null);
  const lt2GameTargets = () => {
    if (!lt2CardScope) return null;
    const hidden = (status: ReturnType<typeof useFeeDisplayStatus>) =>
      lt2FeeAdjusted && lt2HideUnknownFees && feeDisplayCanHide(status);
    return lt2MoneylineGameTargets({
      scope: lt2CardScope,
      game,
      away: {
        book: game.kalshi?.away ?? null,
        label: game.awayCode || game.awayName,
        priceHidden: hidden(lt2AwayFeeStatus),
      },
      home: {
        book: game.kalshi?.home ?? null,
        label: game.homeCode || game.homeName,
        priceHidden: hidden(lt2HomeFeeStatus),
      },
      priceMode,
    });
  };
  const lt2GameFocused = useLt2Card(
    lt2GameEnabled ? lt2GameCardId : null,
    lt2GameEnabled,
    lt2GameTargets,
  );

  const renderTeamRow = (teamSide: TeamSide, isFirstRow: boolean) => {
    const teamName = teamSide === 'away' ? game.awayName : game.homeName;
    const kBook = teamSide === 'away' ? game.kalshi?.away : game.kalshi?.home;
    const pBook = teamSide === 'away' ? game.polymarket?.away : game.polymarket?.home;

    // Pill prices — what number each Y/N pill displays. Driven by the toolbar
    // PriceMode (maker = best bid, taker = best ask, market = last trade).
    const yesAskCents = pillPriceCents(kBook, 'yes', priceMode);
    const noAskCents = pillPriceCents(kBook, 'no', priceMode);
    const polyYesAskCents = pillPriceCents(pBook, 'yes', priceMode);
    const polyNoAskCents = pillPriceCents(pBook, 'no', priceMode);

    // #167 — crossed-book tripwire, one read per venue book. The condition is
    // a property of the BOOK, so the same read qualifies both of that venue's
    // pills; it is symmetric across sides, so there is nothing per-side to
    // compute. Derived per render and never stored, so it clears by itself when
    // the book heals or ownership falls back to a sane source.
    const kCrossed = crossedBookRead(kBook, 'kalshi');
    const pCrossed = crossedBookRead(pBook, 'polymarket');

    // Game-state-derived row treatment. Final rows get the winner ✓ in place
    // of price pills + dimmed opacity (prices are guaranteed 100¢/0¢, so the
    // pills are noise) and suppress velocity alerts. Still sourced from the
    // row heuristic, not `chipState` — see the note above; #121 deliberately
    // left row treatment alone. The chips themselves read `chipState`.
    const isFinal = game.gameState === 'final';
    const isWinner = isFinal && game.finalWinnerSide === teamSide;
    // #1070/#1107: only rows that can still trade hold the one-click odds
    // width, on BOTH venues' pills — a Poly side dropping to "—" would
    // otherwise narrow its auto-layout column and slide every Kalshi pill.
    const holdOddsWidth = oneClickSlot && !isFinal && !settled;
    // #1108: final and settled rows hold the slot too — dropping it when a
    // game ends would move a held position's rings outside the budget and
    // widen the column at the final whistle.
    const holdRingSlot = ringSlot === 'label' && oneClickSlot;
    // Trailing (Game Detail) holds for every trader: rings appear for anyone
    // with a fill, and a slot keyed to one-click would be released at a
    // mid-session revocation, sliding the prices 46px at 1470.
    const holdTrailingRingSlot = ringSlot === 'trailing';
    const posCell = (
      <PosCell
        ticker={teamSide === 'away' ? game.kalshi?.away.marketTicker : game.kalshi?.home.marketTicker}
        book={teamSide === 'away' ? game.kalshi?.away ?? null : game.kalshi?.home ?? null}
        positionByTicker={positionByTicker}
        ordersByTicker={ordersByTicker}
      />
    );
    const kDepthVelocity = depthVelocityFor(kBook, marketSide);
    const pDepthVelocity = depthVelocityFor(pBook, marketSide);
    const kDepthDelta = kDepthVelocity.kind === 'ready' ? kDepthVelocity.delta : null;
    const pDepthDelta = pDepthVelocity.kind === 'ready' ? pDepthVelocity.delta : null;
    const velocityAlert =
      !isFinal && velocityMagnitude(kDepthDelta, pDepthDelta) >= VELOCITY_ALERT_DELTA;

    // #179 — position-independent dashboard cue. Derived, never stored. The
    // book's timestamp only moves with an ingest that also rebuilds this row's
    // book (so it arrives through `game`); the AGE half is time, and `rowNow`
    // above re-renders the row each second so a stale poly book withdraws the
    // cue within a second of the bar. FINAL rows never flag: resolved 100¢/0¢
    // books are noise.
    const divergence = showRowDivergence && !isFinal
      ? rowDivergence(kBook, pBook, (ticker) =>
          useMarketStore.getState().getOrderbookAgeMs(ticker))
      : null;

    // Thick top border between games (not on first game, not when this game is expanded — ladder gets it)
    // groupSelected counts: a 3-way group with ANY outcome expanded renders
    // every row in the group as selected (tint + blue rails), so the box
    // wraps the whole market.
    const isGameSelected = expandedTeamSide !== null || groupSelected;
    const borderClass = isFirstRow && gameIndex > 0 && !isGameSelected ? 'border-t-2 border-border' : '';
    // Blue selection box rails on the team rows (the ladder rows carry their
    // own). Top: single-side rows (period spread/total) have no ladder above
    // them, so the team row IS the box top; continuation rows (Draw/Tie)
    // never open the box — the group's top rail lives on the pair above; a
    // collapsed pair whose Draw/Tie below is the expanded outcome has no
    // ladder above it, so the away row is the box top. Bottom: a collapsed
    // continuation row (its ladder only renders when it is itself the
    // expanded outcome) is the group's last row — it closes the box.
    const railTop = (!!singleSide && !continuation) || (!singleSide && expandedTeamSide === null && isFirstRow);
    const railBottom = !!continuation && expandedTeamSide === null;
    // #1112: under holdColumnsOnExpand a selected game keeps its 2px divider
    // slot. Without the switch the slot goes because the blue border-t-2
    // takes its place; the held box draws its rails as shadows, so losing
    // the slot would pull the row up 2px. The slot turns blue where it is the
    // box's top edge (the shadow then skips its own top rail), grey otherwise.
    const heldDividerSlot = holdColumnsOnExpand && isFirstRow && gameIndex > 0 && isGameSelected;

    // Both rows of the selected game get a darker tint to frame the paired
    // ladders around them; falls back to standard zebra striping when no game is
    // selected. Theme-split via Tailwind utilities — light mode wants a
    // much lighter overlay than the dark-mode value, otherwise rows go
    // near-black against a white page.
    const rowStyle =
      !isGameSelected && gameIndex % 2 === 1
        ? { backgroundColor: 'hsl(var(--muted) / var(--zebra-opacity))' }
        : undefined;

    const isThisRowExpanded = expandedTeamSide === teamSide;

    // The hotkey fence reads these marks off the DOM (see Lt2HotkeyListener).
    // GAME-level focus paints BOTH team rows; each row carries its own key's
    // price mark (away = "a", home = "d") so the listener proves the pressed
    // key's team row is visible — A can never certify the top team's price
    // by finding the bottom team's row.
    const lt2RowFocused = lt2GameFocused;
    const lt2RowPriceKey = teamSide === 'away' ? 'a' : 'd';
    const lt2FocusMark = lt2RowFocused ? (lt2GameCardId ?? undefined) : undefined;
    // The keyed price mark stays through expansion: the team row keeps
    // rendering its pills when its ladder opens beneath it, so the key's
    // action remains exactly the displayed top-of-book NO.
    const lt2PriceMark = lt2RowFocused ? lt2RowPriceKey : undefined;

    // ── Consensus odds + score (shared by the Score and Odds columns) ──
    // Slot swap + draw handling live in the hoisted fairFor (shared with
    // the ladder's fair-value dot).
    const consensus = liveConsensus;
    const { cents: teamFairCents, tenths: teamFairTenths } = fairFor(teamSide);
    const pctValue = teamFairCents != null ? Math.round(teamFairCents) : null;
    // Displayed odds carry the tenths digit when hydra sends the sub-cent
    // feed (the GameDetailPanel U-row precedent), integer cents otherwise.
    const displayCents = teamFairTenths != null ? teamFairTenths / 10 : teamFairCents;
    const pct =
      teamFairTenths != null
        ? `${(teamFairTenths / 10).toFixed(1)}%`
        : pctValue != null
          ? `${pctValue}%`
          : null;
    // American-odds translation of the same fair prob, shown under the %.
    // Display-only (fair prob, not a tradable quote); boundary probs render
    // americanOdds' "—" sentinel, which the cell suppresses.
    const pctAmerican =
      displayCents != null && displayCents > 0 && displayCents < 100
        ? americanOdds(displayCents)
        : null;
    // Rank-based tone across the game's outcome set (Dan, Jun 11): the old
    // fixed thresholds (>=51 green / ==50 blue) stop making sense when a
    // soccer 3-way splits 100 three ways and 48 can lead. Highest = green,
    // lowest = blue (title convention), middle/ties = grey — computed over
    // rounded DISPLAY values (tenths when shown, else integers) so
    // equal-looking numbers color as ties.
    const displayVal = (cents: number, tenths?: number | null) =>
      tenths != null ? Math.round(tenths) / 10 : Math.round(cents);
    const consensusValues =
      consensus == null
        ? []
        : [
            displayVal(consensus.awayFairProbCents, consensus.awayFairProbTenths),
            displayVal(consensus.homeFairProbCents, consensus.homeFairProbTenths),
            ...(consensus.drawFairProbCents != null
              ? [displayVal(consensus.drawFairProbCents, consensus.drawFairProbTenths)]
              : []),
          ];
    const rowDisplayValue =
      teamFairTenths != null ? Math.round(teamFairTenths) / 10 : pctValue;
    const pctTone =
      rowDisplayValue == null
        ? 'text-muted-foreground'
        : consensusRankTone(rowDisplayValue, consensusValues);
    const teamScore =
      oddsOutcome === 'draw' ? null : teamSide === 'away' ? awayScoreDisplay : homeScoreDisplay;
    // Scoreless statusId-3 records must NOT blank the Odds cell — on the
    // 2026-06-12 live soccer game the consensus rendered for a moment on
    // refresh, then vanished when the gameStates poll delivered a garbage
    // FINAL. isConfirmedFinal requires the score.
    const isFinalState = isConfirmedFinal(liveGameState);
    // Kalshi-lag value signal (lib/divergence/kalshiLagValue) — Poly +
    // Unabated moved, Kalshi's executable ask hasn't repriced yet (≥2¢ below
    // both, fresh references only). Always the taker read
    // (bestAskCentsForBook) regardless of the toolbar price mode: a bid
    // resting 2¢ under fair is normal book shape; an ASK 2¢ under both
    // references is the actionable lag. While true, the pill's price font
    // pulses gold (animate-value-gold-text-pulse). The getOrderbookAgeMs read
    // is imperative; its data half rides the book prop and its time half rides
    // `rowNow` (see the row clock above), so it cannot go stale on a row that
    // no longer re-renders with the whole dashboard.
    const kalshiValueSignal = (side: MarketSide): boolean => {
      if (isFinal || settled || isFinalState || displayCents == null) return false;
      const fairSide = side === 'yes' ? displayCents : mirrorCents(displayCents);
      return kalshiLagValueActive({
        kalshiAskCents: bestAskCentsForBook(kBook, side),
        polyAskCents: bestAskCentsForBook(pBook, side),
        fairCents: fairSide,
        polyBookAgeMs: pBook
          ? useMarketStore.getState().getOrderbookAgeMs(pBook.marketTicker)
          : null,
        consensusAgeMs,
      });
    };
    // Brightness conveys confidence: a 50% coin-flip is dimmest, a near-
    // certain call (toward 0/100%) brightest; floored at 0.4 for legibility.
    const confidenceOpacity =
      pctValue == null ? 1 : 0.4 + 0.6 * (Math.abs(pctValue - 50) / 50);
    const consensusAgeMs = consensus != null ? rowNow - consensus.lastUpdateMs : null;
    const consensusAgeChip =
      consensusAgeMs != null && consensusAgeMs >= 300_000
        ? getTimeAgo(new Date(rowNow - consensusAgeMs))
        : null;
    // Sharp-anchor state for behavior C. true/false render distinctly;
    // null/undefined (pre-rollout hydra) renders the plain legacy look.
    // false is the NORMAL field-only state (src 7 is sparse), not an error.
    const sharpAnchored = consensus?.sharpBookUsed ?? null;
    const consensusTitle = consensus
      ? `Unabated consensus · ${consensus.contributingBooks} book${consensus.contributingBooks === 1 ? '' : 's'} · ${consensusAgeChip ?? 'just updated'}${
          sharpAnchored == null ? '' : sharpAnchored ? ' · Pinnacle-anchored (sharp)' : ' · field books only'
        }`
      : undefined;
    // Trend glow for THIS row's displayed side. The trend state tracks
    // Unabated's own away/home slots, so apply the same swap the fair-cents
    // read above does; the Draw row reads the side-less draw track.
    const glowSide: 'away' | 'home' | 'draw' =
      oddsOutcome === 'draw'
        ? 'draw'
        : (teamSide === 'away') !== consensusSwapped
          ? 'away'
          : 'home';
    const sideGlow = consensusGlow != null ? consensusGlow[glowSide] : null;

    return (
      <tr
        key={`${game.key}-${teamSide}`}
        data-view-anchor={JSON.stringify([game.key, teamSide])}
        ref={isFirstRow ? groupTopRef : undefined}
        className={cn(
          // Hover is 15% black on dark so it's clear the row is a click
          // target (bg-accent/50 blended into the zebra alternation). On
          // light it is 6%: under the 8% ladder and open rows, so a hovered
          // row never reads as more selected than the open one (see
          // docs/261016DecisionMoneylineLightLooks.md).
          // The `!` important prefix is load-bearing: alternate rows set
          // `background-color` via an inline `style`,
          // and inline styles outrank plain Tailwind utility classes —
          // including the hover variant. Forcing !important on hover
          // makes the highlight win on zebra rows too.
          'hover:!bg-black/15 light:hover:!bg-black/[0.06] cursor-pointer transition-colors',
          borderClass,
          // A shade lighter than the ladder rows (15% black, 8% on light) so
          // the matchup rows read as the focal point of the selected game.
          // No `dark:` variants: the app toggles `.light`, never `.dark`, so
          // they never apply (tailwind.config.js darkMode).
          isGameSelected && 'bg-black/5',
          // Blue selection box — side rails on the team rows; top/bottom
          // rails live on the first/last ladder rows that bracket them.
          // Suppressed where the caller passes hideSelectionBox (Game
          // Detail's moneyline section, where it's noise).
          heldDividerSlot && (!hideSelectionBox && railTop ? 'border-t-2 border-t-blue-500' : 'border-t-2 border-border'),
          isGameSelected && !hideSelectionBox && selectionRails(railTop && !heldDividerSlot, railBottom, holdColumnsOnExpand),
          velocityAlert && 'animate-velocity-alert-row',
          (isFinal || settled) && 'opacity-60',
          // #647 persistent hotkey-focus tint — !important so the zebra
          // rowStyle's inline background can't hide it (spread/total rule).
          lt2RowFocused && '!bg-primary/15',
        )}
        style={rowStyle}
        // Expansion is a SEPARATE action from hotkey focus (Phase 3 Loom
        // correction). The row body click ONLY toggles expansion now: the
        // explicit ⌨ chip is the one gesture that aims A/D, so expanding an
        // unfocused row can never silently arm the hotkeys for it. A game
        // that is ALREADY aimed keeps its focus through expand/collapse,
        // and expansion does NOT pause the pair — both team rows keep
        // rendering their displayed prices above the inline ladder, so A/D
        // stay live throughout. (The old choreography acquired focus here,
        // which welded the two states: reaching collapsed+focused took the
        // undiscoverable click-twice dance the Loom shows Citadel failing.)
        onClick={() => {
          onToggleExpand(game.key, teamSide);
        }}
        data-lt2-focused={lt2FocusMark}
        data-lt2-price={lt2PriceMark}
        data-lt2-hotkey-control={lt2RowFocused || undefined}
      >
        {/* Date (first row only). Series-winner rows repurpose the column
            to a badge; FINAL rows show a FINAL chip in the same slot
            (guaranteed 5-min lifetime before hydra prunes); per-day rows
            show the date. Event start time renders below the date in a
            smaller line when known; LIVE rows get a green pulse dot
            beside it. Skipped entirely when `hideDateColumn` so the
            consuming table's column-count stays consistent. */}
        {!hideDateColumn && (
          <Td className="text-muted-foreground font-mono text-sm">
            {isFirstRow ? (
              <div className="flex items-start gap-1">
                {/* The mark controls share a slightly wider gap than the rest
                    of the cell: two 12px glyphs at gap-1 is the misclick
                    surface #651 hardened against. When the lens is off this
                    wrapper holds one child and lays out identically to before. */}
                <span className="flex items-center gap-1.5">
                  <SaveGameToggle game={game} />
                  {showMinimizeToggle && <MinimizeGameToggle game={game} />}
                </span>
                {(() => {
                  // Series rows carry a fabricated sort-to-bottom date
                  // ("9999-<round>-<yy>", marketStore sentinel) and are keyed
                  // by canonicalSeriesKey, so no /game/:date/:a/:b page can
                  // ever resolve for them — render the badge unlinked instead
                  // of a dead link ("Bad game URL" error, Brendan Jun 6).
                  // Soccer rows DO link: GameDetail resolves multi-outcome
                  // games via getMultiOutcomeEvents since 2026-06-11.
                  const isSeriesRow = game.series != null || game.date.startsWith('9999');
                  const body = (
                    <>
                      <span className="flex items-center gap-1">
                        {chipState === 'pre' && game.startTimeMs != null && (
                          <NearEventStartPulse startTimeMs={game.startTimeMs} />
                        )}
                        {game.series ? (
                          <span
                            className="text-foreground inline-block rounded border border-amber-500/60 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-400"
                            title={`Playoff series — ${roundLabel(game.sport, game.series.round)}, 20${game.series.year}`}
                          >
                            {roundLabel(game.sport, game.series.round)}
                          </span>
                        ) : chipState === 'final' ? (
                          <span
                            className="inline-block rounded border border-zinc-500/60 bg-zinc-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400"
                            title="Game finalized — resolved 100¢ / 0¢"
                          >
                            FINAL
                          </span>
                        ) : chipState === 'live' ? (
                          <LiveBadge />
                        ) : chipState === 'unknown' ? (
                          <UnknownStatusBadge />
                        ) : (
                          formatGameDateLabel(game.date)
                        )}
                      </span>
                      {/* Live period status (e.g. "Bot 3rd"). The scheduled start
                          time + countdown moved to the Score column. */}
                      {chipState === 'live' && liveStatusText && (
                        <span className="text-foreground font-mono text-[16px] font-bold">
                          {liveStatusText}
                        </span>
                      )}
                      {/* Game volume under the date — most-repeated trader ask
                          (Prof Jun 2, Joshua's Jun 8 call notes). Kalshi away +
                          home are distinct tickers (sum); Poly away/home share
                          one slug-level volume (count once).

                          #754 — the sum itself is `measuredEventVolume`, the
                          same rule the discovery modes RANK whole games by, so
                          the figure on a row and the order the rows arrive in
                          can never disagree. It counts measured traded volume
                          only: a book whose `volume` is the resting-depth
                          fallback contributes nothing, because the Vol cell one
                          column over already marks that book "~ resting depth,
                          NOT executed volume" while this line's own tooltip
                          calls itself traded volume. Counting depth here was
                          that contradiction, inside one row. */}
                      {(() => {
                        const gameVol = measuredEventVolume(game);
                        const shown = gameVol.value != null && Math.round(gameVol.value) >= 1;
                        // Always render the line box so the row height doesn't
                        // jump when the first contract trades (>=1 boundary).
                        return (
                          <span
                            className="text-muted-foreground font-mono text-[10px]"
                            title={shown
                              ? (gameVol.coverage === 'partial'
                                ? 'Traded volume since market open, from the markets that reported it. Another market in this event reported no traded volume — Kalshi lists each side separately — so its resting depth is NOT included and this total covers only part of the event.'
                                : 'Combined lifetime traded volume since market open (Kalshi + Polymarket), not rolling 24-hour volume')
                              : undefined}
                          >
                            {shown
                              /* The same "~" the Vol cell uses for a figure that
                                 is not the whole of what it names. */
                              ? `${gameVol.coverage === 'partial' ? '~' : ''}${formatVolume(gameVol.value!)} total vol`
                              : '\u00A0'}
                          </span>
                        );
                    })()}
                  </>
                );
                // No link for a series row, or for an unstamped tennis row
                // that cannot name its tour (gameDetailLinkForRow, #1284).
                const detailHref = isSeriesRow ? null : gameDetailLinkForRow(game);
                return detailHref == null ? (
                  <span className="flex flex-col leading-tight">{body}</span>
                ) : (
                  <Link
                    to={detailHref}

                    onClick={(e) => e.stopPropagation()}
                    className="hover:text-foreground hover:underline focus:outline-none focus-visible:underline flex flex-col leading-tight"
                    title="Open game detail"
                  >
                    {body}
                  </Link>
                );
                })()}
              </div>
            ) : ''}
          </Td>
        )}

        {/* Team — name plus inline per-user POS chips (contracts/open
            orders). The team's consensus odds land in the Odds column to
            the right. */}
        <Td className="font-medium">
          <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            {/* max-w + truncate keeps long competitor names (tennis player
                full names from yes_sub_title, e.g. "Felix Auger-Aliassime")
                from blowing out the column — the widest Team cell sizes the
                whole column under auto table-layout. Full name on hover via
                title. */}
            {/* The identity group keeps the SAME width budget whatever the
                hotkey state: the ⌨ chip's slot and the aim badge both render
                INSIDE the label's max-width allocation (a long name truncates
                to make room) instead of widening the table — the capture
                harness caught the additive version pushing the moneyline
                board past the 1470/1920 density budgets. #1070: the chip's
                slot is held on hotkey-eligible top rows even while disarmed,
                so arming never changes the cell's width. #1108: on ring-slot
                rows the budget is the group's exact width and the rings sit
                inside it too — a ring appearing only shortens the name, and
                with no ring the name has the whole budget it has today. */}
            <span
              style={holdRingSlot ? { width: labelWidthPx } : { maxWidth: labelWidthPx }}
              className={cn('flex items-center gap-1', wrapLabel ? 'w-[200px] min-w-[200px] shrink-0' : holdRingSlot ? 'shrink-0' : 'min-w-0')}
            >
              {isThisRowExpanded ? (
                <span
                  title={teamName}
                  className={cn(
                    holdColumnsOnExpand
                      // #1112: a ring (no layout) instead of a border, and
                      // negative margins cancelling the padding, so the chip
                      // takes the plain label's exact box — no rewrap, no
                      // taller row, no pills sliding down on expand.
                      ? 'inline-block min-w-0 rounded-md -mx-2 -my-0.5 px-2 py-0.5 ring-1 ring-inset'
                      : 'inline-block min-w-0 rounded-md border px-2 py-0.5',
                    wrapLabel ? 'whitespace-normal break-words leading-snug' : 'truncate',
                    holdColumnsOnExpand
                      ? marketSide === 'yes'
                        ? 'ring-positive bg-positive/10 text-positive'
                        : 'ring-negative bg-negative/10 text-negative'
                      : marketSide === 'yes'
                        ? 'border-positive bg-positive/10 text-positive'
                        : 'border-negative bg-negative/10 text-negative',
                  )}
                >
                  {teamName}
                </span>
              ) : (
                <span title={teamName} className={cn('min-w-0', wrapLabel ? 'whitespace-normal break-words leading-snug' : 'truncate')}>
                  {teamName}
                </span>
              )}
              {/* The pre-focus half of the gesture: explicit, visible, and
                  it does NOT expand. One chip per GAME (top team row) —
                  aiming targets the game pair, never one row. Armed-gated —
                  armed is when A/D matter. #1070: its slot is held while
                  disarmed too, so arming never widens this cell. */}
              {teamSide === 'away' &&
                !lt2GameFocused &&
                lt2ChipSlotHeld &&
                lt2GameCardId && (
                  <Lt2FocusChipSlot
                    show={lt2Armed && lt2GameEnabled}
                    onFocus={() => useLt2FocusStore.getState().setFocus(lt2GameCardId)}
                  />
                )}
              {/* Game-level hotkey badge — the visible claim that A posts NO on
                  the top team and D posts NO on the bottom team, at their
                  displayed prices. Rendered once, on the top row, inside the
                  name's width budget (#1070): a long name truncates to make
                  room rather than the badge widening the column. */}
              {teamSide === 'away' && lt2GameFocused && lt2GameCardId && (
                <Lt2FocusBadge
                  sides={`A posts NO on ${game.awayCode || game.awayName} (top), D posts NO on ${game.homeCode || game.homeName} (bottom)`}
                  onClear={() => useLt2FocusStore.getState().clearFocusIf(lt2GameCardId)}
                />
              )}
              {holdRingSlot && posCell}
            </span>
            {/* SETTLED chip — first row only (singleSide rows are their own
                first row, so period bets get exactly one). Same visual
                vocabulary as the Date column's FINAL chip, which these
                surfaces hide. */}
            {settled && isFirstRow && (
              <span
                className="inline-block shrink-0 rounded border border-zinc-500/60 bg-zinc-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400"
                title="Market resolved — prices are settlement values, not tradable quotes"
              >
                SETTLED
              </span>
            )}
            {isFirstRow && <StaleMarketBadge stale={rowHasStaleMarket} />}
            {holdTrailingRingSlot ? (
              <span data-ring-slot="trailing" className="inline-flex w-11 shrink-0">
                {posCell}
              </span>
            ) : (
              !holdRingSlot && posCell
            )}
            <BotPresenceChip
              bots={botPresenceByTicker?.get(
                (teamSide === 'away'
                  ? game.kalshi?.away.marketTicker
                  : game.kalshi?.home.marketTicker) ?? '',
              )}
            />
          </div>
          {showInjuryEntries && (
            // #1389 — a fixed-width clipping box: the line's width never depends
            // on the report (no column reflow), and an overrun of the label
            // budget is a clipped-text finding in the layout harness, not a
            // silent spill. The worst case fits (see InjuryEntries).
            <div style={{ width: labelWidthPx }} className="min-w-0 overflow-hidden">
              <BoardInjuryEntry
                gameKey={game.key}
                teamCode={teamSide === 'away' ? game.awayCode : game.homeCode}
                showAge={teamSide === 'away'}
                expanded={injuryReportOpen}
                controls={injuryReportId}
                onToggle={(origin) => onToggleInjuryReport?.(game.key, origin)}
              />
            </div>
          )}
          {labelInjury && isFirstRow && <div style={{ width: labelWidthPx }} className="min-w-0 overflow-hidden">{labelInjury}</div>}
          {/* Line strip (compact Player Props). Under the label, not beside
              it: beside would push the analytics columns right on every
              surface that passes nothing. */}
          {labelAccessory && isFirstRow && <div>{labelAccessory}</div>}
          </div>
        </Td>

        {/* Score — kickoff time + countdown until the game starts, then the
            live/final score. Score is per-team (each row shows its side); the
            pre-game time line is per-game, so it renders on the first row
            only. */}
        {!hideScoreColumn && (
          <Td className="whitespace-nowrap text-center font-mono">
            {showLiveScore && teamScore != null ? (
              <span className="text-foreground text-[16px] font-bold">{teamScore}</span>
            ) : chipState === 'live' || chipState === 'unknown' ? (
              // Under way (or possibly under way) with no score feed — tennis
              // and other un-scored sports: an explicit dash, never the stale
              // start time (Joshua, Jun 6).
              //
              // Gated on chipState, not the row heuristic, so this agrees with
              // the Date cell (#121). A POSTPONED game — canonical state says
              // scheduled while the clock has passed — falls through to the
              // branch below and keeps showing its scheduled time. Under the
              // row heuristic it read as live and lost the time to this dash,
              // which contradicted the Date cell's pregame chip.
              <span className="text-muted-foreground/40">—</span>
            ) : chipState !== 'final' && isFirstRow && game.timePt ? (
              <span className="flex flex-col items-center leading-tight">
                <span className="text-foreground text-[16px] font-bold">{game.timePt}</span>
                {game.startTimeMs != null && (
                  <GameCountdown startTimeMs={game.startTimeMs} className="text-sm" />
                )}
              </span>
            ) : (
              ''
            )}
          </Td>
        )}

        {/* Odds — Unabated consensus win probability for this team, in its
            own column (split out of the old Score/Odds cell), to the tenth
            when hydra's sub-cent feed is live, with the American-odds
            translation beneath. Brightness conveys confidence: a 50/50
            coin-flip is dimmest, a near-certain call (toward 0/100%)
            brightest. Blank once the game is final. */}
        <Td className="whitespace-nowrap text-center font-mono tabular-nums">
            {hideOddsColumn ? (
              ''
            ) : pct != null && pctValue != null && !isFinalState ? (
              <OddsPctText
                pctValue={pctValue}
                text={pct}
                american={pctAmerican}
                toneClass={pctTone}
                sizeClass="text-[17px]"
                opacity={confidenceOpacity}
                title={consensusTitle}
                glow={sideGlow}
                sharp={sharpAnchored}
              />
            ) : (
              <span className="text-muted-foreground/40">—</span>
            )}
        </Td>

        {/* OP/PNCL — OddsPapi de-vigged fair values (consensus /
            pinnacle at equal weight, Dan 2026-08-03), immediately right
            of Odds, styled with the U column's own visual language (rank
            tone within the game's OP values, confidence opacity, integer
            halos, pinnacle update glow). Still excluded from trend
            arrows, sharp star, fair-dot anchoring, divergence signals,
            and any cross-source ranking against Unabated. Blank on
            final, like Odds.
            Orientation: team-oriented buckets (moneyline, spread) align to
            THIS row's home (rule 4); positional O/U buckets (total, team
            totals, TD/FG totals) never swap — Over rides the away slot
            from the slice's own orientation (opRowHomeCodeFor). */}
        {!hideOpColumn && (
          <Td className="whitespace-nowrap text-center font-mono tabular-nums">
            {!isFinal && !isFinalState ? (
              <OpOddsText
                slice={opSlice}
                side={oddsOutcome === 'draw' ? 'draw' : teamSide}
                rowHomeCode={opRowHomeCodeFor(opSlice, game.homeCode)}
                glow={opGlow}
                lastAnchor={opLastAnchor}
              />
            ) : (
              <span className="text-muted-foreground/40">—</span>
            )}
          </Td>
        )}

        {/* Kalshi Yes/No pills — replaced by winner ✓ / blank on FINAL rows
            since 100¢/0¢ pills are noise once the market resolves. */}
        <Td className={cn('whitespace-nowrap text-center', holdOddsWidth && 'px-1.5')}>
          {isFinal ? (
            <div className="flex items-center justify-center text-sm">
              {isWinner ? (
                <span className="text-positive font-semibold" title="Resolved YES (winner)">✓</span>
              ) : (
                <span className="text-muted-foreground/40">—</span>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center gap-1.5">
              <PricePill
                label="Y"
                interaction={onSummaryYesSelect ? 'view' : 'trade'}
                cents={yesAskCents}
                holdOddsWidth={holdOddsWidth}
                side="yes"
                sideTint={sideTint}
                activeMarketSide={marketSide}
                priceMode={priceMode}
                disabled={kBook == null || kBook.freshness === 'stale'}
                disabledReason={kBook?.freshness === 'stale' ? STALE_PILL_REASON : undefined}
                testId={`pill-kalshi-${teamSide}-Y`}
                ticker={kBook?.marketTicker ?? null}
                makerRebatePct={makerRebateFraction}
                valueSignal={kalshiValueSignal('yes')}
                crossedBook={kCrossed}
                onClick={(e) => {
                  e.stopPropagation();
                  if (yesAskCents == null) return;
                  if (onSummaryYesSelect) {
                    onSummaryYesSelect(game.key, teamSide, 'kalshi');
                    return;
                  }
                  onTradeClick?.(game.key, teamSide, 'kalshi', {
                    priceCents: yesAskCents,
                    initialOrderSide: 'yes',
                    postOnly: false,
                  });
                }}
              />
              <PricePill
                label="N"
                interaction={onSummaryNoSelect ? 'view' : 'trade'}
                cents={noAskCents}
                holdOddsWidth={holdOddsWidth}
                side="no"
                sideTint={sideTint}
                activeMarketSide={marketSide}
                priceMode={priceMode}
                disabled={kBook == null || kBook.freshness === 'stale'}
                disabledReason={kBook?.freshness === 'stale' ? STALE_PILL_REASON : undefined}
                testId={`pill-kalshi-${teamSide}-N`}
                ticker={kBook?.marketTicker ?? null}
                makerRebatePct={makerRebateFraction}
                valueSignal={kalshiValueSignal('no')}
                crossedBook={kCrossed}
                onClick={(e) => {
                  e.stopPropagation();
                  if (noAskCents == null) return;
                  if (onSummaryNoSelect) {
                    onSummaryNoSelect(game.key, teamSide, 'kalshi');
                    return;
                  }
                  onTradeClick?.(game.key, teamSide, 'kalshi', {
                    priceCents: noAskCents,
                    initialOrderSide: 'no',
                    postOnly: false,
                  });
                }}
              />
            </div>
          )}
        </Td>

        {/* K Volume. Fixed-ch span: live digit-count changes must not alter
            the column's content width or the auto table layout re-flows every
            column on each tick (the page-jitter bug). Same treatment on every
            live numeric cell in this row. */}
        <Td className="text-muted-foreground text-right font-mono text-sm">
          {isFinal ? '' : <AggregateVolumeCell book={kBook} />}
        </Td>

        {/* K Liquidity — total $ in book; ladder pills scale against this */}
        <Td className="text-muted-foreground text-right font-mono text-sm">
          {isFinal ? '' : (
            <KalshiLiquidityCell book={kBook} />
          )}
        </Td>

        {/* K Queue — contracts resting at the best bid on the active marketSide.
            Color follows the same absolute thresholds the trade-view cards use
            (queueTone in orderCardModel.ts): <50k green, 50-300k yellow, ≥300k
            red. Returns null (→ '—') when the book has no real bid on that
            side. FINAL rows blank since the resolved book carries no signal. */}
        {(() => {
          const kQueue = bestBidDepthForSide(bookForDisplayOnly(kBook), marketSide);
          return (
            <Td className={cn('text-right font-mono text-sm', queueTone(kQueue, null))}>
              {isFinal ? '' : (
                <span className="inline-block w-[6ch] text-right">
                  {kQueue == null ? '—' : formatVolume(kQueue)}
                </span>
              )}
            </Td>
          );
        })()}

        {/* K Δ/m — best-bid depth rate of change over the trailing 1 minute on
            the active marketSide. Pair-reads with the P Δ/m cell to the right
            of the Poly column for cross-exchange sweep detection. Empty until
            ~1m of samples accumulates. */}
        <Td className={cn('text-right font-mono text-sm', depthDeltaTone(kDepthDelta))}>
          {isFinal ? '' : (
            <span className="inline-block w-[7ch] text-right">
              <DepthVelocityValue display={kDepthVelocity} />
            </span>
          )}
        </Td>

        {/* #1112: a prop tab with no Polymarket market drops these four. */}
        {!hidePolyColumns && (
          <>
            {/* Polymarket Yes/No pills — same pattern as Kalshi, FINAL same swap */}
            <Td className={cn('whitespace-nowrap text-center', holdOddsWidth && 'px-1.5')}>
              {isFinal ? (
                <div className="flex items-center justify-center text-sm">
                  {isWinner ? (
                    <span className="text-positive font-semibold" title="Resolved YES (winner)">✓</span>
                  ) : (
                    <span className="text-muted-foreground/40">—</span>
                  )}
                </div>
              ) : (
                <div className="relative flex items-center justify-center gap-1.5">
                  <PricePill
                    label="Y"
                    interaction={onSummaryYesSelect ? 'view' : 'trade'}
                    cents={polyYesAskCents}
                    holdOddsWidth={holdOddsWidth}
                    side="yes"
                    sideTint={sideTint}
                    activeMarketSide={marketSide}
                    priceMode={priceMode}
                    disabled={pBook == null || pBook.freshness === 'stale'}
                    disabledReason={pBook?.freshness === 'stale' ? STALE_PILL_REASON : undefined}
                    testId={`pill-polymarket-${teamSide}-Y`}
                    ticker={pBook?.marketTicker ?? null}
                    makerRebatePct={makerRebateFraction}
                    crossedBook={pCrossed}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onSummaryYesSelect) {
                        onSummaryYesSelect(game.key, teamSide, 'polymarket');
                        return;
                      }
                      onTradeClick?.(game.key, teamSide, 'polymarket', {
                        priceCents: polyYesAskCents ?? undefined,
                        initialOrderSide: 'yes',
                        postOnly: false,
                      });
                    }}
                  />
                  <PricePill
                    label="N"
                    interaction={onSummaryNoSelect ? 'view' : 'trade'}
                    cents={polyNoAskCents}
                    holdOddsWidth={holdOddsWidth}
                    side="no"
                    sideTint={sideTint}
                    activeMarketSide={marketSide}
                    priceMode={priceMode}
                    disabled={pBook == null || pBook.freshness === 'stale'}
                    disabledReason={pBook?.freshness === 'stale' ? STALE_PILL_REASON : undefined}
                    testId={`pill-polymarket-${teamSide}-N`}
                    ticker={pBook?.marketTicker ?? null}
                    makerRebatePct={makerRebateFraction}
                    crossedBook={pCrossed}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onSummaryNoSelect) {
                        onSummaryNoSelect(game.key, teamSide, 'polymarket');
                        return;
                      }
                      onTradeClick?.(game.key, teamSide, 'polymarket', {
                        priceCents: polyNoAskCents ?? undefined,
                        initialOrderSide: 'no',
                        postOnly: false,
                      });
                    }}
                  />
                  {/* #179: absolute, zero-flow-width overlay. These tables use auto
                      layout, so anything that changes a cell's content width on a
                      live tick reflows every column (the page-jitter rule above the
                      K Volume cell) — out of flow, the marker costs zero width.
                      The wrapper stops row expansion; DivergenceBadge itself keeps
                      pointer events so its existing title stays hoverable.

                      Compact DOT, not the labelled pill: the pill is taller than
                      this cell, so the only placement that cleared the poly N pill
                      put it above the cell entirely, visibly crossing into the row
                      above. The dot seats INSIDE the row — vertically centred on
                      the pills, parked in the cell's own px-3 right gutter so it
                      overlaps neither pill nor the next column. Same violet, same
                      tooltip; the signed magnitude the pill printed is in the
                      tooltip text already. Vertical offsets are therefore banned
                      here — a test pins that. */}
                  {isDivergenceActive(divergence) && (
                    <span
                      data-testid="row-divergence-overlay"
                      className="absolute -right-2 top-1/2 z-10 -translate-y-1/2"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <DivergenceBadge info={divergence} variant="dot" />
                    </span>
                  )}
                </div>
              )}
            </Td>

            {/* P Volume */}
            <Td className="text-muted-foreground text-right font-mono text-sm">
              {isFinal ? '' : <AggregateVolumeCell book={pBook} />}
            </Td>

            {/* P Liquidity — total $ in book; ladder pills scale against this */}
            <Td className="text-muted-foreground text-right font-mono text-sm">
              {isFinal ? '' : <AggregateLiquidityCell book={pBook} />}
            </Td>

            {/* P Δ/m — Polymarket sibling of K Δ/m. Same active-side semantic. */}
            <Td className={cn('text-right font-mono text-sm', depthDeltaTone(pDepthDelta))}>
              {isFinal ? '' : (
                <span className="inline-block w-[7ch] text-right">
                  <DepthVelocityValue display={pDepthVelocity} />
                </span>
              )}
            </Td>
          </>
        )}
        {showUsColumn && (usLine ? <UsLinePriceCell cell={usLine} priceMode={priceMode} marketSide={marketSide}
          onClick={(side, cents) => onUsTradeClick?.(game.key, teamSide, { initialOrderSide: side, priceCents: cents })} /> : (<>
          <UsBoardCell
            market={usMarket}
            plan={usVenuePlanForBoardKey(game.key)}
            now={rowNow}
            teamCode={(teamSide === 'away' ? game.awayCode : game.homeCode) ?? ''}
            teamSide={teamSide}
            priceMode={priceMode}
            activeMarketSide={marketSide}
            sideTint={sideTint}
            isFinal={isFinal}
            onClick={(side, boardSide, priceCents) =>
              onUsTradeClick?.(game.key, side, { priceCents: priceCents ?? undefined, initialOrderSide: boardSide })}
          />
          <UsMetricCells statistics={usMarket && 'statistics' in usMarket && usQuoteCurrent(usMarket, rowNow) ? usMarket.statistics : null} book={usMarket && usQuoteCurrent(usMarket, rowNow) ? usLadderBook : null} slug={usMarket?.marketSlug ?? null} now={rowNow} final={isFinal} />
        </>))}
        {showNovigColumn && (
          <NovigBoardCell
            quote={novigQuote}
            now={rowNow}
            teamSide={teamSide}
            priceMode={priceMode}
            isFinal={isFinal}
            onClick={(side, click) => onNovigTradeClick?.(game.key, side, click)}
          />
        )}
      </tr>
    );
  };

  // 3 ask rows + 3 bid rows for a single team. Whenever either team is
  // selected, render both team books around the paired matchup rows: away book
  // above the two names, home book below them. This keeps the existing
  // full-width ladder UX while making the opposite side visible immediately.
  // YES side: asks come from inverting NO bids (100 − no[i].price), bids come from yes[].
  // NO  side: asks come from inverting YES bids, bids come from no[].
  const renderLadderRows = (teamSide: TeamSide) => {
    const teamCode = teamSide === 'away' ? game.awayCode : game.homeCode;
    const teamName = teamSide === 'away' ? game.awayName : game.homeName;
    const kBook = teamSide === 'away' ? game.kalshi?.away : game.kalshi?.home;
    const pBook = teamSide === 'away' ? game.polymarket?.away : game.polymarket?.home;
    if (!kBook && !pBook && !(showUsColumn && (usMarket || usLine?.state !== 'absent' && usLine))) return null;
    if (dualOutcomeLadder) return (
      <tr key={`${game.key}-${teamSide}-dual`}><td colSpan={15 - (hideDateColumn ? 1 : 0) - (hideScoreColumn ? 1 : 0) - (hideOpColumn ? 1 : 0) - (hidePolyColumns ? 4 : 0) + (showUsColumn ? 4 : 0) + (showNovigColumn ? 1 : 0)}>
        <DualOutcomeBooks reference={lineReference} kalshi={kBook ?? null} polymarket={pBook ?? null}
          orders={kBook ? ordersByTicker.get(kBook.marketTicker) ?? [] : []}
          makerRebatePct={makerRebateFraction} onTrade={onTradeClick} gameKey={game.key} teamSide={teamSide}
          labels={dualOutcomeLabels?.(teamSide)}
          usBook={showUsColumn && usLine && teamSide === (singleSide ?? expandedTeamSide) ? <UsLineBooks cell={usLine}
            onTrade={(side, cents) => onUsTradeClick?.(game.key, teamSide, { initialOrderSide: side, priceCents: cents })} /> : undefined} />
      </td></tr>
    );

    const buildSide = (book: VenueBook | null | undefined) => {
      book = bookForDisplayOnly(book);
      if (!book) return { asks: [] as BookLevel[], bids: [] as BookLevel[] };
      const ownBids = marketSide === 'yes' ? book.yes : book.no;
      const oppositeBids = marketSide === 'yes' ? book.no : book.yes;
      const lastYes = book.lastPriceCents;
      const ownLast = lastYes == null ? null : marketSide === 'yes' ? lastYes : mirrorCents(lastYes);
      const oppositeLast = lastYes == null ? null : marketSide === 'yes' ? mirrorCents(lastYes) : lastYes;
      return {
        asks: oppositeBids
          .filter(bidIsReal(oppositeLast))
          .slice(0, ladderLevels)
          // Exact mirror — order chips exact-match against these level
          // prices; a float 100−p level misses its resting sub-cent order.
          .map((l) => ({ priceCents: mirrorCents(l.priceCents), size: l.size })),
        bids: ownBids.filter(bidIsReal(ownLast)).slice(0, ladderLevels),
      };
    };

    const k = buildSide(kBook);
    const p = buildSide(pBook);

    // Scale anchor for ladder liquidity pills: % of total venue book liquidity.
    const kMaxLiq = bookForDisplayOnly(kBook)?.liquidity || 1;
    const pMaxLiq = bookForDisplayOnly(pBook)?.liquidity || 1;

    // Per-price-level user-order chips for the Kalshi ladder. Split by action
    // because BUY orders rest on the bid stack and need to render alongside
    // bid rows, while SELL orders rest on the ask stack and need to render
    // alongside ask rows. Filtered to marketSide (the side currently expanded)
    // and sorted within each price level by qty desc.
    //
    // #676: that sort is a stable INPUT order, not the display order. Which
    // chips win the bounded inline slots is decided where the truncation
    // happens — `rankRestingOrders` inside `RestingOrderGroup`.
    const buildOrdersByCents = (action: 'buy' | 'sell') => {
      const map = new Map<number, BidRowOrder[]>();
      if (!kBook) return map;
      const orders = ordersByTicker.get(kBook.marketTicker) ?? [];
      for (const o of orders) {
        if (o.action !== action) continue;
        if (o.side !== marketSide) continue;
        const depth = queueDepth(o, kBook);
        const list = map.get(o.priceCents) ?? [];
        list.push({
          orderId: o.orderId,
          remainingCount: o.remainingCount,
          queueOrBetter: depth.atLevelOrBetter,
          tooltip: orderTooltip(o, kBook),
          // #676: the chip row ranks confirmed queue readings ahead of
          // remembered ones, so the provenance has to travel with the number.
          queueRetained: o.queueRetained,
        });
        map.set(o.priceCents, list);
      }
      for (const list of map.values()) {
        list.sort((a, b) => b.remainingCount - a.remainingCount);
      }
      return map;
    };
    const kBuyOrdersByCents = buildOrdersByCents('buy');
    const kSellOrdersByCents = buildOrdersByCents('sell');

    // Visual order: asks highest→lowest (worst at top, best at bottom near strip), then strip,
    // then bids highest→lowest (best at top near strip, worst at bottom).
    const kAsksVisual = [...k.asks].reverse();
    const kBidsVisual = k.bids;
    const pAsksVisual = [...p.asks].reverse();
    const pBidsVisual = p.bids;

    // hydra reports last trade as the YES last; invert for NO side.
    // Render a separate strip price per venue so K and P can diverge.
    const lastFor = (book: VenueBook | null | undefined): number | null => {
      const lastYes = book?.lastPriceCents ?? null;
      if (lastYes == null) return null;
      return marketSide === 'yes' ? lastYes : mirrorCents(lastYes);
    };
    const kLast = lastFor(kBook);
    const pLast = lastFor(pBook);
    const padTo = (rows: BookLevel[]): (BookLevel | null)[] => {
      const out: (BookLevel | null)[] = [...rows];
      while (out.length < ladderLevels) out.push(null);
      return out;
    };

    const kAsksPadded = padTo(kAsksVisual);
    const kBidsPadded = padTo(kBidsVisual);
    const pAsksPadded = padTo(pAsksVisual);
    const pBidsPadded = padTo(pBidsVisual);

    // US depth across price/quantity/dollars, in the same visual order and paired
    // by index like K/P. US lists one market per game, so each team's ladder
    // is its own outcome of that market (as the US pills read it).
    const usYesTeam = usMarket?.yesTeam ?? null;
    const usCurrent = usLadderBook !== null && usYesTeam !== null
      && usLadderBook.marketSlug === usMarket?.marketSlug && usBookCurrent(usLadderBook, rowNow);
    const us = usCurrent ? usBookForOutcome(usLadderBook, usOutcomeFor(teamCode ?? '', usYesTeam, marketSide)) : null;
    const usAsksPadded = padTo(us ? us.asks.slice(0, ladderLevels).reverse() : []);
    const usBidsPadded = padTo(us ? us.bids.slice(0, ladderLevels) : []);
    // The caption keeps missing/stale depth visible without creating a quote.
    const usUnavailable = usMarket && !usCurrent ? 'No current Polymarket US book' : undefined;
    const usBookStatus = usUnavailable
      ? usLadderBook !== null && usLadderBook.marketSlug === usMarket?.marketSlug && usLadderBook.state !== 'unavailable'
        ? 'US book stale' : 'US book unavailable'
      : undefined;

    // ── OddsPapi fair-value dot — one per venue price column ──────────
    // Marks where the fair prob truly sits on this venue's visual price
    // stack, tenth-accurate (interpolated between the straddling levels'
    // row centers). Per-venue because K and P ladder rows are index-paired,
    // not price-aligned — a single marker can't be positionally true for
    // both books; the pair doubles as a routing signal when the venues
    // disagree. Color = actionable edge vs this venue's best ask (±1¢ band,
    // consensusDotTone). Suppressed once the market resolves, mirroring the
    // Odds column.
    // Source: PINNACLE by default (Dan, 2026-09-10 — filled dot, "P"); the
    // OddsPapi consensus is the fallback only when Pinnacle has no fair on
    // the market (outline dot, "O"). Both ride the row's opSlice via
    // selectFairDotValue (the dot moved off the dying Unabated feed on
    // 2026-08-18), so a pinned bucket row (GameLines spread/total) prices
    // the dot against ITS market, and rows without OP coverage simply show
    // no dot.
    const opDotView = opSlice == null ? null : buildOpFairView(opSlice, opRowHomeCodeFor(opSlice, game.homeCode));
    const opDotSide =
      opDotView == null
        ? null
        : oddsOutcome === 'draw'
          ? opDotView.draw
          : teamSide === 'away'
            ? opDotView.away
            : opDotView.home;
    const fair = selectFairDotValue(opDotSide) ?? { source: 'consensus', tenths: null, cents: null };
    const fairYesCents = fair.tenths != null ? fair.tenths / 10 : fair.cents;
    const fairActiveCents =
      fairYesCents == null ? null : marketSide === 'yes' ? fairYesCents : mirrorCents(fairYesCents);
    const showFairDot =
      fairActiveCents != null &&
      game.gameState !== 'final' &&
      !settled &&
      !isConfirmedFinal(liveGameState);
    const buildFairDot = (
      side: { asks: BookLevel[]; bids: BookLevel[] },
      asksPadded: Array<BookLevel | null>,
      bidsPadded: Array<BookLevel | null>,
      venueLabel: string,
    ): LadderConsensusDot | null => {
      if (!showFairDot || fairActiveCents == null) return null;
      const placement = consensusDotPlacement(asksPadded, bidsPadded, fairActiveCents);
      if (!placement) return null;
      const bestAsk = side.asks[0]?.priceCents ?? null;
      const tone = consensusDotTone(fairActiveCents, bestAsk);
      // Label reads in ACTIVE-side space — on the NO ladder the trader is
      // pricing the NO bet, so the fair prob + American odds shown are the
      // NO side's (100 − team win prob), matching the prices around the dot.
      // (First cut showed the yes-space win prob on both toggles; on NO
      // ladders that read as inverted odds — Dan, 2026-07-07.)
      const pctText =
        fair.tenths != null
          ? fairActiveCents.toFixed(1)
          : String(Math.round(fairActiveCents));
      const americanText = americanOdds(fairActiveCents);
      const yesPctText =
        fair.tenths != null ? (fair.tenths / 10).toFixed(1) : String(Math.round(fair.cents ?? 0));
      const edge = bestAsk == null ? null : fairActiveCents - bestAsk;
      const toneLabel =
        tone === 'favorable'
          ? 'favorable'
          : tone === 'unfavorable'
            ? 'unfavorable'
            : tone === 'coin-toss'
              ? 'coin toss'
              : 'no ask to price against';
      const sourceLabel = fair.source === 'pinnacle' ? 'Pinnacle fair' : 'OddsPapi consensus fair';
      const title = `${sourceLabel} ${pctText}¢ ${marketSide.toUpperCase()} (${americanText})${
        marketSide === 'no' ? ` — team win ${yesPctText}%` : ''
      }${
        edge != null
          ? ` · ${edge >= 0 ? '+' : ''}${edge.toFixed(1)}¢ vs ${venueLabel} best ask ${bestAsk}¢`
          : ''
      } — ${toneLabel}${placement.clamped ? ` · beyond visible book (${placement.clamped})` : ''}`;
      return { ...placement, tone, source: fair.source, pctText, americanText, title };
    };
    const kFairDot = kBook?.bookSourceValidity === 'unconfirmed' ? null : buildFairDot(k, kAsksPadded, kBidsPadded, 'Kalshi');
    const pFairDot = buildFairDot(p, pAsksPadded, pBidsPadded, 'Polymarket');

    const renderBookRows = (
      kind: 'ask' | 'bid',
      kRows: Array<BookLevel | null>,
      pRows: Array<BookLevel | null>,
      usRows: Array<BookLevel | null>,
    ) => {
      return Array.from({ length: ladderLevels }, (_, idx) => {
        const kLevel = kRows[idx] ?? null;
        const pLevel = pRows[idx] ?? null;
        const usLevel = usRows[idx] ?? null;
        // The group renders away-ladder → team rows → home-ladder, so the
        // very first row is the away asks' top and the very last is the home
        // bids' bottom. Those two get the box's horizontal rails; the side
        // rails + selected-team tint apply to every ladder row so the book
        // reads as part of the highlighted game.
        // In the binary pair the away-ask top and home-bid bottom bracket the
        // team rows. A single-side row has no ladder above it (the team row is
        // the box top, handled there), so its one ladder only carries the
        // bottom rail on its last bid row.
        // #154 (D1): the ladder caption row now sits above the away asks and
        // owns the group-top rail, so no ask row carries it.
        const isGroupTop = false;
        // No bottom rail when a Draw/Tie continuation row follows — that row
        // (or its own ladder) closes the box instead.
        const isGroupBottom =
          !groupExtendsBelow &&
          (singleSide
            ? kind === 'bid' && idx === ladderLevels - 1
            : teamSide === 'home' && kind === 'bid' && idx === ladderLevels - 1);
        const rowClass = cn(
          'bg-black/15 light:bg-black/[0.08]',
          // Blue selection box rails — suppressed under hideSelectionBox
          // (Game Detail's moneyline section); the selected-team tint stays
          // either way.
          !hideSelectionBox && selectionRails(isGroupTop, isGroupBottom, holdColumnsOnExpand),
        );
        return (
          <LadderRow
            key={`${game.key}-${teamSide}-${kind}-${idx}`}
            rowClass={rowClass}
            ladderBlock={teamSide}
            kLevel={kLevel}
            pLevel={pLevel}
            kTicker={kBook?.marketTicker}
            pTicker={pBook?.marketTicker}
            kUnavailable={kBook?.bookSourceValidity === 'unconfirmed'}
            pUnavailable={pBook?.bookSourceValidity === 'unconfirmed'}
            kMaxLiq={kMaxLiq}
            pMaxLiq={pMaxLiq}
            kLastCents={kLast}
            pLastCents={pLast}
            kind={kind}
            marketSide={marketSide}
            makerRebatePct={makerRebateFraction}
            teamCode={teamCode}
            teamName={teamName}
            isSelectedTeam={expandedTeamSide === teamSide}
            kUserOrders={
              kLevel
                ? kind === 'ask'
                  ? kSellOrdersByCents.get(kLevel.priceCents)
                  : kBuyOrdersByCents.get(kLevel.priceCents)
                : undefined
            }
            kFairDot={
              kFairDot && kFairDot.kind === kind && kFairDot.idx === idx ? kFairDot : undefined
            }
            pFairDot={
              pFairDot && pFairDot.kind === kind && pFairDot.idx === idx ? pFairDot : undefined
            }
            onKalshiClick={() => {
              if (!kLevel || kBook?.bookSourceValidity === 'unconfirmed') return;
              const liquiditySeed = captureBookLiquidity(kBook?.marketTicker, marketSide, kLevel.size);
              onTradeClick?.(game.key, teamSide, 'kalshi', {
                priceCents: kLevel.priceCents,
                ...(liquiditySeed ? { liquiditySeed } : {}),
                initialOrderSide: marketSide,
                postOnly: kind === 'bid',
              });
            }}
            onPolyClick={() => {
              if (!pLevel || pBook?.bookSourceValidity === 'unconfirmed') return;
              onTradeClick?.(game.key, teamSide, 'polymarket', {
                priceCents: pLevel.priceCents,
                initialOrderSide: marketSide,
                postOnly: kind === 'bid',
              });
            }}
            hideDateColumn={hideDateColumn}
            hideScoreColumn={hideScoreColumn}
            hideOddsColumn={hideOddsColumn}
            hideOpColumn={hideOpColumn}
            hidePolyColumns={hidePolyColumns}
            showUsColumn={showUsColumn}
            usLevel={usLevel}
            usUnavailable={usUnavailable}
            // As the US pill: the US ticket for this team and side at the price.
            onUsClick={() => {
              if (!usLevel || !usBookCurrent(usLadderBook, Date.now())) return;
              onUsTradeClick?.(game.key, teamSide, { priceCents: usLevel.priceCents, initialOrderSide: marketSide });
            }}
            showNovigColumn={showNovigColumn}
          />
        );
      });
    };

    // #154 (D1): visible ladder caption. For the away block it is the top of the
    // expanded selection box (owns border-t-2); the home block's caption is
    // interior, and singleSide's team row above is the box top — caption interior.
    const captionRowClass = cn(
      'bg-black/15 light:bg-black/[0.08]',
      !hideSelectionBox && selectionRails(!singleSide && teamSide === 'away', false, holdColumnsOnExpand),
    );

    return (
      <Fragment key={`${game.key}-${teamSide}-ladder`}>
        <LadderColumnCaption
          rowClass={captionRowClass}
          ladderBlock={teamSide}
          kBookStatus={kBook?.bookSourceValidity === 'unconfirmed' ? (kBook.retainedDepth ? 'Last confirmed book' : 'Awaiting book') : undefined}
          pBookStatus={pBook?.bookSourceValidity === 'unconfirmed' ? (pBook.retainedDepth ? 'Last confirmed book' : 'Awaiting book') : undefined}
          kTicker={kBook?.marketTicker}
          pTicker={pBook?.marketTicker}
          hideDateColumn={hideDateColumn}
          hideScoreColumn={hideScoreColumn}
          hideOddsColumn={hideOddsColumn}
          hideOpColumn={hideOpColumn}
          hidePolyColumns={hidePolyColumns}
          holdColumnsOnExpand={holdColumnsOnExpand}
          showUsColumn={showUsColumn}
          usDepthShown={us !== null}
          usBookStatus={usBookStatus}
          showNovigColumn={showNovigColumn}
        />
        {renderBookRows('ask', kAsksPadded, pAsksPadded, usAsksPadded)}
        {/* Asks ↔ bids separator. Faint thin grey hairline so it's
            clear where the spread sits without competing visually
            with the row borders. */}
        <tr
          aria-hidden="true"
          data-ladder-block={teamSide}
          className={cn(
            'bg-black/15 light:bg-black/[0.08]',
            !hideSelectionBox && selectionRails(false, false, holdColumnsOnExpand),
          )}
        >
          <td colSpan={15 - (hideDateColumn ? 1 : 0) - (hideScoreColumn ? 1 : 0) - (hideOpColumn ? 1 : 0) - (hidePolyColumns ? 4 : 0) + (showUsColumn ? 4 : 0) + (showNovigColumn ? 1 : 0)} className="border-border/40 border-t p-0" />
        </tr>
        {renderBookRows('bid', kBidsPadded, pBidsPadded, usBidsPadded)}
      </Fragment>
    );
  };

  const showPairedBooks = expandedTeamSide !== null;

  // Single-outcome mode (Game Lines: Tie, single spread/total lines): render
  // just this outcome's row + its own ladder, not the away/home pair.
  if (singleSide) {
    return (
      <Fragment>
        {renderTeamRow(singleSide, !continuation)}
        {showPairedBooks && renderLadderRows(singleSide)}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {showPairedBooks && renderLadderRows('away')}
      {renderTeamRow('away', true)}
      {renderTeamRow('home', false)}
      {showPairedBooks && renderLadderRows('home')}
      {/* #1389 — the game's inline injury report, opened by its entry. */}
      {showInjuryEntries && injuryReportOpen && onCloseInjuryReport && (
        <tr data-testid="board-injury-report-row">
          <td colSpan={15 - (hideDateColumn ? 1 : 0) - (hideScoreColumn ? 1 : 0) - (hideOpColumn ? 1 : 0) - (hidePolyColumns ? 4 : 0) + (showUsColumn ? 4 : 0) + (showNovigColumn ? 1 : 0)} className="px-3">
            <BoardInjuryReport
              id={injuryReportId}
              gameKey={game.key}
              title={`${game.awayName || game.awayCode} @ ${game.homeName || game.homeCode}`}
              teams={[game.awayCode, game.homeCode]}
              onClose={onCloseInjuryReport}
            />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

// ── PosCell — per-user Kalshi position + open-order chips ───────────
// Renders side-by-side chips for the row's Kalshi ticker, scoped to the
// logged-in user. The position chip ('c') uses an SVG ring split into a
// green YES arc and a red NO arc proportional to the user's net contracts
// per side, so a 75/25 holding shows 75% green + 25% red. Each open order
// gets its own ('o') chip with `queue_position / queue_position + better`
// — "better" means cumulative same-side bid depth at strictly more
// aggressive prices, recomputed from the current orderbook on every render
// (no extra subscription; the row already updates with marketStore RAF).

function PosCell({
  ticker,
  book,
  positionByTicker,
  ordersByTicker,
}: {
  ticker: string | null | undefined;
  book: VenueBook | null;
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
}) {
  if (!ticker) return null;
  const pos = positionByTicker.get(ticker);
  const orders = ordersByTicker.get(ticker);
  if (!pos && (!orders || orders.length === 0)) return null;

  return (
    <div className="flex items-center gap-1">
      {pos && pos.yesCount + pos.noCount > 0 && (
        <span className="inline-flex items-center" title={heldContractsTitle(pos)}>
          <PosRing yesCount={pos.yesCount} noCount={pos.noCount} variant="closed" size={20} />
        </span>
      )}
      {/* Open-order indicator — single O chip aggregating all working orders
          on this ticker (per-row qty/queue text already lives on the ladder
          rows). Ring colors split by yes/no order count so a mixed book reads
          at a glance; tooltip lists each order one per line. */}
      {orders && orders.length > 0 && (() => {
        let yesOrders = 0;
        let noOrders = 0;
        for (const o of orders) {
          if (o.side === 'yes') yesOrders += 1;
          else noOrders += 1;
        }
        const tooltip = orders.map((o) => orderTooltip(o, book)).join('\n');
        return (
          <span className="inline-flex items-center" title={tooltip}>
            <PosRing yesCount={yesOrders} noCount={noOrders} variant="open" size={20} />
          </span>
        );
      })()}
    </div>
  );
}

// ── OddsPctText — consensus % with tenths-trend cues ─────────────────
// Color and confidence-opacity are decided by the caller; this wrapper owns
// the three per-number trend treatments driven by the sub-cent consensus
// feed (HYDRA_API.md "Unabated consensus update"):
//   A. recency brightness — the number rides from its confidence baseline
//      to full opacity on every frame arrival (tenth-only frames count —
//      the integer can be identical to the previous frame's) and eases
//      back to baseline over ~10s.
//   B. trend arrow — a PERMANENT green ▲ / red ▼ (TrendArrow atom): the
//      sign of the net tenths drift over a trailing ~10s window while
//      live, holding the last observed direction once the window drains.
//      Freshness reads from tone — recency-weighted delta mass colors it,
//      draining to almost fully grey ~10s after the last move. Invisible
//      spacer before the side ever moves, so the layout never shifts. The
//      arrow ignores the confidence dim so direction stays legible on a
//      near-coin-flip.
//   C. sharp anchor — a superscript gold star (SharpStar atom) while the
//      consensus is Pinnacle-anchored (sharpBookUsed): bright gold on a
//      fresh frame, decaying to nearly grey ~10s after the last update,
//      resting grey while the anchor holds. No star = field-only, the
//      normal state for most games (src 7 is sparse), or unknown when the
//      feed predates the flag.
// Plus the INTEGER-change cue: when the displayed integer itself moves
// (not a tenth-only frame), the number pulses the amber halo exactly four
// times (animate-odds-integer-glow), re-triggered by a key remount per
// change. This is the bounded successor to the old infinite
// useFlashOnChange amber pulse.
function OddsPctText({
  pctValue,
  text,
  american,
  toneClass,
  sizeClass,
  opacity,
  title,
  glow,
  sharp,
}: {
  /** The displayed integer — drives the 4-beat glow on change. The rendered
   *  `text` may carry a tenths digit; the glow stays keyed on the integer so
   *  the 250ms-coalesced tenths cadence doesn't strobe the halo (same rule
   *  as the panel's U-row). */
  pctValue: number;
  text: string;
  /** American-odds translation rendered in a small muted line under the %.
   *  Null/undefined = omit the line entirely. */
  american?: string | null;
  toneClass: string;
  sizeClass: string;
  opacity: number;
  title?: string;
  glow?: ConsensusSideGlow | null;
  sharp?: boolean | null;
}) {
  const recency = glow?.recency ?? 0;
  const effectiveOpacity = opacity + (1 - opacity) * recency;
  const integerGlowKey = useChangeKey(pctValue);
  return (
    <span className="inline-flex flex-col items-center leading-tight">
      <span className="inline-flex items-center gap-0.5">
        <TrendArrow
          direction={glow?.direction ?? 0}
          strength={glow?.strength ?? 0}
          sizeClass={sizeClass}
        />
        <span
          key={integerGlowKey}
          className={cn(
            sizeClass,
            toneClass,
            'transition-opacity duration-200 ease-linear',
            integerGlowKey > 0 && 'animate-odds-integer-glow',
          )}
          style={{ opacity: effectiveOpacity }}
          title={title}
        >
          {text}
        </span>
        <SharpStar sharp={sharp} recency={recency} className="text-[10px]" />
      </span>
      {american != null && american !== '—' && (
        <span
          className="text-muted-foreground text-[11px]"
          style={{ opacity: effectiveOpacity }}
          title={title}
        >
          {american}
        </span>
      )}
    </span>
  );
}

// ── OpOddsText — the OP/PNCL column's cell content ───────────────────
// Dan-amended 2026-08-03 (equal importance + full U-column styling):
// renders `consensus / pinnacle` as TWO equal-weight numbers at the Odds
// column's size, styled with the U column's visual language — the
// green-leader/blue-trailer rank tone + confidence opacity on the
// consensus number (ranked WITHIN this game's OP values only, never
// against Unabated's), a 4-beat amber integer halo per number on change
// (keyed per displayed integer so the tenths cadence doesn't strobe,
// same rule as OddsPctText) — plus the pinnacle number's 6s UPDATE GLOW
// (globals.css op-anchor-glow: a quote update lights a soft halo around
// the number that burns out over OP_ANCHOR_GLOW_MS; keyed per quote,
// negative delay so older quotes mount without a halo; the number's own
// color never changes — Dan 2026-08-04). American subline
// from the consensus cents exactly as the U column derives its own.
// ANCHOR-ONLY (consensus absent) shows the pinnacle number alone with a
// dotted underline mark. Neither ⇒ `—`.
// This cell NEVER computes fair values — everything shown is off the wire
// (rule 6c); the #1287 My books value appears only in Game Detail's table. Still excluded from trend arrows, sharp star, fair-dot anchoring,
// divergence signals, and Unabated's cross-source ranking.
// Freshness (rule 7 as amended) is PER DISPLAYED VALUE and independent:
// the consensus authority is consensus.lastUpdateMs, the pinnacle
// number's is that quote's own updatedAtMs; OpGameOdds.lastUpdateMs is
// BARRED for both. NO opacity dimming (Dan 2026-08-04 — a quiet pregame
// quote isn't wrong; matches the U column's no-dim precedent): age shows
// through the hover annotation: a value quiet past OP_STALE_MS gains
// the factual "no delta received in Xm" line.
/** Exported for GameLinesSection's full-feed-B specials block — the same
 *  consensus/pinnacle two-line cell every OP surface renders (Dan's bar:
 *  any market shown shows consensus + Pinnacle). */
export function OpOddsText({
  slice,
  side,
  rowHomeCode,
  glow = null,
  lastAnchor = null,
}: {
  slice: OpOddsSlice | null;
  side: TeamSide | 'draw';
  rowHomeCode: string;
  /** OP's own trend glow (useConsensusGlow over the OP trend bucket) —
   *  velocity arrow + recency brightness on the consensus number. */
  glow?: ReturnType<typeof useConsensusGlow>;
  /** Last-known anchor fair values (store cache) — the number the
   *  phase-3 strikethrough is drawn over. */
  lastAnchor?: OpLastAnchorFair | null;
}) {
  // Derivations before hooks — hooks must run unconditionally.
  const view = slice == null ? null : buildOpFairView(slice, rowHomeCode, lastAnchor);
  const sv = view == null ? null : side === 'draw' ? view.draw : side === 'away' ? view.away : view.home;
  const consensusText = sv == null ? null : fairPctText(sv.consensusTenths);
  const anchorText = sv == null ? null : fairPctText(sv.anchorFairTenths);
  // U-style integer halos, one per displayed number.
  const consensusGlowKey = useChangeKey(
    sv?.consensusTenths == null ? null : Math.round(sv.consensusTenths / 10),
  );
  const anchorGlowKey = useChangeKey(
    sv?.anchorFairTenths == null ? null : Math.round(sv.anchorFairTenths / 10),
  );
  // Mounted-freshness clock authority (G1 round 3): the per-value
  // "no delta received" annotations flip at OP_STALE_MS off render-time
  // Date.now() reads — arm ONE bounded re-render at each upcoming
  // boundary so a quiet cell crosses it without depending on unrelated
  // store/parent activity. (The pinnacle glow needs nothing: pure CSS.)
  useDeadlineTick([
    view?.consensusLastUpdateMs != null ? view.consensusLastUpdateMs + OP_STALE_MS : null,
    view?.anchorUpdatedAtMs != null ? view.anchorUpdatedAtMs + OP_STALE_MS : null,
    // Round-1 finding 2: the withdrawal tooltip's "Xm ago" must advance
    // while mounted — arm the next age-display boundary (re-armed per
    // fire; one bounded timeout, no ticker).
    view?.pinnacleWithdrawn != null
      ? opWithdrawalAgeDeadline(view.pinnacleWithdrawn.sinceMs)
      : null,
  ]);

  const dash = <span className="text-muted-foreground/40">—</span>;
  // Rule 12: an explicit pinnacle withdrawal renders (struck) even when
  // no live fair value does.
  const withdrawn = view?.pinnacleWithdrawn ?? null;
  if (view == null || sv == null || (consensusText == null && anchorText == null && withdrawn == null)) {
    return dash;
  }

  const anchorOnly = consensusText == null;
  // The Odds column's green-leader/blue-trailer tone + confidence
  // opacity, computed over THIS game's displayed OP values (per-side
  // primary: consensus, else anchor) — OP ranks against itself only.
  const displayedPct = (s: OpFairSideView | null): number | null => {
    const t = s == null ? null : s.consensusTenths ?? s.anchorFairTenths;
    return t == null ? null : Math.round(t) / 10;
  };
  const gameValues = [view.away, view.home, view.draw]
    .map(displayedPct)
    .filter((v): v is number => v != null);
  const sidePct = displayedPct(sv);
  const toneClass =
    sidePct == null ? 'text-muted-foreground' : consensusRankTone(sidePct, gameValues);
  // The trend engine tracks PROVIDER slots — map through the row swap
  // exactly as the UNBTD column does.
  const glowSide: 'away' | 'home' | 'draw' =
    side === 'draw' ? 'draw' : (side === 'away') !== view.swapped ? 'away' : 'home';
  const sideGlow: ConsensusSideGlow | null = glow?.[glowSide] ?? null;
  // U-column brightness: confidence base, ridden to full by frame recency.
  const baseOpacity = sidePct == null ? 1 : 0.4 + 0.6 * (Math.abs(sidePct - 50) / 50);
  const confidenceOpacity = baseOpacity + (1 - baseOpacity) * (sideGlow?.recency ?? 0);
  // American sublines — the U column's exact derivation (rule 6c renders
  // hydra values; the American translation is display-only, like U's).
  // ONE PER VALUE (Dan, 2026-08-17): consensus's American sits under the
  // consensus %, the anchor's under the anchor %, column-aligned.
  const consensusAmerican = anchorOnly
    ? null
    : fairAmericanText(sv.consensusTenths, sv.consensusCents);
  const anchorAmerican = fairAmericanText(sv.anchorFairTenths, null);
  // Rule 7 (amended): independent per-value freshness authorities.
  const anchorStale = view.anchorUpdatedAtMs != null && opIsStale(view.anchorUpdatedAtMs);
  const consensusStale =
    view.consensusLastUpdateMs != null && opIsStale(view.consensusLastUpdateMs);
  const primaryStale = anchorOnly ? anchorStale : consensusStale;
  const anchorName = view.anchorBookmaker ?? 'pinnacle';
  // Rule 12: the strikethrough tooltip = reason + age from sinceMs ONLY.
  const withdrawnTitle = withdrawn != null ? opWithdrawalTitle(withdrawn) : '';
  const title = anchorOnly
    ? `OddsPapi · de-vigged ${anchorName} only (no consensus for this value) — fair win %, hydra-computed. Comparison only.${
        anchorStale ? ` ${opNoDeltaText(anchorName, view.anchorUpdatedAtMs!)}` : ''
      }${withdrawn != null ? ` ${withdrawnTitle}.` : ''}`
    : `OddsPapi consensus (de-vigged${
        view.contributingBooks != null ? `, ${view.contributingBooks} books` : ''
      }${view.pinnacleAnchored ? `, ${anchorName}-anchored` : ''}) / de-vigged ${anchorName} — fair win %, hydra-computed. Comparison only.${
        consensusStale ? ` ${opNoDeltaText('Consensus', view.consensusLastUpdateMs!)}` : ''
      }${
        anchorText != null && anchorStale
          ? ` ${opNoDeltaText(anchorName, view.anchorUpdatedAtMs!)}`
          : ''
      }${withdrawn != null ? ` ${withdrawnTitle}.` : ''}`;
  // The struck number: last-known anchor fair value for this side, or a
  // struck placeholder when never seen this session.
  const struckText =
    withdrawn != null ? fairPctText(sv.withdrawnLastTenths) : null;
  return (
    <span
      className="inline-flex flex-col items-center leading-tight"
      title={title}
      data-op-mode={anchorOnly ? 'anchor-only' : 'consensus'}
      data-op-stale={primaryStale || undefined}
      data-op-anchor-stale={(anchorText != null && anchorStale) || undefined}
    >
      {/* items-start: each value is a two-line column (% over its own
          American), so the %-line stays rank-aligned across arrow /
          consensus / separator / pinnacle while each American sits under
          the value it translates (Dan, 2026-08-17). */}
      <span className="inline-flex min-w-[13ch] items-start justify-center gap-1">
        {consensusText != null && (
          <TrendArrow
            direction={sideGlow?.direction ?? 0}
            strength={sideGlow?.strength ?? 0}
            sizeClass="text-[15px]"
          />
        )}
        {consensusText != null && (
          <span className="inline-flex flex-col items-center">
            <span
              key={`c${consensusGlowKey}`}
              className={cn(
                toneClass,
                'text-[15px]',
                consensusGlowKey > 0 && 'animate-odds-integer-glow',
              )}
              style={{ opacity: confidenceOpacity }}
              data-op-consensus-value
            >
              {consensusText}%
            </span>
            {consensusAmerican != null && (
              <span className="text-muted-foreground text-[11px]" data-op-consensus-american>
                {consensusAmerican}
              </span>
            )}
          </span>
        )}
        {/* Anti-jitter (Dan 2026-08-23): the separator + anchor SLOT render
            whenever consensus does — Pinnacle dropping in/out must change a
            glyph, never the cell's shape. The ghost dash below is the
            reserved slot for an absent anchor. */}
        {consensusText != null && (
          <span className="text-muted-foreground/50 text-[13px]">/</span>
        )}
        {/* Pinnacle at EQUAL size (Dan 2026-08-03). Outer span: freshness
            update glow keyed per quote (halo only — its color is static);
            inner span: integer halo keyed per value change. Dims off ITS
            OWN quote age, independent of consensus. */}
        {anchorText != null && view.anchorUpdatedAtMs != null && (
          <span className="inline-flex flex-col items-center">
            <span
              key={`f${view.anchorUpdatedAtMs}`}
              className={cn(
                'animate-op-anchor-glow text-muted-foreground text-[15px]',
                anchorOnly && 'border-b border-dotted border-teal-400/60',
              )}
              style={{ animationDelay: `${opAnchorGlowDelayMs(view.anchorUpdatedAtMs)}ms` }}
              data-op-anchor-value
            >
              <span
                key={`a${anchorGlowKey}`}
                className={cn(anchorGlowKey > 0 && 'animate-odds-integer-glow')}
              >
                {anchorText}%
              </span>
            </span>
            {anchorAmerican != null && (
              <span className="text-muted-foreground text-[11px]" data-op-anchor-american>
                {anchorAmerican}
              </span>
            )}
          </span>
        )}
        {/* RESERVED anchor slot — Pinnacle currently absent with no
            withdrawal entry. A fixed-width dimmed dash holds the anchor
            column's real estate so the quote's return costs zero reflow.
            Width matches the value form ("50.9%" ≈ 5ch). */}
        {consensusText != null && anchorText == null && withdrawn == null && (
          <span
            className="text-muted-foreground/30 inline-block w-[5ch] text-center text-[15px]"
            data-op-anchor-ghost
          >
            —
          </span>
        )}
        {/* THE STRIKETHROUGH (phase 3, rule 12): pinnacle listed in the
            frame's withdrawnBooks ⇒ its slot renders the last-known
            value in red, struck through — the number stays, the strike
            says the price is withheld and the tooltip says why. Clears
            the instant a frame arrives without the entry (absolute
            frames, no latching). Never rendered from mere absence. */}
        {withdrawn != null && anchorText == null && (
          <span
            className="text-red-400 line-through decoration-red-400/80 text-[15px]"
            title={withdrawnTitle}
            data-op-withdrawn={withdrawn.reason}
          >
            {struckText != null ? `${struckText}%` : '—'}
          </span>
        )}
      </span>
    </span>
  );
}

// ── LadderColumnCaption ───────────────────────────────────────────────

/**
 * #154 (D1) — VISIBLE column caption for the EXPANDED ladder.
 *
 * The shared thead "K Vol"/"K Liq" headers describe the MARKET rows (executed
 * traded volume / total $ resting in book) and must stay intact. But the ladder's
 * per-price cells under them are resting contract qty and resting $ at that price —
 * NOT traded volume. A tooltip alone doesn't prevent MISREADING (it needs a hover),
 * so the expanded ladder carries its own visible, truthful caption: "Resting Qty"
 * over the size column and "Resting $" over the liquidity column, for each venue.
 * Mirrors LadderRow's exact column layout so the labels sit over the right cells.
 * Presentation only — no data change. Exported for #154's focused label test.
 */
export function LadderColumnCaption({
  rowClass,
  ladderBlock,
  kBookStatus,
  pBookStatus,
  kTicker,
  pTicker,
  hideDateColumn,
  hideScoreColumn,
  // hideOddsColumn accepted (caller symmetry) but unused: the U slot became
  // structural with the odds filter — the caption always keeps its spacer.
  hideOpColumn = true,
  hidePolyColumns = false,
  holdColumnsOnExpand = false,
  showUsColumn = false,
  usDepthShown = false,
  usBookStatus,
  showNovigColumn = false,
}: {
  rowClass?: string;
  /** #1398 — which team's ladder block this row opens (`data-ladder-block`). */
  ladderBlock?: TeamSide;
  kBookStatus?: string;
  pBookStatus?: string;
  kTicker?: string;
  pTicker?: string;
  hideDateColumn?: boolean;
  hideScoreColumn?: boolean;
  hideOddsColumn?: boolean;
  /** Defaults TRUE (fail closed) — see GameRowsProps.hideOpColumn. */
  hideOpColumn?: boolean;
  /** See GameRowsProps.hidePolyColumns. */
  hidePolyColumns?: boolean;
  /** See GameRowsProps.holdColumnsOnExpand. */
  holdColumnsOnExpand?: boolean;
  /** The board's US column: a blank cell under it, as ArchivedRow draws, so
   *  the selection box's right rail reaches the table edge. */
  showUsColumn?: boolean;
  /** #1234 — the US cells below carry depth: label them. */
  usDepthShown?: boolean;
  usBookStatus?: string;
  /** #1150 — likewise the board's Novig column. */
  showNovigColumn?: boolean;
}) {
  // #1112: inline-size containment drops a label from its column's sizing
  // (it still renders, inside the width the collapsed rows already gave).
  const held = (label: ReactNode) =>
    holdColumnsOnExpand ? <div className="[contain:inline-size]">{label}</div> : label;
  const qty = (
    <span className="text-muted-foreground/70 inline-block w-full whitespace-nowrap text-right text-[9px] font-semibold uppercase tracking-wide">
      Resting Qty
    </span>
  );
  const dollars = (
    <span className="text-muted-foreground/70 whitespace-nowrap text-[9px] font-semibold uppercase tracking-wide">
      Resting $
    </span>
  );
  return (
    <tr
      className={cn(rowClass)}
      data-ladder-block={ladderBlock}
      title="Expanded ladder — resting book (contracts / $ resting at each price), NOT traded volume"
    >
      {!hideDateColumn && <Td className="py-0.5">{' '}</Td>}
      {/* Team-label spacer */}
      <Td className="py-0.5">{' '}</Td>
      {!hideScoreColumn && <Td className="py-0.5">{' '}</Td>}
      <Td className="py-0.5">{' '}</Td>{/* U/orders slot — structural */}
      {/* OP spacer */}
      {!hideOpColumn && <Td className="py-0.5">{' '}</Td>}
      {/* Kalshi price spacer */}
      <Td className="py-0.5 text-[9px] text-amber-400">{kBookStatus ?? <OrderbookFreshness ticker={kTicker} />}</Td>
      {/* K Size → Resting Qty */}
      <Td className="py-0.5 text-right">{held(qty)}</Td>
      {/* K Liquidity → Resting $ */}
      <Td className="py-0.5">{held(dollars)}</Td>
      {/* K Queue + K Δ/m spacers */}
      <Td className="py-0.5">{' '}</Td>
      <Td className="py-0.5">{' '}</Td>
      {!hidePolyColumns && (
        <>
          {/* Poly price spacer */}
          <Td className="py-0.5 text-[9px] text-amber-400">{pBookStatus ?? <OrderbookFreshness ticker={pTicker} />}</Td>
          {/* P Size → Resting Qty */}
          <Td className="py-0.5 text-right">{held(qty)}</Td>
          {/* P Liquidity → Resting $ */}
          <Td className="py-0.5">{held(dollars)}</Td>
          {/* P Δ/m spacer */}
          <Td className="py-0.5">{' '}</Td>
        </>
      )}
      {/* #1234 — over the US depth cells when they carry a book; blank otherwise
          (the US pills live in the team rows). */}
      {showUsColumn && (<>
        <Td className="py-0.5 text-center">
          {usDepthShown ? (
            <span className="text-muted-foreground/70 whitespace-nowrap text-[9px] font-semibold uppercase tracking-wide">
              US price
            </span>
          ) : usBookStatus ? (
            <span className="text-amber-400 whitespace-nowrap text-[9px]" role="status">{usBookStatus}</span>
          ) : ' '}
        </Td>
        <UsLadderMetricCaption shown={usDepthShown} />
      </>)}
      {showNovigColumn && <Td className="py-0.5">{' '}</Td>}
    </tr>
  );
}

// ── LadderRow ────────────────────────────────────────────────────────

/** Placement + presentation payload for the ladder's fair-value
 *  dot, resolved per venue by renderLadderRows in GameRows. Attached to the
 *  single ladder row the placement anchors to; the dot's offsetRows lets it
 *  visually sit anywhere between that row's center and the next level's. */
interface LadderConsensusDot extends ConsensusDotPlacement {
  tone: ConsensusDotTone;
  /** Which fair value the dot marks: Pinnacle (filled dot, "P") or the
   *  OddsPapi consensus fallback (outline dot, "O"). */
  source: FairDotSource;
  /** Fair win prob %, tenth precision when hydra sends it ("46.3"). */
  pctText: string;
  /** American translation of the same prob ("+116.0"). */
  americanText: string;
  title: string;
}

const FAIR_DOT_BG: Record<ConsensusDotTone, string> = {
  favorable: 'bg-positive',
  'coin-toss': 'bg-yellow-400',
  unfavorable: 'bg-negative',
  'no-ask': 'bg-muted-foreground/70',
};
const FAIR_DOT_TEXT: Record<ConsensusDotTone, string> = {
  favorable: 'text-positive',
  'coin-toss': 'text-yellow-400',
  unfavorable: 'text-negative',
  'no-ask': 'text-muted-foreground',
};
/** Outline variant (consensus fallback): tone on the border, hollow fill. */
const FAIR_DOT_BORDER: Record<ConsensusDotTone, string> = {
  favorable: 'border-positive',
  'coin-toss': 'border-yellow-400',
  unfavorable: 'border-negative',
  'no-ask': 'border-muted-foreground/70',
};

/** The dot + its "P 46.3% +116.0" label. Lives in the venue price cell's
 *  INACTIVE half — prices hang under the active Y/N pill, so the other
 *  pill's lane is always free: under Y ('no' mode) the group right-anchors
 *  at the cell midpoint, under N ('yes' mode) it left-anchors there, so the
 *  label always grows into the empty half instead of over the price text.
 *  Amber letter = source identity: "P" with a FILLED dot when the value is
 *  Pinnacle's fair, "O" with an OUTLINE dot when it fell back to the
 *  OddsPapi consensus (Dan, 2026-09-10); tone colors the fill or the ring
 *  respectively. Values at the queue-chip size (10px mono, foreground/90).
 *  Vertical position is % of
 *  the anchor row's height from its center (offsetRows can exceed 1 across
 *  padding rows; td overflow is visible and positioned elements paint above
 *  the neighboring rows' backgrounds). Tenths updates glide via the top
 *  transition instead of jumping. Clamped placements (fair beyond the
 *  visible book) swap the dot for ▲/▼ pinned to the edge row so the marker
 *  never lies about a price it isn't at. */
function ConsensusFairDot({
  dot,
  marketSide,
}: {
  dot: LadderConsensusDot;
  marketSide: MarketSide;
}) {
  const anchor =
    marketSide === 'yes' ? { left: 'calc(50% + 4px)' } : { right: 'calc(50% + 4px)' };
  const top = dot.clamped
    ? dot.clamped === 'above'
      ? '25%'
      : '75%'
    : `calc(50% + ${dot.offsetRows * 100}%)`;
  return (
    <span
      data-testid="fair-dot"
      data-fair-source={dot.source}
      className="absolute z-10 flex -translate-y-1/2 items-center gap-1 whitespace-nowrap font-mono text-[10px] leading-none"
      style={{ top, ...anchor, transition: 'top 250ms linear' }}
      title={dot.title}
    >
      {dot.clamped ? (
        <span className={cn('shrink-0 text-[9px]', FAIR_DOT_TEXT[dot.tone])}>
          {dot.clamped === 'above' ? '▲' : '▼'}
        </span>
      ) : dot.source === 'pinnacle' ? (
        <span
          data-testid="fair-dot-glyph"
          className={cn(
            'h-2 w-2 shrink-0 rounded-full ring-1 ring-black/50 dark:ring-white/40',
            FAIR_DOT_BG[dot.tone],
          )}
        />
      ) : (
        <span
          data-testid="fair-dot-glyph"
          className={cn(
            'h-2 w-2 shrink-0 rounded-full border bg-transparent',
            FAIR_DOT_BORDER[dot.tone],
          )}
        />
      )}
      <span className="font-semibold text-amber-400">{dot.source === 'pinnacle' ? 'P' : 'O'}</span>
      <span className="text-foreground/90">{dot.pctText}%</span>
      <span className="text-foreground/90">{dot.americanText}</span>
    </span>
  );
}

/**
 * #1070 — the reserving ladder gutter's width, measured in the shift lab on
 * the widest content the states draw: two chips at `formatOrderQty`'s widest
 * (seven digits, 89px), a three-digit `+N` and the level control's retry face
 * (`↻1001`), in the app's Inter. The width is a cap, so nothing in the gutter
 * can widen the column, and it is what keeps every quantity, queue reading and
 * cancel whole. The level cancel confirms in its overlay (`compactLevelCancel`)
 * because its inline armed face (`✕ 3 · 6000000?`) would not fit beside them.
 *
 * Past that, a failed level sweep of 1002+ orders on one rung (`↻1002` beside
 * `+1000`) next to two seven-digit chips overflows by 1px (2px at `↻9999`).
 * The chip slots clip pixels rather than text (see `chipSlotPx`), so that
 * comes out of each chip's 2px right padding, not its digits. Idle, `+9997`
 * fits.
 */
const ORDER_GUTTER_PX = 262;
/** #1070 — inline chips the reserving gutter holds; the rest sit behind +N. */
const ORDER_GUTTER_CHIPS = 2;
/**
 * #1070 — each inline chip's slot: the widest chip face (`9999999/ 999`), so
 * a chip changing width never moves its neighbours (see `chipSlotPx`).
 */
const ORDER_GUTTER_CHIP_SLOT_PX = 91;

/**
 * #1070 — a ladder rung's own-order gutter for a trader who can trade from
 * the ladder: always the width of a full inline group, with or without
 * orders, so the first order to rest never widens the column.
 */
// #1250: unused reservation stays before the entire order group. Do not pin
// only its cancel button to the far edge: it must stay beside its quantities.
function ReservedOrderGutter({
  orders,
  level,
}: {
  orders: readonly BidRowOrder[];
  level: LadderLevelIdentity | null;
}) {
  const style = { width: ORDER_GUTTER_PX, minWidth: ORDER_GUTTER_PX, maxWidth: ORDER_GUTTER_PX };
  if (orders.length === 0) {
    return <div aria-hidden data-order-gutter className="shrink-0" style={style} />;
  }
  return (
    <div
      data-order-gutter
      className="text-foreground/90 flex shrink-0 items-center justify-end gap-1 overflow-hidden text-[10px] leading-4"
      style={style}
      onClick={(e) => e.stopPropagation()}
    >
      <RestingOrderGroup
        orders={orders}
        level={level}
        inlineLimit={ORDER_GUTTER_CHIPS}
        compactLevelCancel
        chipSlotPx={ORDER_GUTTER_CHIP_SLOT_PX}
      />
    </div>
  );
}

function LadderRow({
  kLevel,
  pLevel,
  kUnavailable = false,
  pUnavailable = false,
  kTicker,
  pTicker,
  kMaxLiq,
  pMaxLiq,
  kLastCents,
  pLastCents,
  kind,
  marketSide,
  makerRebatePct = 0,
  teamCode,
  teamName,
  isSelectedTeam,
  kUserOrders,
  kFairDot,
  pFairDot,
  onKalshiClick,
  onPolyClick,
  rowClass,
  ladderBlock,
  hideDateColumn = false,
  hideScoreColumn = false,
  // hideOddsColumn accepted but unused — the orders cell in this slot is
  // structural (odds filter, content-only semantics).
  hideOpColumn = true,
  hidePolyColumns = false,
  showUsColumn = false,
  usLevel = null,
  usUnavailable,
  onUsClick,
  showNovigColumn = false,
}: {
  kLevel: BookLevel | null;
  pLevel: BookLevel | null;
  kUnavailable?: boolean;
  pUnavailable?: boolean;
  kTicker?: string | null;
  pTicker?: string | null;
  kMaxLiq: number;
  pMaxLiq: number;
  /** The dashboard's active Y/N toggle. The price cells align under the
   *  matching pill — left (under Y) for 'yes', right (under N) for 'no' —
   *  so the ladder visually hangs beneath whichever side is selected. */
  marketSide: MarketSide;
  /** Maker-fee rebate fraction (0..1) folded into maker-perspective odds;
   *  0 = no rebate. Computed once per game by the parent. */
  makerRebatePct?: number;
  /** Side-native last trade for the Kalshi book (already inverted for
   *  NO when marketSide='no' upstream). Drives the slow-pulse grey
   *  glow on whichever ladder row matches it. Null when unknown. */
  kLastCents?: number | null;
  /** Same for the Polymarket book. */
  pLastCents?: number | null;
  kind: 'ask' | 'bid';
  teamCode: string;
  teamName: string;
  isSelectedTeam: boolean;
  /** User open orders matching this Kalshi bid row's price (bid kind only).
   *  Pre-sorted by qty desc, with same hover tooltip as the inline O ring. */
  kUserOrders?: BidRowOrder[];
  /** Fair-value dot (Pinnacle, else consensus) anchored to THIS row for the Kalshi price
   *  column (renderLadderRows resolves the anchor; at most one ladder row
   *  per venue carries it). Undefined = dot lives on another row or no
   *  consensus. */
  kFairDot?: LadderConsensusDot;
  /** Same for the Polymarket price column. */
  pFairDot?: LadderConsensusDot;
  onKalshiClick: () => void;
  onPolyClick: () => void;
  /** Tint + blue-selection-box rails for this ladder row, computed by the
   *  caller from the row's position in the expanded game group. */
  rowClass?: string;
  /** #1398 — the team ladder block this rung belongs to. With the
   *  `data-ladder-label` end and the order gutter / orders cell it marks the
   *  blank area Game Detail's injury panels may use (InjuryLadderPanels). */
  ladderBlock?: TeamSide;
  /** Skip the leading Date spacer when the containing table doesn't
   *  carry a Date column (GameDetail). Matches the GameRows prop. */
  hideDateColumn?: boolean;
  /** Skip the Score-column spacer / Odds (user-orders) column to match a
   *  thead without those columns (GameDetail Game Lines). */
  hideScoreColumn?: boolean;
  hideOddsColumn?: boolean;
  /** Defaults TRUE (fail closed) — see GameRowsProps.hideOpColumn. */
  hideOpColumn?: boolean;
  /** See GameRowsProps.hidePolyColumns. */
  hidePolyColumns?: boolean;
  /** The board's US column. Without `usLevel` its cell is blank, as
   *  ArchivedRow draws, so the selection box's right rail reaches the table edge. */
  showUsColumn?: boolean;
  /** #1234 — the US level at this row's index: price and resting qty, compact
   *  in the one US column. Null leaves the cell blank. */
  usLevel?: BookLevel | null;
  /** Why the US cells are blank, as their hover. */
  usUnavailable?: string;
  onUsClick?: () => void;
  /** #1150 — likewise the board's Novig column. */
  showNovigColumn?: boolean;
}) {
  const kIsLastTrade = kLastCents != null && kLevel?.priceCents === kLastCents;
  const pIsLastTrade = pLastCents != null && pLevel?.priceCents === pLastCents;
  const kLiq = restingDollars(kLevel);
  const pLiq = restingDollars(pLevel);
  const colorClass = kind === 'ask' ? 'text-negative' : 'text-positive';
  const hasUserOrders = kUserOrders && kUserOrders.length > 0;
  // #1070: for a trader who can trade from the ladder, the own-order gutter
  // holds the width of a full inline group whether or not orders rest here.
  // Unreserved, the first order on any rung widened the column ~174px and
  // slid every price on the ladder. The reserving gutter shares one cell
  // with the rung label, spanning the Team and Score columns too, so the
  // reservation reuses width the table already draws: the label needs ~62px
  // of a ~200px Team column, and the Score spacer is blank on every rung.
  // Measured, that holds two chips at every width without the sideways scroll
  // a per-column gutter cost; the rest sit behind the existing +N.
  // Kalshi-only (Poly own orders are not tracked here): a rung with no
  // Kalshi market can never hold one, so it reserves nothing. The compact
  // chip display only: a trader on the #677 expanded display keeps main's
  // wrapping region unchanged (its width and height bounds are its own).
  // And only where the Score spacer exists to absorb it: a table without a
  // Score column (Game Lines) has no blank width to reuse, so it keeps
  // main's unreserved gutter rather than widen.
  const compactOrders = useDisplaySettings((s) => s.restingOrderDisplay) !== 'expanded';
  const reserveOrderGutter =
    useGrantSlot('oneClickAccess') !== 'hidden' && !!kTicker && compactOrders && !hideScoreColumn;
  const ownOrderLevel: LadderLevelIdentity | null =
    kTicker && kLevel
      ? {
          marketTicker: kTicker,
          side: marketSide,
          // An ask row is the SELL stack, a bid row the BUY stack — the same
          // split buildOrdersByCents bucketed these by.
          action: kind === 'ask' ? 'sell' : 'buy',
          priceCents: kLevel.priceCents,
        }
      : null;

  // Dense ladder cents are always the raw venue level. The adjacent odds use
  // the action implied by that row: an ask is crossed as a taker; a bid rests
  // as a maker. This is display-only and the click handlers still receive the
  // raw level cents. Polymarket has no fee schedule on file, so its effective
  // odds basis remains raw.
  const feeAdjusted = useDisplaySettings((s) => s.feeAdjustedOdds);
  const hideUnknownFeePrices = useDisplaySettings((s) => s.hideUnknownFeePrices);
  const kFeeEntry = useFeeFor(kTicker);
  const pFeeEntry = useFeeFor(pTicker);
  const resolvedKFeeStatus = useFeeDisplayStatus(kTicker, kFeeEntry);
  const kFeeStatus = feeAdjusted ? resolvedKFeeStatus : 'not-applicable';
  const hideKalshiPrice = hideUnknownFeePrices && feeDisplayCanHide(kFeeStatus);
  const kPrice = kLevel
    ? ladderRowPricePresentation(kLevel.priceCents, kind, feeAdjusted, kFeeEntry, makerRebatePct)
    : null;
  const pPrice = pLevel
    ? ladderRowPricePresentation(pLevel.priceCents, kind, feeAdjusted, pFeeEntry, makerRebatePct)
    : null;
  const kDisplayCents = hideKalshiPrice ? null : kPrice?.rawCents ?? null;
  const kOdds = hideKalshiPrice || !kPrice ? null : americanOdds(kPrice.effectiveCents);
  const pOdds = pPrice ? americanOdds(pPrice.effectiveCents) : null;

  // Price + odds nodes, defined once and dropped into the active half of the
  // split price cell below (see the two-half alignment note there).
  const kPriceTitle = kLevel
    ? hideKalshiPrice
      ? FEE_DISPLAY_MESSAGE[kFeeStatus] ?? undefined
      : kPrice && kPrice.effectiveCents !== kPrice.rawCents
        ? `${kIsLastTrade ? 'Last trade' : 'Click to trade'} @ ${kPrice.rawCents}¢ raw; Fees odds ${kOdds ?? '—'} use ${kPrice.effectiveCents}¢ ${kPrice.perspective} basis`
        : `${kIsLastTrade ? 'Last trade' : 'Click to trade'} @ ${kPrice?.rawCents ?? kLevel.priceCents}¢${kOdds ? ` → ${kOdds}` : ''}`
    : undefined;
  const kPriceOddsContent = kLevel ? (
    <>
      <span className={cn('inline-block min-w-[4ch] text-right tabular-nums', colorClass)}>
        {kDisplayCents != null ? `${kDisplayCents}¢` : '—'}
      </span>
      {kOdds && (
        <>
          <span className="text-muted-foreground/40 text-xs">|</span>
          <span className="text-foreground text-xs">{kOdds}</span>
        </>
      )}
    </>
  ) : null;
  const kPriceOdds = kLevel ? (
    hideKalshiPrice ? (
      <span
        className={cn('inline-flex items-center gap-1 px-1', kIsLastTrade && 'animate-last-trade-pulse')}
        title={kPriceTitle}
      >
        {kPriceOddsContent}
        <FeeScheduleMarker status={kFeeStatus} />
      </span>
    ) : (
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          className={cn(
            'inline-flex cursor-pointer items-center gap-1 rounded-sm border-0 bg-transparent px-1 font-mono hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500',
            kIsLastTrade && 'animate-last-trade-pulse',
          )}
          title={kUnavailable ? 'Book confirmation pending; order entry unavailable' : kPriceTitle}
          disabled={kUnavailable}
          onClick={(e) => {
            e.stopPropagation();
            onKalshiClick();
          }}
        >
          {kPriceOddsContent}
        </button>
        <FeeScheduleMarker status={kFeeStatus} />
      </span>
    )
  ) : null;
  const pPriceOdds = pLevel ? (
    <button
      type="button"
      className={cn(
        'inline-flex cursor-pointer items-center gap-1 rounded-sm border-0 bg-transparent px-1 font-mono hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500',
        pIsLastTrade && 'animate-last-trade-pulse',
      )}
      title={pUnavailable ? 'Book confirmation pending; order entry unavailable' : pIsLastTrade ? `Last trade @ ${pLevel.priceCents}¢` : `Click to trade @ ${pLevel.priceCents}¢${pOdds ? ` → ${pOdds}` : ''}`}
      disabled={pUnavailable}
      onClick={(e) => {
        e.stopPropagation();
        onPolyClick();
      }}
    >
      <span className={cn('inline-block w-[4ch] text-right tabular-nums', colorClass)}>
        {pLevel.priceCents}¢
      </span>
      {pOdds && (
        <>
          <span className="text-muted-foreground/40 text-xs">|</span>
          <span className="text-foreground text-xs">{pOdds}</span>
        </>
      )}
    </button>
  ) : null;

  const ladderLabel = (
    <div
      className="flex min-w-0 items-center gap-1.5 font-mono text-[10px]"
      title={`${teamName} ${kind}`}
    >
      <span
        className={cn(
          'max-w-[5ch] truncate rounded px-1 py-px font-semibold',
          isSelectedTeam ? 'bg-primary/10 text-primary' : 'text-muted-foreground',
        )}
      >
        {teamCode}
      </span>
      <span data-ladder-label="" className={cn('font-semibold', colorClass)}>{kind.toUpperCase()}</span>
    </div>
  );

  return (
    <tr className={cn(rowClass)} data-ladder-block={ladderBlock}>
      {!hideDateColumn && <Td className="py-0.5"> </Td>}
      {reserveOrderGutter ? (
        // #1070: label and reserving gutter share the Team, Score and orders
        // columns (see LadderRow above).
        <Td className="py-0.5 pr-0" colSpan={3}>
          <div className="flex items-center justify-between gap-1.5">
            {ladderLabel}
            <ReservedOrderGutter orders={kUserOrders ?? []} level={ownOrderLevel} />
          </div>
        </Td>
      ) : (
        <>
          {/* Compact ladder label so paired books remain readable when both teams open. */}
          <Td className="py-0.5">{ladderLabel}</Td>
          {/* Score-column spacer — score/countdown lives in the summary rows. */}
          {!hideScoreColumn && <Td className="py-0.5">{' '}</Td>}

          {/* Your active Kalshi orders resting at this price level (qty / queue-or-
              better, vertical-bar separated, qty desc). This is the column the
              Ask/Bid label used to occupy — direction is already conveyed by the
              price color and which half of the ladder (above/below the spread
              hairline) the row sits in, so the label was redundant. Right-aligned
              so the chips sit flush against the price column. Each chip is a
              direct-cancel control (RestingOrderChip — honors the Cancel guard
              setting + the #241 cancel fence), and the group is LED by #523's
              CancelLevelControl, which withdraws every own order at this exact
              level in one frozen action; the wrapper still stops
              propagation so clicks in the gaps don't open the trade panel.
              #650: the row draws at most INLINE_CHIP_LIMIT of them and moves
              the rest behind a `+N` disclosure — unbounded, they took the
              price column's width and overprinted its odds. RestingOrderGroup owns both
              that bound and the level control.
              (Poly user orders aren't tracked yet, so this stays Kalshi-only.) */}
          <Td className="py-0.5 text-right" data-ladder-orders="">
              {hasUserOrders ? (
                <div
                  className="text-foreground/90 flex items-center justify-end gap-1.5 overflow-hidden text-[10px] leading-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <RestingOrderGroup orders={kUserOrders!} level={ownOrderLevel} />
                </div>
              ) : (
                ''
              )}
          </Td>
        </>
      )}

      {/* OP spacer — the OP value lives in the team rows above; ladder
          rows leave it blank to keep header→row column alignment intact. */}
      {!hideOpColumn && <Td className="py-0.5">{' '}</Td>}

      {/* Kalshi price + American odds. The cell is split into two equal
          halves so the value hangs under the *active* pill: left half (under
          Y) when marketSide='yes', right half — its left edge at the cell
          midpoint, ≈ under the N pill's left edge — when 'no'. Left-justified
          within the half, so mixed integer / 1-decimal odds line up on their
          left edge regardless of width. */}
      <Td className="relative whitespace-nowrap py-0.5 font-mono">
        {kLevel ? (
          <div className="flex">
            <div className="w-1/2 text-left">{marketSide === 'yes' ? kPriceOdds : null}</div>
            <div className="w-1/2 text-left">{marketSide === 'no' ? kPriceOdds : null}</div>
          </div>
        ) : null}
        {kFairDot && <ConsensusFairDot dot={kFairDot} marketSide={marketSide} />}
      </Td>

      {/* K Size — #154: resting contract qty at this price (NOT traded volume),
          under the shared "Vol" header. Titled "Resting Qty" so it reads truly. */}
      <Td className="text-muted-foreground py-0.5 text-right font-mono text-xs">
        {kLevel ? (
          <span
            className="inline-block w-[6ch] text-right"
            title="Resting Qty — contracts resting at this price level (not traded volume)"
          >{formatVolume(kLevel.size)}</span>
        ) : ''}
      </Td>

      {/* K Liquidity — #154: resting $ at this price (priceCents × size / 100),
          scaled to % of total Kalshi book liquidity. Titled "Resting $". */}
      <Td className="py-0.5" title="Resting $ — price × size resting at this price level">
        {kLiq != null && <LiquidityBar value={kLiq} maxValue={kMaxLiq} height={16} />}
      </Td>

      {/* K Queue spacer — the K Q column lives in the team rows above;
          ladder rows leave it blank to keep header→row column alignment
          intact. The per-level size already lives in the K Size column to
          the left, so a queue read here would be redundant. */}
      <Td className="py-0.5">{' '}</Td>

      {/* K Δ/m spacer — velocity column lives in the team rows above. */}
      <Td className="py-0.5">{' '}</Td>

      {!hidePolyColumns && (
        <>
          {/* Polymarket price — same two-half active-pill alignment as Kalshi. */}
          <Td className="relative whitespace-nowrap py-0.5 font-mono">
            {pLevel ? (
              <div className="flex">
                <div className="w-1/2 text-left">{marketSide === 'yes' ? pPriceOdds : null}</div>
                <div className="w-1/2 text-left">{marketSide === 'no' ? pPriceOdds : null}</div>
              </div>
            ) : null}
            {pFairDot && <ConsensusFairDot dot={pFairDot} marketSide={marketSide} />}
          </Td>

          {/* P Size — #154: resting contract qty at this price (NOT traded volume),
              under the shared "Vol" header. Titled "Resting Qty" so it reads truly. */}
          <Td className="text-muted-foreground py-0.5 text-right font-mono text-xs">
            {pLevel ? (
              <span
                className="inline-block w-[6ch] text-right"
                title="Resting Qty — contracts resting at this price level (not traded volume)"
              >{formatVolume(pLevel.size)}</span>
            ) : ''}
          </Td>

          {/* P Liquidity — #154: resting $ at this price (priceCents × size / 100),
              scaled to % of total Polymarket book liquidity. Titled "Resting $". */}
          <Td className="py-0.5" title="Resting $ — price × size resting at this price level">
            {pLiq != null && <LiquidityBar value={pLiq} maxValue={pMaxLiq} height={16} />}
          </Td>

          {/* P Δ/m spacer — velocity column lives in the team rows above. */}
          <Td className="py-0.5">{' '}</Td>
        </>
      )}

      {/* US price, resting quantity and dollars align with the team row's
          metric columns. Raw price; K/P odds carry their own fee basis. */}
      {showUsColumn && (<>
        <Td className="py-0.5 text-center font-mono" title={usLevel ? undefined : usUnavailable}>
          {usLevel ? (
            <button
              type="button"
              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-sm border-0 bg-transparent px-1 hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
              title={`Polymarket US · click to trade @ ${usLevel.priceCents}¢ · resting qty ${usLevel.size}`}
              data-testid={`us-ladder-${kind}`}
              onClick={(e) => {
                e.stopPropagation();
                onUsClick?.();
              }}
            >
              <span className={cn('inline-block min-w-[4ch] text-right tabular-nums', colorClass)}>{usLevel.priceCents}¢</span>
            </button>
          ) : ' '}
        </Td>
        <UsLadderMetricCells level={usLevel} />
      </>)}
      {showNovigColumn && <Td className="py-0.5">{' '}</Td>}
    </tr>
  );
}

